// INDEX_SPLIT_B2_v1 (07.09.2026) -- Kalender + Termine: CANCEL_REASONS, seriesFutureSiblings, CancelDialog, CalendarPage, BlockerButton, BookingModal, SeriesOverviewModal, BookingsPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { ArrowRight, Calendar, CalendarDays, Check, ChevronLeft, ChevronRight, Plus, RefreshCw, UserPlus, X, useEffect, useRef, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { SeasonAbsenceMark, SeasonBadge, SeasonCorner, SeasonPeg, activeSeason, dateInSeason, isSeasonAbsence } from "./seasonal.jsx"; // SEASONAL_DECOR_v1
import { CAL_END_HOUR, CAL_START_HOUR, COLORS, TIME_SLOTS, calcDynamicDuration, calcDynamicPrice, findDuplicateCustomers, findNearbyAppointments, formatDate, formatDateShort, getApptBadges, getEndTime, getISOWeek, isKosmetikSalon, pickShiftRow, shiftAppliesOn, timeToMinutes, today } from "../core/config.jsx";
import { Badge, Btn, Card, DupCustomerHint, Input, Modal, Select, TimeCombo } from "../core/ui.jsx";
import { EmployeeAvatar, EmployeeBadges } from "../employees/employees.jsx";
import { Header } from "../layout/layout.jsx";
import { svcTintOf } from "../core/svcaudience.js"; // SERVICE_AUDIENCE_COLORS_v1
import qrcode from "qrcode-generator";
// SELF_HOST_QR_v1 (17.09.2026): QR-Code fuer den SumUp-Checkout-Link lokal erzeugen (MIT-Lib qrcode-generator,
// gebuendelt per esbuild). Vorher ging die Checkout-URL samt Browser-IP an api.qrserver.com (goQR.me, Drittanbieter).
function qrDataUrl(text) {
  try {
    const qr = qrcode(0, "M");
    qr.addData(String(text || ""));
    qr.make();
    return qr.createDataURL(6, 8);
  } catch (e) {
    return "";
  }
}


// CANCEL_REASON_v1 -- gemeinsamer Storno-Dialog (Calendar + Bookings)
export const CANCEL_REASONS = [
  { value: "kunde_krank",   label: "Kunde krank/verhindert" },
  { value: "vergessen",     label: "Kunde hat vergessen / nicht erschienen" },
  { value: "anderer_termin", label: "Anderer Termin / Konflikt" },
  { value: "salon_verspaetung", label: "Verspätung Salon / Mitarbeiter" },
  { value: "auf_kundenwunsch", label: "Auf Kundenwunsch (mehrfach)" },
  { value: "sonstiges",     label: "Sonstiges" },
];
// SERIES_CANCEL_v1 -- künftige, nicht stornierte Serien-Geschwister eines Termins
// (Basis für die "ganze Serie stornieren"-Auswahl im CancelDialog)
export const seriesFutureSiblings = (apt, appointments) => {
  if (!apt) return [];
  const root = apt.recurringParentId || apt.id;
  return (appointments || []).filter(a => a.id !== apt.id
    && (a.id === root || a.recurringParentId === root)
    && !a.phaseParentId
    && a.status !== "cancelled"
    && String(a.date).slice(0, 10) >= String(apt.date).slice(0, 10));
};
export const CancelDialog = ({ open, onClose, onConfirm, customerName, seriesCount = 0 }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // SERIES_CANCEL_v1 -- bei Serienterminen: nur dieser eine oder alle folgenden?
  const [scope, setScope] = useState("single");
  React.useEffect(() => {
    if (open) { setReason(""); setNote(""); setSaving(false); setScope("single"); }
  }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="Termin stornieren" width={480}>
      <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 12 }}>
        {customerName ? <>Termin von <strong style={{ color: COLORS.text }}>{customerName}</strong> stornieren.</> : "Termin stornieren."}
        {" "}Bitte Grund auswählen — wird im Audit-Log gespeichert (Finanzamt-relevant).
      </div>
      {/* SERIES_CANCEL_v1 -- Auswahl nur sichtbar wenn der Termin zu einer Serie gehört */}
      {seriesCount > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12, padding: "10px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--bg)" }}>
          <div style={{ fontSize: 12, fontWeight: 700 }}>🔁 Dieser Termin gehört zu einer Serie.</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="radio" name="cancelScope" checked={scope === "single"} onChange={() => setScope("single")} />
            Nur diesen einen Termin stornieren
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="radio" name="cancelScope" checked={scope === "series"} onChange={() => setScope("series")} />
            Diesen und {seriesCount === 1 ? "den 1 weiteren künftigen Serientermin" : `alle ${seriesCount} weiteren künftigen Serientermine`} stornieren
          </label>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Vergangene Termine der Serie bleiben unberührt.</div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        {CANCEL_REASONS.map(r => (
          <label key={r.value} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "6px 10px", borderRadius: 8, cursor: "pointer", background: reason === r.value ? `${COLORS.primary}10` : "transparent", border: `1px solid ${reason === r.value ? COLORS.primary : "transparent"}` }}>
            <input type="radio" name="cancelReason" value={r.value} checked={reason === r.value} onChange={() => setReason(r.value)} />
            {r.label}
          </label>
        ))}
      </div>
      <Input label="Notiz (optional)" placeholder="z.B. kommt nächste Woche wieder" value={note} onChange={e => setNote(e.target.value)} />
      <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onClose} disabled={saving}>Abbrechen</Btn>
        <Btn variant="danger" disabled={!reason || saving} onClick={async () => {
          setSaving(true);
          // SERIES_CANCEL_v1 -- dritter Parameter: true = ganze Serie ab diesem Termin
          await onConfirm(reason, note, seriesCount > 0 && scope === "series");
          setSaving(false);
        }} style={{ background: "#f59e0b" }}>{saving ? "Storniere..." : (seriesCount > 0 && scope === "series" ? "Serie stornieren" : "Stornieren")}</Btn>
      </div>
    </Modal>
  );
};

// ============================================================
// PAGE: Calendar
// ============================================================
export const CalendarPage = ({ appointments, setAppointments, employees, setEmployees, customers, setCustomers, services, badgeAwards, enabledFeatures = {}, onGoToShifts, currentUser }) => { /* PAUSE_DAY_v1: +currentUser */ /* VACATION_ENTITLEMENT_v1: +onGoToShifts */
  const [calView, setCalView] = useState("day"); // "day" | "month" | "year"
  const [selectedDate, setSelectedDate] = useState(today);

  // CAL_DISPLAY_WINDOW_v1 -- Tagesraster-Fenster: Salon-Einstellung (Standard 08-20) plus
  // Sicherheitsnetz: liegt am angezeigten Tag ein Termin AUSSERHALB des Fensters, dehnt sich
  // das Raster automatisch — es darf NIE ein Termin unsichtbar sein (z.B. 6-Uhr-Termin,
  // nachdem die Einstellung zurückgesetzt wurde).
  const _gridDs = formatDate(selectedDate);
  let gridStartHour = CAL_START_HOUR, gridEndHour = CAL_END_HOUR;
  appointments.forEach(a => {
    if (a.date !== _gridDs || a.status === "cancelled" || !a.startTime) return;
    const _s = timeToMinutes(a.startTime), _e = _s + (a.totalDuration || 0);
    if (_s < gridStartHour * 60) gridStartHour = Math.max(0, Math.floor(_s / 60));
    if (_e > gridEndHour * 60) gridEndHour = Math.min(24, Math.ceil(_e / 60));
  });

  // OCCUPANCY_BAR_v1 -- Monats-Auslastung: Wochen-Schichtvorlage (nur zur Anzeige) + Tages-Detailpanel.
  // Reine Darstellung, KEINE Buchungslogik. Der Balken zeigt belegte vs. freie Arbeitszeit pro Mitarbeiter.
  const [calShifts, setCalShifts] = useState([]);
  const [monthDetail, setMonthDetail] = useState(null); // { ds, dayDate } -> Tages-Timeline beim Klick
  useEffect(() => {
    let alive = true;
    // OCC_SHIFTS_FETCH_FIX_v1 -- api.request liefert {ok,status,data}, nie ein nacktes Array; alter Check warf die Schichten immer weg ("nicht eingeteilt" bei allen)
    api.request("/shifts").then(r => { if (alive && r && r.ok && Array.isArray(r.data)) setCalShifts(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // PAUSE_DAY_v1 (Toby 07.10.2026, Meldung Haidhausen) -- Pause für EINEN Tag verlegen oder streichen.
  // Ändern dürfen nur Inhaber/Admin und der Salon-Login ohne eigene Mitarbeiter-Zuordnung (Toby: „nur Inhaber und Salon-Login“).
  const canEditDayBreak = !!(currentUser && (currentUser.role === "owner" || currentUser.role === "admin"
    || (currentUser.role === "employee" && !currentUser.employee_id)));
  const [pauseDay, setPauseDay] = useState(null);            // { emp, date, win }
  const [pauseDayForm, setPauseDayForm] = useState({ from: "", to: "" });
  const [pauseDayBusy, setPauseDayBusy] = useState(false);
  const [pauseDayErr, setPauseDayErr] = useState("");
  const [breakOvs, setBreakOvs] = useState({});              // "empId|YYYY-MM-DD" -> {s,e} | null (= keine Pause)
  const pdFmt = (m) => String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0");
  // Pause eines Tages für die Auslastungs-Balken: Tages-Ausnahme vor Schichtplan
  const occBreakOf = (empId, ds, sh) => {
    const k = empId + "|" + ds;
    if (Object.prototype.hasOwnProperty.call(breakOvs, k)) return breakOvs[k];
    return (sh && sh.break_start && sh.break_end) ? { s: occToMin(sh.break_start), e: occToMin(sh.break_end) } : null;
  };
  // OCCUPANCY_BAR_v1 -- Helfer
  const occToMin = (t) => { const p = String(t || "").split(":"); return (Number(p[0]) || 0) * 60 + (Number(p[1]) || 0); };
  const occFmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m) % 60).padStart(2, "0")}`;
  const occDayKey = (d) => ["so", "mo", "di", "mi", "do", "fr", "sa"][d.getDay()];
  // BIWEEKLY_SHIFT_v1 -- ds optional: 14-Tage-Zeilen zählen nur in ihrer Woche, paritäts-spezifische Zeile gewinnt
  // SHIFT_VALID_RANGE_v1 -- erst die gültige Zeile wählen (Zeitraum vor Vorlage), DANN frei/ohne Zeiten aussortieren
  const occWeekShift = (empId, dk, ds) => {
    const sh = pickShiftRow(calShifts.filter(s => s.employee_id === empId && s.day_of_week === dk && shiftAppliesOn(s, ds)));
    return sh && !sh.is_off && sh.start_time && sh.end_time ? sh : undefined;
  };
  const occAvailMin = (sh) => {
    if (!sh) return 0;
    let mins = occToMin(sh.end_time) - occToMin(sh.start_time);
    if (sh.break_start && sh.break_end) mins -= Math.max(0, occToMin(sh.break_end) - occToMin(sh.break_start));
    return Math.max(0, mins);
  };
  // belegte Minuten eines Mitarbeiters an einem Tag (Phasen-Master raus, sonst Doppelzaehlung)
  const occBusyMin = (empId, ds) => appointments
    .filter(a => a.employeeId === empId && a.date === ds && a.status !== "cancelled" && !(a.isPhaseMaster || a.is_phase_master))
    .reduce((s, a) => s + (a.totalDuration || 0), 0);
  // DAY_STRIP_OCC_v1 (23.09.2026) -- Tages-Auslastung EINES Mitarbeiters, unveraendert aus OCC_MINI_TIMELINE_v1
  // (Monatsansicht) herausgezogen, damit Monatsansicht und Tagesleiste genau dieselbe Rechnung nutzen:
  // Schicht-Fenster, Termin-Bloecke, Pause, freie Luecken. null = weder eingeteilt noch Termine.
  const occEmpDay = (emp, dayDate, ds) => {
    const sh = occWeekShift(emp.id, occDayKey(dayDate), ds);
    const dayAppts = appointments
      .filter(a => a.employeeId === emp.id && a.date === ds && a.status !== "cancelled" && !(a.isPhaseMaster || a.is_phase_master))
      .map(a => ({ s: occToMin(a.startTime), e: occToMin(a.startTime) + (a.totalDuration || 0), vac: a.source === "vacation_auto" }))
      .sort((x, y) => x.s - y.s);
    if (!sh && dayAppts.length === 0) return null; // weder eingeteilt noch Termine -> ausblenden
    const winStart = sh ? occToMin(sh.start_time) : dayAppts[0].s;
    const winEnd = sh ? occToMin(sh.end_time) : dayAppts[dayAppts.length - 1].e;
    const span = Math.max(1, winEnd - winStart);
    const blocks = dayAppts.map(a => ({ s: a.s, e: a.e, type: "appt" }));
    { const _pb = occBreakOf(emp.id, ds, sh); if (_pb) blocks.push({ s: _pb.s, e: _pb.e, type: "break" }); } /* PAUSE_DAY_v1 */
    blocks.sort((a, b) => a.s - b.s);
    const gaps = [];
    let cursor = winStart;
    blocks.forEach(b => {
      const bs = Math.max(b.s, winStart), be = Math.min(b.e, winEnd);
      if (bs > cursor) gaps.push([cursor, bs]);
      cursor = Math.max(cursor, be);
    });
    if (cursor < winEnd) gaps.push([cursor, winEnd]);
    const breakMin = blocks.filter(b => b.type === "break").reduce((s, b) => s + Math.max(0, Math.min(b.e, winEnd) - Math.max(b.s, winStart)), 0);
    const freeMin = gaps.reduce((s, g) => s + (g[1] - g[0]), 0);
    const availEff = Math.max(0, span - breakMin);
    const pct = availEff > 0 ? Math.min(100, Math.round(((availEff - freeMin) / availEff) * 100)) : (dayAppts.length ? 100 : 0);
    return { emp, sh, dayAppts, blocks, gaps, winStart, winEnd, span, freeMin, availEff, pct, onVacation: dayAppts.some(a => a.vac) };
  };
  // DAY_STRIP_OCC_v1 -- Team-Auslastung eines Tages fuer die Tagesleiste: Kapazitaet = nur eingeteilte Mitarbeiter,
  // wer an dem Tag Urlaub hat (vacation_auto) zaehlt weder als frei noch als belegt.
  const occTeamDay = (dayDate) => {
    const ds = formatDate(dayDate);
    let free = 0, avail = 0;
    const lines = [];
    employees.forEach(emp => {
      const r = occEmpDay(emp, dayDate, ds);
      if (!r || !r.sh || r.onVacation) return;
      free += r.freeMin; avail += r.availEff;
      lines.push(`${emp.name}: ` + (r.freeMin > 0 ? `frei ${r.gaps.map(g => `${occFmt(g[0])}–${occFmt(g[1])}`).join(", ")}` : "ausgebucht"));
    });
    return { free, avail, pct: avail > 0 ? Math.round(((avail - free) / avail) * 100) : null, tip: lines.join("\n") };
  };

  // EMPLOYEE_SORT_v2.1 -- Long-Press auf MA-Header → Drag-Mode → Drop swappt Position
  // Härter gegen Fehlauslösung: 1,0 Sek + Move-Threshold (>10px = Scroll-Intent, abbrechen).
  const [empDragId, setEmpDragId] = useState(null);
  const [empDropTargetId, setEmpDropTargetId] = useState(null);
  const empPressTimerRef = useRef(null);
  const empPressStartXRef = useRef(0);
  const empPressStartYRef = useRef(0);

  const startEmpPress = (empId, clientX, clientY) => {
    if (empPressTimerRef.current) clearTimeout(empPressTimerRef.current);
    empPressStartXRef.current = clientX;
    empPressStartYRef.current = clientY || 0;
    empPressTimerRef.current = setTimeout(() => {
      setEmpDragId(empId);
      empPressTimerRef.current = null;
      // EMPLOYEE_SORT_v2.1: Vibrationsfeedback auf Mobile damit User merkt: Drag-Mode aktiv
      try { if (navigator.vibrate) navigator.vibrate(20); } catch(e) {}
    }, 1000); // 1,0 Sek Long-Press (war 0,7 → zu schnell)
  };
  const cancelEmpPress = () => {
    if (empPressTimerRef.current) { clearTimeout(empPressTimerRef.current); empPressTimerRef.current = null; }
  };
  // EMPLOYEE_SORT_v2.1: Wenn User Finger/Maus während der Long-Press-Wartezeit bewegt → wahrscheinlich Scroll, kein Drag
  const checkMoveAbort = (clientX, clientY) => {
    if (!empPressTimerRef.current) return;
    const dx = Math.abs((clientX || 0) - empPressStartXRef.current);
    const dy = Math.abs((clientY || 0) - empPressStartYRef.current);
    if (dx > 10 || dy > 10) cancelEmpPress();
  };
  const handleEmpDragMove = (clientX, allHeaders) => {
    if (!empDragId || !allHeaders) return;
    // Finde Header unter Cursor
    let hit = null;
    for (const [id, rect] of allHeaders) {
      if (clientX >= rect.left && clientX <= rect.right) { hit = id; break; }
    }
    if (hit !== empDropTargetId) setEmpDropTargetId(hit);
  };
  const endEmpDrag = async () => {
    cancelEmpPress();
    // EMPLOYEE_SORT_v2.1: Snapshots ziehen BEVOR State zurückgesetzt wird,
    // sonst geht der Drop-Target durch das setState(null) verloren.
    const dragSnapshot = empDragId;
    const targetSnapshot = empDropTargetId;
    // Visuelle States SOFORT clearen damit gestrichelter Drop-Rahmen verschwindet
    setEmpDragId(null);
    setEmpDropTargetId(null);
    if (dragSnapshot && targetSnapshot && dragSnapshot !== targetSnapshot) {
      // Reorder: dragId an die Position von dropTargetId verschieben
      const order = employees.map(e => e.id);
      const fromIdx = order.indexOf(dragSnapshot);
      const toIdx = order.indexOf(targetSnapshot);
      if (fromIdx !== -1 && toIdx !== -1) {
        const [moved] = order.splice(fromIdx, 1);
        order.splice(toIdx, 0, moved);
        const res = await api.request("/employees/reorder", {
          method: "POST", body: JSON.stringify({ order })
        });
        if (res && res.ok) {
          const r = await api.getEmployees();
          if (r && r.ok && setEmployees) setEmployees(r.data || []);
        }
      }
    }
  };
  // Global Mouse/Touch-Move + Up während Drag-Mode
  // EMPLOYEE_SORT_v2.3 -- iPad-Fix: Body-Scroll lock + preventDefault auf touchmove,
  // sonst wischt iPad-Safari die ganze Seite statt den MA zu draggen.
  useEffect(() => {
    if (!empDragId) return;
    // Body-Scroll für die Drag-Dauer komplett sperren (Bounce + Pan-Gesture aus)
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    const prevOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    document.body.style.overscrollBehavior = "contain";
    const headers = () => {
      const els = document.querySelectorAll("[data-emp-header]");
      const list = [];
      els.forEach(el => {
        const id = parseInt(el.getAttribute("data-emp-header"), 10);
        const rect = el.getBoundingClientRect();
        list.push([id, rect]);
      });
      return list;
    };
    const onMove = (e) => {
      // iPad: preventDefault NUR möglich wenn Event cancelable + non-passive registriert ist
      if (e.cancelable) { try { e.preventDefault(); } catch (_) {} }
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      handleEmpDragMove(x, headers());
    };
    const onUp = () => { endEmpDrag(); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    document.addEventListener("touchcancel", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      document.removeEventListener("touchcancel", onUp);
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
      document.body.style.overscrollBehavior = prevOverscroll;
    };
  }, [empDragId, empDropTargetId, employees]);
  // EMPLOYEE_SORT_v2.1 -- Während Long-Press-WARTEZEIT (vor Drag-Aktivierung): Move = wahrscheinlich Scroll → Timer abbrechen
  useEffect(() => {
    const onPressMove = (e) => {
      if (!empPressTimerRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      checkMoveAbort(x, y);
    };
    const onPressEnd = () => { cancelEmpPress(); };
    document.addEventListener("mousemove", onPressMove);
    document.addEventListener("touchmove", onPressMove, { passive: true });
    document.addEventListener("mouseup", onPressEnd);
    document.addEventListener("touchend", onPressEnd);
    document.addEventListener("touchcancel", onPressEnd);
    return () => {
      document.removeEventListener("mousemove", onPressMove);
      document.removeEventListener("touchmove", onPressMove);
      document.removeEventListener("mouseup", onPressEnd);
      document.removeEventListener("touchend", onPressEnd);
      document.removeEventListener("touchcancel", onPressEnd);
    };
  }, []);
  /* CAL_DAY_POLISH_v1 -- Tick fuer Now-Line: jede Minute Re-Render damit rote Linie wandert */
  const [, _setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => _setNowTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);
  // EXTRA_SHIFT_CAL_v1 -- shift_overrides fuer den aktuell gewaehlten Tag laden,
  // damit Extra-Schicht / Krank-Markierung in der MA-Spalte erscheint.
  const [dayOverrides, setDayOverrides] = useState([]);
  // EXTRA_SHIFT_CAL_DELETE_v1 -- Tick zum Neuladen der Overrides nach Entfernen aus dem Kalender
  const [ovReloadTick, setOvReloadTick] = useState(0);
  // SHIFT_WINDOW_CAL_v1 -- aufgeloeste Arbeits-Zeitfenster pro MA fuer den gewaehlten Tag
  // (regulaere Schicht + Overrides) -> Nicht-Arbeitszeiten im Raster ausgrauen.
  const [dayShiftWindows, setDayShiftWindows] = useState({});
  useEffect(() => {
    const ds = formatDate(selectedDate);
    let cancelled = false;
    (async () => {
      const res = await api.request("/shift-overrides?from=" + ds + "&to=" + ds);
      if (cancelled) return;
      if (res && res.ok && Array.isArray(res.data)) setDayOverrides(res.data);
      else setDayOverrides([]);
    })();
    (async () => {
      const res = await api.request("/shift-windows?date=" + ds);
      if (cancelled) return;
      if (res && res.ok && res.data && res.data.windows) setDayShiftWindows(res.data.windows);
      else setDayShiftWindows({});
    })();
    return () => { cancelled = true; };
  }, [selectedDate, ovReloadTick]);
  // PAUSE_DAY_v1 -- Tages-Ausnahmen der Pause für den sichtbaren Monat (±7 Tage) laden
  useEffect(() => {
    let alive = true;
    const y = selectedDate.getFullYear(), m = selectedDate.getMonth();
    const from = formatDate(new Date(y, m, 1 - 7)), to = formatDate(new Date(y, m + 1, 7));
    api.request("/shift-breaks?from=" + from + "&to=" + to).then(r => {
      if (!alive || !r || !r.ok || !r.data || !Array.isArray(r.data.overrides)) return;
      const map = {};
      r.data.overrides.forEach(o => {
        map[o.employee_id + "|" + o.date] = (o.break_start && o.break_end) ? { s: occToMin(o.break_start), e: occToMin(o.break_end) } : null;
      });
      setBreakOvs(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, [selectedDate.getFullYear(), selectedDate.getMonth(), ovReloadTick]);
  const openPauseDay = (emp, win) => {
    const hasEff = typeof win.break_start === "number" && typeof win.break_end === "number";
    const hasPlan = typeof win.plan_break_start === "number" && typeof win.plan_break_end === "number";
    setPauseDayForm({
      from: hasEff ? pdFmt(win.break_start) : (hasPlan ? pdFmt(win.plan_break_start) : "12:00"),
      to: hasEff ? pdFmt(win.break_end) : (hasPlan ? pdFmt(win.plan_break_end) : "13:00"),
    });
    setPauseDayErr("");
    setPauseDay({ emp, date: formatDate(selectedDate), win });
  };
  const savePauseDay = async (mode, confirmOverlap) => {
    if (!pauseDay) return;
    setPauseDayBusy(true); setPauseDayErr("");
    try {
      let res;
      if (mode === "plan") {
        res = await api.request("/shift-breaks?employee_id=" + pauseDay.emp.id + "&date=" + pauseDay.date, { method: "DELETE" });
      } else {
        const body = { employee_id: pauseDay.emp.id, date: pauseDay.date };
        if (mode === "none") body.no_break = true;
        else { body.break_start = pauseDayForm.from; body.break_end = pauseDayForm.to; }
        if (confirmOverlap) body.confirm_overlap = true;
        res = await api.request("/shift-breaks", { method: "PUT", body: JSON.stringify(body) });
        if (res && res.status === 409 && res.data && res.data.reason === "overlap") {
          const list = (res.data.overlaps || []).map(o => "• " + o.start + "–" + o.end + " " + o.label).join("\n");
          setPauseDayBusy(false);
          if (window.confirm("Zu dieser Zeit stehen schon Einträge:\n" + list + "\n\nPause trotzdem dorthin legen? Die Einträge bleiben unverändert.")) return savePauseDay(mode, true);
          return;
        }
      }
      if (!res || !res.ok) {
        setPauseDayErr((res && res.data && res.data.error) || "Speichern fehlgeschlagen");
        setPauseDayBusy(false);
        return;
      }
      setPauseDay(null);
      setOvReloadTick(t => t + 1);
    } catch (e) {
      setPauseDayErr("Speichern fehlgeschlagen");
    }
    setPauseDayBusy(false);
  };
    const [showDatePicker, setShowDatePicker] = useState(false);
    const stripSelRef = useRef(null); // DAY_STRIP_OCC_v1 -- zuletzt in Sicht gescrollter Tag der Tagesleiste
    const [dpMonth, setDpMonth] = useState(today.getMonth());
    const [dpYear, setDpYear] = useState(today.getFullYear());
  const [showBooking, setShowBooking] = useState(false);
  // CAL_NEW_CUST_QUICK_BOOK_v1 -- wenn true, startet BookingModal direkt im Neukunde-Modus
  const [bookingStartNewCustomer, setBookingStartNewCustomer] = useState(false);
  // CAL_CLICK_TO_BOOK_v1 -- vorausgefüllte Werte aus Klick auf leere Zeit-Fläche
  const [bookingPrefill, setBookingPrefill] = useState(null); // { employeeId, date, startTime }
  // CAL_SLOT_CHOOSER_v1 -- Klick auf leere Zeit-Flaeche oeffnet zuerst die Frage "Termin buchen" oder "Pause"
  const [slotChoice, setSlotChoice] = useState(null);   // { employeeId, date, startTime }
  const [pauseSignal, setPauseSignal] = useState(0);    // Zaehler: bei jedem Inkrement oeffnet BlockerButton vorausgefuellt
  const [pausePrefill, setPausePrefill] = useState(null); // { employeeId, date, startTime, endTime }
  const [selectedApt, setSelectedApt] = useState(null);
  // IDLE_PHASE_BLOCK_v1 -- Auswahl-Fenster beim Klick auf eine Einwirkzeit-Karte: {apt, emp, dateStr}
  const [idlePhaseChooser, setIdlePhaseChooser] = useState(null);
  // APT_NOTE_EDIT_v1 -- Kunden-Notiz direkt im Termin-Detail-Fenster bearbeiten (synchron mit CRM/BookingModal: customers.notes)
  const [aptNoteEdit, setAptNoteEdit] = useState(null); // null = nicht im Bearbeiten-Modus, sonst der getippte Text
  const [aptNoteSaving, setAptNoteSaving] = useState(false);
  // APT_NOTE_ONCE_v1 (Toby 07.10.2026) -- Notiz NUR für diesen Termin (appointments.notes), getrennt von der dauerhaften
  // Kunden-Notiz. Beispiel: „Sohn kommt mit und wird auch geschnitten“ darf nicht beim nächsten Termin wieder auftauchen.
  const [aptOnceEdit, setAptOnceEdit] = useState(null);   // null = nicht im Bearbeiten-Modus, sonst der getippte Text
  const [aptOnceSaving, setAptOnceSaving] = useState(false);
  // NO_SHOW_MARK_v1 (Toby 07.10.2026 „go 1, nur Inhaber und Salon-Login“) -- „Nicht erschienen“ im Termin-Fenster
  const [noShowBusy, setNoShowBusy] = useState(false);
  // DRAG_TIME_HINT_v1 -- Live-Anzeige der Uhrzeit waehrend Drag-Drop.
  // offsetY wird im dragstart in einem Ref gespeichert (dataTransfer.getData ist beim
  // dragover aus Sicherheitsgruenden leer in den meisten Browsern).
  const [dragHint, setDragHint] = useState(null); // { empId, time, x, y }
  const dragOffsetYRef = useRef(0);
  // CAL_TOUCH_MOVE_v3 -- letzte Drag-Hover-Position merken: Android-Chrome feuert beim Finger-Drag
  // KEIN 'drop' (daher springt der Termin zurueck), aber 'dragend' kommt an -> dort speichern.
  const lastDragOverRef = useRef(null);
  // CANCEL_REASON_v1 -- Termin-Storno-Dialog (Calendar-Page)
  const [cancelTarget, setCancelTarget] = useState(null);
  // APPT_ASSIST_REMOVE_v1 (Toby 18.09.2026) -- Klick auf die 🤝-Assistenz-Karte oeffnet ein eigenes
  // kleines Fenster mit "Assistenz loeschen". Geloescht wird NUR die Assistenz (Karte beim Helfer +
  // Hinweis am Haupttermin) -- der Haupttermin selbst bleibt unangetastet. { block, masterId, emp }
  const [assistantTarget, setAssistantTarget] = useState(null);
  const [assistantBusy, setAssistantBusy] = useState(false);

  // NOTIF_DEEP_LINK_v1 -- Termin-Card kurz hervorheben wenn aus Notification-Click reingekommen.
  // Setzt sich nach 4s automatisch zurueck.
  const [highlightedApptId, setHighlightedApptId] = useState(null);
  useEffect(() => {
    const onFocusAppt = (ev) => {
      const detail = (ev && ev.detail) || {};
      const dateStr = detail.date;
      const apptId = detail.appointmentId;
      if (dateStr) {
        try {
          const [y, m, d] = dateStr.split("-").map(Number);
          if (y && m && d) {
            setSelectedDate(new Date(y, m - 1, d));
            setCalView("day");
          }
        } catch (e) {}
      }
      if (apptId) {
        setHighlightedApptId(apptId);
        // NOTIF_DEEP_LINK_v1 -- Robustes Scroll-into-view: Card kommt evtl. erst nach
        // appointments-refetch ins DOM. Probieren bei 250/700/1500/3000ms.
        const tries = [250, 700, 1500, 3000];
        tries.forEach((ms) => {
          setTimeout(() => {
            try {
              const el = document.querySelector(`[data-appt-id="${apptId}"]`);
              if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
            } catch (e) {}
          }, ms);
        });
        // Nach 5 Sek Highlight zurueck (laenger, weil refresh+render Zeit kostet)
        setTimeout(() => setHighlightedApptId(null), 5000);
      }
    };
    window.addEventListener("shearly-focus-appt", onFocusAppt);
    return () => window.removeEventListener("shearly-focus-appt", onFocusAppt);
  }, []);

  // DRAG_DROP_v1 -- Termine per Drag&Drop verschieben (Maus + Touch)
  // CAL_TOUCH_MOVE_v1 -- gemeinsame Commit-Logik fuer Maus-Drop UND Touch-Drag
  const commitApptMove = async (aptId, targetEmpId, newTime, dateStr) => {
    const apt = appointments.find(a => a.id === aptId);
    if (!apt) return;
    if (apt.startTime === newTime && apt.employeeId === targetEmpId && apt.date === dateStr) return;
    const original = { ...apt };
    const updated = { ...apt, startTime: newTime, employeeId: targetEmpId, date: dateStr };
    setAppointments(prev => prev.map(a => a.id === aptId ? updated : a));
    // SALON_LOGIN_MOVE_v1 (Toby 07.10.2026) -- Spalte nur mitschicken, wenn sie sich wirklich aendert
    // (Mitarbeiter-Logins bekamen sonst schon beim Verschieben in der eigenen Spalte 403).
    const _movePayload = { start_time: newTime, date: dateStr };
    if (Number(targetEmpId) !== Number(apt.employeeId)) _movePayload.employee_id = targetEmpId;
    try {
      let res = await api.updateAppointment(aptId, _movePayload);
      // MANUAL_OVERLAP_OK_v1 -- Konflikt beim Verschieben: nach Bestätigung bewusst überlappen
      if (res && res.status === 409 && res.data && res.data.overlap_confirmable) {
        if (window.confirm((res.data.error || "Der Mitarbeiter hat in diesem Zeitraum bereits einen Termin.") + "\n\nTrotzdem hierher verschieben? Die Termine überschneiden sich dann bewusst.")) {
          res = await api.updateAppointment(aptId, { ..._movePayload, allow_overlap: true });
        }
      }
      if (!res || !res.ok) {
        setAppointments(prev => prev.map(a => a.id === aptId ? original : a));
        if (res && res.status === 409) alert((res.data && res.data.error) || "Konflikt: Mitarbeiter hat bereits einen Termin um diese Zeit.");
        else alert("Verschieben fehlgeschlagen.");
        return;
      }
      // MANUAL_SHIFT_HINT_v1 -- Verschieben außerhalb der Schicht geht durch, nur Hinweis anzeigen
      if (res.data && res.data.shift_hint) alert(res.data.shift_hint);
      // CAL_PHASE_GLUE_v1 -- Einwirkzeit an "Auftragen" anheften: verschiebt man eine busy-Phase,
      // wandert die unmittelbar folgende Einwirk-Phase (idle) um denselben Zeit-/MA-/Datums-Versatz mit,
      // damit Auftragen + Einwirkzeit zusammenbleiben.
      if (apt.phaseParentId && !(apt.phaseBusyPct === 0 || apt.phaseBusyPct === "0")) {
        const deltaMin = timeToMinutes(newTime) - timeToMinutes(apt.startTime);
        const idle = appointments.find(a => a.phaseParentId === apt.phaseParentId
          && a.phaseNo === (apt.phaseNo + 1) && (a.phaseBusyPct === 0 || a.phaseBusyPct === "0"));
        if (idle) {
          const iMin = timeToMinutes(idle.startTime) + deltaMin;
          const iTime = `${String(Math.floor(iMin / 60)).padStart(2, "0")}:${String(iMin % 60).padStart(2, "0")}`;
          const iOrig = { ...idle };
          setAppointments(prev => prev.map(a => a.id === idle.id ? { ...a, startTime: iTime, employeeId: targetEmpId, date: dateStr } : a));
          try {
            const ir = await api.updateAppointment(idle.id, Number(targetEmpId) !== Number(idle.employeeId)
              ? { start_time: iTime, employee_id: targetEmpId, date: dateStr } : { start_time: iTime, date: dateStr });  // SALON_LOGIN_MOVE_v1
            if (!ir || !ir.ok) setAppointments(prev => prev.map(a => a.id === idle.id ? iOrig : a));
          } catch (_) { setAppointments(prev => prev.map(a => a.id === idle.id ? iOrig : a)); }
        }
      }
      // PHASE_MASTER_SYNC_v1 -- der Server hat den (unsichtbaren) Master nachgezogen: neu laden,
      // damit "Ganzen Termin öffnen" und die Termindetails sofort die neue Uhrzeit zeigen.
      if (apt.phaseParentId || apt.isPhaseMaster || apt.is_phase_master) {
        try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
      }
    } catch (err) {
      setAppointments(prev => prev.map(a => a.id === aptId ? original : a));
      alert("Fehler beim Verschieben: " + (err && err.message ? err.message : "unbekannt"));
    }
  };

  const handleApptDrop = async (e, targetEmpId) => {
    e.preventDefault();
    const aptId = Number(e.dataTransfer.getData("text/apt-id"));
    if (!aptId) return;
    const apt = appointments.find(a => a.id === aptId);
    if (!apt) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // CAL_DAY_POLISH_v1 -- offsetY-Korrektur: Termin-TOP landet dort wo der Cursor beim Drop ist
    const offsetY = Number(e.dataTransfer.getData("text/offset-y")) || 0;
    const y = Math.max(0, e.clientY - rect.top - offsetY);
    const minutesFromTop = Math.round((y / 64 * 60) / 15) * 15;
    const newHour = gridStartHour + Math.floor(minutesFromTop / 60); // CAL_DISPLAY_WINDOW_v1
    const newMin = minutesFromTop % 60;
    if (newHour < gridStartHour || newHour > gridEndHour + 1) return;
    const newTime = `${String(newHour).padStart(2,"0")}:${String(newMin).padStart(2,"0")}`;
    commitApptMove(aptId, targetEmpId, newTime, formatDate(selectedDate));
    lastDragOverRef.current = null; // CAL_TOUCH_MOVE_v3 -- 'drop' hat gespeichert -> dragend-Fallback nicht doppelt
  };

  // CAL_TOUCH_MOVE_v1 -- Long-Press auf Termin-Karte -> Touch-Drag (Android: HTML5-DnD geht nicht per Finger).
  // Phase 1: 500ms halten (bei >10px Bewegung = Scrollen -> abbrechen). Phase 2: Drag mit elementFromPoint-Hit-Test.
  const startApptTouchDrag = (e, apt) => {
    if (!apt || apt.status === "cancelled") return;
    if (!e.touches || e.touches.length !== 1) return;
    const startX = e.touches[0].clientX, startY = e.touches[0].clientY;
    let timer = null, canceled = false;
    const cleanup = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      document.removeEventListener("touchmove", preMove);
      document.removeEventListener("touchend", preEnd);
      document.removeEventListener("touchcancel", preEnd);
    };
    const preMove = (ev) => {
      const t = ev.touches && ev.touches[0]; if (!t) return;
      if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) {
        canceled = true; cleanup(); setLongPressActive(null);
      }
    };
    const preEnd = () => { canceled = true; cleanup(); setLongPressActive(null); };
    document.addEventListener("touchmove", preMove, { passive: true });
    document.addEventListener("touchend", preEnd);
    document.addEventListener("touchcancel", preEnd);
    setLongPressActive(apt.id);
    timer = setTimeout(() => {
      cleanup();
      if (canceled) { setLongPressActive(null); return; }
      try { if (navigator.vibrate) navigator.vibrate(40); } catch (_) {}
      setLongPressActive(null);
      beginApptTouchDrag(apt);
    }, 500);
  };

  const beginApptTouchDrag = (apt) => {
    const ref = { aptId: apt.id, targetEmpId: apt.employeeId, newTime: apt.startTime, valid: false };
    touchDragRef.current = ref;
    setTouchDragId(apt.id);
    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault(); // Scroll waehrend Drag stoppen
      const t = (ev.touches && ev.touches[0]) || (ev.changedTouches && ev.changedTouches[0]);
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const col = el && el.closest && el.closest("[data-drop-col]");
      if (!col) { setDragHint(null); ref.valid = false; return; }
      const rect = col.getBoundingClientRect();
      const y = Math.max(0, t.clientY - rect.top);
      const mins = Math.round((y / 64 * 60) / 15) * 15;
      const h = gridStartHour + Math.floor(mins / 60), m = mins % 60; // CAL_DISPLAY_WINDOW_v1
      if (h < gridStartHour || h > gridEndHour + 1) { setDragHint(null); ref.valid = false; return; }
      ref.targetEmpId = Number(col.getAttribute("data-drop-col"));
      ref.newTime = String(h).padStart(2,"0") + ":" + String(m).padStart(2,"0");
      ref.valid = true;
      setDragHint({ empId: ref.targetEmpId, time: ref.newTime, x: t.clientX, y: t.clientY });
    };
    const onEnd = () => {
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      const r = touchDragRef.current;
      touchDragRef.current = null;
      setTouchDragId(null); setDragHint(null);
      // Klick nach Drag unterdruecken (sonst oeffnet sich der Termin-Detail)
      suppressNextClickRef.current = true;
      setTimeout(() => { suppressNextClickRef.current = false; }, 400);
      if (r && r.valid) commitApptMove(r.aptId, r.targetEmpId, r.newTime, formatDate(selectedDate));
    };
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
  };
  const [editAptData, setEditAptData] = useState(null);
  /* APT_RESIZE_v1 -- Termin verlängern/verkürzen per Touch oder Maus am unteren Rand */
  const resizeRef = useRef(null);
  const [resizePreview, setResizePreview] = useState(null); // {id, newDur} fuer Live-Visuell
  /* APT_RESIZE_v1.1 -- Click-nach-Resize unterdruecken (iOS-Touch wandelt sonst Touch-End zu Click) */
  const suppressNextClickRef = useRef(false);
  const lastHandleTouchRef = useRef(0); // APPT_TAP_HANDLE_v1
  /* CAL_TOUCH_LONGPRESS_v1 -- Auf Touch-Geräten (iPad, Tablet) verlangt Resize/Drag einen
     1-Sekunden Long-Press damit zufälliges Wischen nichts kaputt macht.
     Bei Bewegung > 8px während des Hold-Vorgangs: abbrechen → User wollte scrollen.
     Desktop (Maus) bleibt sofort-reaktiv. Visual: Card pulsiert grün+haptisch wenn Long-Press greift. */
  const [longPressActive, setLongPressActive] = useState(null); // apt.id
  const LONG_PRESS_MS = 1000;
  const LONG_PRESS_MOVE_TOLERANCE = 8;
  // CAL_TOUCH_MOVE_v1 -- Touch-Drag zum Verschieben (Android-Chrome kann HTML5-DnD nicht per Finger)
  const touchDragRef = useRef(null);
  const [touchDragId, setTouchDragId] = useState(null);
  const isAndroidTouch = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
  // APPT_TAP_HANDLE_v1 -- Touch-Geraet auch dann erkennen, wenn Chrome auf dem Tablet als Desktop auftritt
  const isTouchDevice = typeof window !== "undefined" && (("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0);
  const startResizeWithGuard = (e, apt) => {
    // Maus: direkt
    if (!e.touches) { startResize(e, apt); return; }
    // Touch: Long-Press required
    e.stopPropagation();
    const startY = e.touches[0].clientY;
    const startX = e.touches[0].clientX;
    let timer = null;
    let canceled = false;
    const cleanup = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", cleanup);
      document.removeEventListener("touchcancel", cleanup);
      setLongPressActive(null);
    };
    const onMove = (ev) => {
      const t = ev.touches[0];
      if (!t) return;
      const dy = Math.abs(t.clientY - startY);
      const dx = Math.abs(t.clientX - startX);
      if (dy > LONG_PRESS_MOVE_TOLERANCE || dx > LONG_PRESS_MOVE_TOLERANCE) {
        canceled = true;
        cleanup();
      }
    };
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", cleanup);
    document.addEventListener("touchcancel", cleanup);
    setLongPressActive(apt.id);
    timer = setTimeout(() => {
      if (canceled) return;
      cleanup();
      // haptisch (iPad/Android)
      try { if (navigator.vibrate) navigator.vibrate(40); } catch (_) {}
      // jetzt echten Resize starten — Start-Y explizit (Event evtl. recycelt)
      startResize(e, apt, startY);
    }, LONG_PRESS_MS);
  };
  const startResize = (e, apt, explicitStartY) => {
    if (apt.status === "cancelled") return;
    // PHASE_RESIZE_CASCADE_v1 -- Phasen-Blöcke wieder ziehbar; Server kaskadiert auf die ganze Gruppe
    e.preventDefault();
    e.stopPropagation();
    // APT_RESIZE_ANDROID_FIX_v1 -- Start-Y kann explizit uebergeben werden (vom Long-Press-Guard),
    // damit wir nicht 1 Sek spaeter aus einem evtl. recycelten Touch-Event lesen muessen.
    const clientY = (typeof explicitStartY === "number") ? explicitStartY
      : ((e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY);
    resizeRef.current = {
      aptId: apt.id, startY: clientY,
      startDur: apt.totalDuration,
      currentNewDur: apt.totalDuration,
      apt,
    };

    // RESIZE_TIME_HINT_v1 (08.09.2026, Toby: "Uhrzeit beim Ziehen verlängern in kleinem Fenster ist nicht
    // mehr dabei") -- dasselbe schwebende Kästchen wie beim Verschieben (DRAG_TIME_HINT_v1) jetzt auch
    // beim Verlängern: "Start – neues Ende · Dauer". Vorher stand die Endzeit nur in der Karte, die bei
    // kurzen Terminen zu klein für die zweite Zeile ist.
    const hintFor = (dur, x, y) => ({
      time: apt.startTime + " – " + getEndTime(apt.startTime, dur) + " · " + dur + " Min",
      x: x, y: y,
    });
    {
      const cx = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
      setDragHint(hintFor(apt.totalDuration, cx || 0, clientY));
    }

    const onMove = (ev) => {
      if (!resizeRef.current) return;
      ev.preventDefault();
      const y = (ev.touches && ev.touches[0]) ? ev.touches[0].clientY : ev.clientY;
      const x = (ev.touches && ev.touches[0]) ? ev.touches[0].clientX : ev.clientX;
      const deltaPx = y - resizeRef.current.startY;
      const deltaMin = Math.round((deltaPx / 64 * 60) / 15) * 15; // 15-Min-Snap
      const newDur = Math.max(15, Math.min(480, resizeRef.current.startDur + deltaMin));
      setDragHint(hintFor(newDur, x, y));  // RESIZE_TIME_HINT_v1: folgt dem Finger/Mauszeiger
      if (newDur === resizeRef.current.currentNewDur) return;
      resizeRef.current.currentNewDur = newDur;
      setResizePreview({ id: resizeRef.current.aptId, newDur });
    };

    const onEnd = async () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
      const ref = resizeRef.current;
      resizeRef.current = null;
      setResizePreview(null);
      setDragHint(null);  // RESIZE_TIME_HINT_v1
      /* APT_RESIZE_v1.1 -- nächsten Click absorbieren (Touch-End triggert sonst Termin-Detail-Modal) */
      suppressNextClickRef.current = true;
      setTimeout(() => { suppressNextClickRef.current = false; }, 400);
      if (!ref) return;
      if (ref.currentNewDur === ref.startDur) return;
      const original = { ...ref.apt };
      const updated = { ...ref.apt, totalDuration: ref.currentNewDur };
      setAppointments(prev => prev.map(a => a.id === ref.aptId ? updated : a));
      try {
        let res = await api.updateAppointment(ref.aptId, { total_duration: ref.currentNewDur });
        // MANUAL_OVERLAP_OK_v1 -- Konflikt beim Verlängern: nach Bestätigung bewusst überlappen
        if (res && res.status === 409 && res.data && res.data.overlap_confirmable) {
          if (window.confirm((res.data.error || "Der Mitarbeiter hat in diesem Zeitraum bereits einen Termin.") + "\n\nTrotzdem verlängern? Die Termine überschneiden sich dann bewusst.")) {
            res = await api.updateAppointment(ref.aptId, { total_duration: ref.currentNewDur, allow_overlap: true });
          }
        }
        if (!res || !res.ok) {
          setAppointments(prev => prev.map(a => a.id === ref.aptId ? original : a));
          if (res && res.status === 409) alert((res.data && res.data.error) || "Konflikt: neue Dauer ueberschneidet einen anderen Termin.");
          else alert("Verlaengern fehlgeschlagen.");
        } else if (res.data && res.data.cascade) {
          // PHASE_RESIZE_CASCADE_v1 -- Server hat Geschwister-Phasen + Master mitgezogen -> Gruppe frisch laden
          try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
        }
        // MANUAL_SHIFT_HINT_v1 -- Verlängern über das Schichtende hinaus geht durch, nur Hinweis anzeigen
        if (res && res.ok && res.data && res.data.shift_hint) alert(res.data.shift_hint);
      } catch (err) {
        setAppointments(prev => prev.map(a => a.id === ref.aptId ? original : a));
        alert("Fehler beim Verlaengern: " + (err && err.message ? err.message : "unbekannt"));
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
  };
  /* CHECKOUT_v1 -- Kassieren-Sub-Modal + SUMUP_v1 QR-Flow */
  const [checkoutApt, setCheckoutApt] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [sumupCheckout, setSumupCheckout] = useState(null); // {url, reference} oder null
  const [sumupError, setSumupError] = useState("");
  /* CHECKOUT_v1 -- POS-Status (welcher Provider + konfiguriert?) — einmalig laden */
  const [posInfo, setPosInfo] = useState({ provider: "", hasKey: false, loaded: false });
  useEffect(() => {
    let mounted = true;
    api.request("/salon/pos-settings").then(r => {
      if (!mounted) return;
      if (r && r.ok && r.data) {
        setPosInfo({
          provider: (r.data.provider || "").toLowerCase(),
          hasKey: !!r.data.has_api_key,
          loaded: true,
        });
      } else {
        setPosInfo({ provider: "", hasKey: false, loaded: true });
      }
    }).catch(() => setPosInfo({ provider: "", hasKey: false, loaded: true }));
    return () => { mounted = false; };
  }, []);
  const sumupReady = posInfo.provider === "sumup" && posInfo.hasKey;
  /* POS_CHECKOUT_DEEPLINK_v1 -- Shearly Kasse freigeschaltet (Flag pos_shearly aus /api/features; fuer
     Mitarbeiter ohne Kassen-Zugriff liefert der Server false): "Bar" oeffnet die Kasse mit dem Termin
     vorbelegt (#token=…&appointment=<id>, Kasse seit 0.23.0); die Kasse meldet "bezahlt" + Bon zurueck
     (POS_APPOINTMENTS_v1). Nicht mehr hier als bezahlt markieren. */
  const posShearly = !!(enabledFeatures && enabledFeatures.pos_shearly === true);
  const openKasseForAppt = async (apt) => {
    setCheckoutBusy(true);
    setSumupError("");
    const w = window.open("", "_blank"); // Popup-Blocker-sicher: synchron im Klick oeffnen (wie KassePage)
    try {
      const r = await api.request("/salon/pos-shearly-sso", { method: "POST", body: "{}" });
      if (r && r.ok && r.data && r.data.url) {
        const url = r.data.url + "&appointment=" + encodeURIComponent(apt.phaseParentId || apt.id);  // KASSIEREN_LINK_v1 -- Kasse kennt nur den Gesamttermin
        if (w) { w.location = url; } else { window.location.href = url; }
        setCheckoutApt(null);
      } else {
        if (w) w.close();
        setSumupError((r && r.data && (r.data.error || r.data.message)) || "Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
      }
    } catch (e) {
      if (w) w.close();
      setSumupError("Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
    }
    setCheckoutBusy(false);
  };
  /* KASSE_CAL_BUTTON_v1 (27.09.2026, Toby: „wenn die kasse gebucht ist soll auch ein button mit "kasse" im kalender
     stehen … manche haben keine zwei ipads oder monitore“) -- Knopf „Kasse“ im Kalenderkopf, gleicher Weg wie die
     Seite „Kasse“ (KassePage): SSO-Link holen, Kasse im neuen Tab. Nur wenn die Shearly Kasse freigeschaltet ist. */
  const [kasseBusy, setKasseBusy] = useState(false);
  const openKasse = async () => {
    setKasseBusy(true);
    const w = window.open("", "_blank"); // Popup-Blocker-sicher: synchron im Klick oeffnen
    try {
      const r = await api.request("/salon/pos-shearly-sso", { method: "POST", body: "{}" });
      if (r && r.ok && r.data && r.data.url) {
        if (w) { w.location = r.data.url; } else { window.location.href = r.data.url; }
      } else {
        if (w) w.close();
        window.alert((r && r.data && (r.data.error || r.data.message)) || "Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
      }
    } catch (e) {
      if (w) w.close();
      window.alert("Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
    }
    setKasseBusy(false);
  };
  const posLabel = posInfo.provider
    ? posInfo.provider.charAt(0).toUpperCase() + posInfo.provider.slice(1)
    : "";
  const requestSumupCheckout = async (apt) => {
    setCheckoutBusy(true);
    setSumupError("");
    setSumupCheckout(null);
    try {
      const r = await api.request("/owner/sumup/checkout", {
        method: "POST",
        body: JSON.stringify({ appointment_id: apt.id }),
      });
      if (r && r.ok && r.data && r.data.checkout_url) {
        setSumupCheckout({ url: r.data.checkout_url, ref: r.data.reference });
      } else {
        const detail = (r && r.data && (r.data.detail || r.data.error)) || "Unbekannter Fehler";
        setSumupError(detail);
      }
    } catch (e) {
      setSumupError("Netzwerk-Fehler: " + (e.message || "?"));
    }
    setCheckoutBusy(false);
  };
  const markApptPaid = async (apt, method) => {
    // KASSIEREN_LINK_v1 -- Zahlung gehoert zum Gesamttermin; die Phasen zieht die Datenbank nach (Migration 102)
    if (apt && apt.phaseParentId) { const _m = appointments.find(a => Number(a.id) === Number(apt.phaseParentId)); if (_m) apt = _m; }
    setCheckoutBusy(true);
    const original = { ...apt };
    const updated = { ...apt, paymentStatus: "paid", paymentMethod: method, paidAt: new Date().toISOString() };
    setAppointments(prev => prev.map(a => a.id === apt.id ? updated : a));
    try {
      const res = await api.request(`/appointments/${apt.id}`, {
        method: "PUT",
        body: JSON.stringify({ payment_status: "paid", payment_method: method }),
      });
      if (!res || !res.ok) {
        setAppointments(prev => prev.map(a => a.id === apt.id ? original : a));
        alert("Speichern fehlgeschlagen: " + ((res && res.data && res.data.error) || "Unbekannt"));
      } else {
        setSelectedApt(updated);
        setCheckoutApt(null);
      }
    } catch (e) {
      setAppointments(prev => prev.map(a => a.id === apt.id ? original : a));
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
    setCheckoutBusy(false);
  };
  /* BOOKING_PREPAY_v1 -- Online-Vorkasse (SumUp) erstatten: Vorschlag vom Server (voll bei Absage in der
     Frist, sonst minus Ausfallgebuehr), Betrag aenderbar, Erstattung laeuft ueber die SumUp-Transaktion. */
  const [refundApt, setRefundApt] = useState(null);
  const [refundInfo, setRefundInfo] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState("");
  const openRefund = async (apt) => {
    setRefundApt(apt); setRefundInfo(null); setRefundError(""); setRefundAmount(String(apt.prepaidAmount || ""));
    const r = await api.request("/owner/prepay/refund?appointment_id=" + apt.id);
    if (r && r.ok && r.data) {
      setRefundInfo(r.data);
      setRefundAmount(String(r.data.suggested_amount != null ? r.data.suggested_amount : (apt.prepaidAmount || "")));
    } else {
      setRefundError((r && r.data && r.data.error) || "Vorschlag konnte nicht geladen werden.");
    }
  };
  const doRefund = async () => {
    if (!refundApt) return;
    const amt = Number(String(refundAmount).replace(",", "."));
    if (!(amt > 0)) { setRefundError("Bitte einen Betrag über 0 eingeben."); return; }
    setRefundBusy(true); setRefundError("");
    try {
      const r = await api.request("/owner/prepay/refund", { method: "POST", body: JSON.stringify({ appointment_id: refundApt.id, amount: amt }) });
      if (r && r.ok && r.data && r.data.ok) {
        const upd = { ...refundApt, prepayStatus: "refunded", prepayRefundAmount: r.data.refunded_amount, paymentStatus: r.data.payment_status || refundApt.paymentStatus,
          /* PREPAY_LEDGER_v1 -- einbehaltener Teil (Teil-Erstattung: Beleg noch in der Kasse buchen) */
          prepayRetainedAmount: r.data.retained_amount != null ? r.data.retained_amount : refundApt.prepayRetainedAmount,
          prepayRetainedRef: r.data.retained_new ? (refundApt.id + "-retained") : refundApt.prepayRetainedRef };
        setAppointments(prev => prev.map(a => a.id === refundApt.id ? upd : a));
        if (selectedApt && selectedApt.id === refundApt.id) setSelectedApt(upd);
        setRefundApt(null);
      } else {
        setRefundError((r && r.data && r.data.error) || "Erstattung fehlgeschlagen.");
      }
    } catch (e) {
      setRefundError("Netzwerkfehler: " + (e.message || "?"));
    }
    setRefundBusy(false);
  };
  // APPT_ASSIST_REMOVE_v1 -- Assistenz eines Termins entfernen. Es wird NICHTS am Haupttermin geaendert
  // ausser den beiden Assistenz-Feldern; der Server raeumt daraufhin den synthetischen 🤝-Block weg
  // (_sync_assistant_block). Bewusst nur die zwei Felder im Payload: kein date/start_time/employee_id,
  // damit weder Ueberschneidungs-Pruefung noch Schicht-Hinweis noch SMS-Neuversand angestossen werden.
  const removeAssistant = async (masterId) => {
    if (!masterId) return false;
    try {
      const res = await api.updateAppointment(masterId, { assistant_employee_id: null, assistant_duration_min: null });
      if (res && res.ok) {
        setAppointments(prev => prev
          .filter(a => !(a.source === "assistant" && Number(a.phaseParentId) === Number(masterId)))
          .map(a => (Number(a.id) === Number(masterId) ? { ...a, assistantEmployeeId: null, assistantDurationMin: null } : a)));
        setSelectedApt(prev => (prev && Number(prev.id) === Number(masterId) ? { ...prev, assistantEmployeeId: null, assistantDurationMin: null } : prev));
        setEditAptData(prev => (prev ? { ...prev, assistantEmployeeId: null, assistantDurationMin: null } : prev));
        try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
        return true;
      }
      alert((res && res.data && res.data.error) || "Die Assistenz konnte nicht entfernt werden.");
    } catch (e) {
      alert("Verbindungsfehler — bitte erneut versuchen.");
    }
    return false;
  };
  // PHASE_MASTER_SYNC_v1 -- Phasen-Gruppe eines Termins (ohne Storno, ohne Assistenz), zeitlich sortiert.
  // Der Master (is_phase_master) wird im Kalender nicht gezeichnet, liegt aber in `appointments` und
  // traegt Leistungen, Preis, Zahlung, Erinnerung -- "Ganzen Termin öffnen" zeigt genau ihn.
  const phaseGroupOf = (masterId) => appointments
    .filter(a => Number(a.phaseParentId) === Number(masterId) && a.status !== "cancelled" && a.source !== "assistant")
    .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime) || (a.phaseNo || 0) - (b.phaseNo || 0));
  const isPhasePart = (a) => !!(a && a.phaseParentId && a.source !== "assistant");
  // ONLINE_NOTE_CAL_v1 (Toby 06.10.2026) -- Termin-Notiz lesbar machen: die Anmerkung aus der Online-Buchung
  // (ONLINE_BOOKING_NOTES_v1), "Haarlänge: …" aus dem Buchungsfenster und Import-Notizen (Salonkee/Treatwell)
  // standen in appointments.notes, der Kalender zeigte aber nur die Kartei-Notiz des Kunden (customers.notes).
  // Blocker nutzen notes als Beschriftung -> dort nichts; Phasen ohne eigene Notiz nehmen die des Haupttermins.
  // NO_SHOW_MARK_v1 -- Termin hat begonnen (bei Phasen zählt der Beginn des ganzen Termins)
  const aptStarted = (apt) => {
    const m = isPhasePart(apt) ? appointments.find(a => Number(a.id) === Number(apt.phaseParentId)) : null;
    const src = m || apt;
    const t = new Date(String(src.date || "").slice(0, 10) + "T" + String(src.startTime || "00:00").slice(0, 5) + ":00");
    return !isNaN(t.getTime()) && t.getTime() <= Date.now();
  };
  const markNoShow = async (apt, undo) => {
    const c0 = customers.find(c => c.id === apt.customerId);
    const msg = undo
      ? "Doch erschienen? Der Termin wird wieder auf „Bestätigt“ gesetzt und die Markierung zurückgenommen."
      : (c0 ? c0.name : "Der Kunde") + " ist nicht erschienen und hat nicht abgesagt?\n\nDer Termin wird als „Nicht erschienen“ markiert, der Kunde bekommt eine rote Markierung.";
    if (!window.confirm(msg)) return;
    setNoShowBusy(true);
    try {
      const res = await api.request("/appointments/" + apt.id + "/no-show", { method: "POST", body: JSON.stringify(undo ? { undo: true } : {}) });
      if (!res || !res.ok) { alert((res && res.data && res.data.error) || "Speichern fehlgeschlagen"); return; }
      const d = res.data || {};
      const mid = Number(d.appointment_id);
      setAppointments(prev => prev.map(a => ((Number(a.id) === mid || Number(a.phaseParentId) === mid) && a.status !== "cancelled") ? { ...a, status: d.status } : a));
      setSelectedApt(sel => sel ? { ...sel, status: d.status } : sel);
      if (setCustomers && d.customer_id) setCustomers(prev => prev.map(c => c.id === d.customer_id ? { ...c, noShows: d.no_show_total, lastNoShow: d.last_no_show } : c));
      try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
    } catch (e) {
      alert("Speichern fehlgeschlagen");
    } finally {
      setNoShowBusy(false);
    }
  };
  const saveAptOnceNote = async (apt, text) => {
    const val = String(text || "").trim().slice(0, 1000);
    const masterId = (apt.phaseParentId && apt.source !== "assistant") ? Number(apt.phaseParentId)
                   : ((apt.isPhaseMaster || apt.is_phase_master) ? Number(apt.id) : null);
    const ids = masterId
      ? appointments.filter(a => a.source !== "assistant" && (Number(a.id) === masterId || Number(a.phaseParentId) === masterId)).map(a => a.id)
      : [apt.id];
    if (!ids.some(id => Number(id) === Number(apt.id))) ids.push(apt.id);
    for (const id of ids) {
      const r = await api.updateAppointment(id, { notes: val });
      if (!r || !r.ok) throw new Error((r && r.data && r.data.error) || "Speichern fehlgeschlagen");
    }
    setAppointments(prev => prev.map(a => ids.some(id => Number(id) === Number(a.id)) ? { ...a, notes: val } : a));
    setSelectedApt(sel => sel ? { ...sel, notes: val } : sel);
  };
  const aptNoteOf = (a) => {
    if (!a || a.source === "blocker" || a.source === "vacation_auto") return "";
    let n = String(a.notes || "").trim();
    if (!n && a.phaseParentId) {
      const m = appointments.find(x => Number(x.id) === Number(a.phaseParentId));
      n = String((m && m.notes) || "").trim();
    }
    return n;
  };
  const openWholeAppt = (masterId) => {
    const master = appointments.find(a => Number(a.id) === Number(masterId));
    setIdlePhaseChooser(null);
    setEditAptData(null);
    setAptNoteEdit(null);
    if (master) { setSelectedApt(master); return; }
    alert("Der Gesamttermin ist gerade nicht geladen — bitte den Kalender neu laden.");
  };
  const fmtEur = (v) => (Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  const prepayLine = (apt) => {
    if (!apt || !apt.prepayStatus) return null;
    const amt = fmtEur(apt.prepaidAmount);
    const prov = apt.prepayProvider === "paypal" ? "PayPal" : apt.prepayProvider === "stripe" ? "Stripe" : "SumUp"; /* PREPAY_PAYPAL_v1 / PREPAY_STRIPE_v1 */
    /* PREPAY_LEDGER_v1 -- einbehaltene Ausfallgebuehr: aus der Kasse (Bon) oder aus einer Teil-Erstattung in Pro
       (dann fehlt der Beleg noch -> Hinweis "in der Kasse buchen") */
    const retained = apt.prepayRetainedAmount != null && Number(apt.prepayRetainedAmount) > 0 ? Number(apt.prepayRetainedAmount) : 0;
    const retainedNoReceipt = retained > 0 && (!apt.prepayRetainedRef || /-retained$/.test(String(apt.prepayRetainedRef)));
    const retainedPart = retained > 0
      ? <span style={{ color: COLORS.text }}> · Ausfallgebühr {fmtEur(retained)} einbehalten{retainedNoReceipt ? <span style={{ color: COLORS.warning }}> – Beleg in der Kasse buchen (Kassieren → Termine → Ausfallgebühr)</span> : apt.prepayRetainedRef ? ` (Bon ${apt.prepayRetainedRef})` : ""}</span>
      : null;
    /* BOOKING_PAY_ONLINE_v1 -- freiwillige Online-Zahlung: eigene Texte (Termin bleibt immer bestehen) */
    const optional = apt.prepayKind === "optional";
    const kassiert = apt.posReceiptNumber ? <span style={{ color: COLORS.text }}> · ✓ kassiert · Bon {apt.posReceiptNumber}</span> : null;
    if (apt.prepayStatus === "paid") {
      const rest = Math.max(0, (Number(apt.totalPrice) || 0) - (Number(apt.prepaidAmount) || 0));
      if (optional) return <span style={{ color: COLORS.success }}>💳 {amt} online bezahlt ({prov}){retainedPart}{kassiert}</span>;
      return <span style={{ color: COLORS.success }}>✓ Vorkasse {amt} online bezahlt ({prov}){retainedPart}{rest > 0.004 && !retained ? <span style={{ color: COLORS.text }}> · Rest {fmtEur(rest)} im Salon</span> : null}{kassiert}</span>;
    }
    if (apt.prepayStatus === "pending" && optional) return <span style={{ color: COLORS.warning }}>⏳ Online-Zahlung {amt} läuft – der Termin bleibt auch ohne Zahlung bestehen</span>;
    if (apt.prepayStatus === "expired" && optional) return <span style={{ color: COLORS.textLight }}>Online-Zahlung {amt} abgebrochen – im Salon kassieren</span>;
    if (apt.prepayStatus === "review") return <span style={{ color: COLORS.danger, fontWeight: 600 }}>⚠️ Online-Zahlung {amt} prüfen ({prov}) – gilt nicht als bezahlt (siehe Benachrichtigung)</span>;
    if (apt.prepayStatus === "pending") return <span style={{ color: COLORS.warning }}>⏳ Vorkasse {amt} ausstehend – Termin wird ohne Zahlung automatisch freigegeben</span>;
    if (apt.prepayStatus === "refunded") return <span style={{ color: COLORS.textLight }}>↩ Vorkasse {amt} erstattet{apt.prepayRefundAmount != null ? ` (${fmtEur(apt.prepayRefundAmount)})` : ""}{retainedPart}</span>;
    if (apt.prepayStatus === "expired") return <span style={{ color: COLORS.textLight }}>Vorkasse {amt} nicht bezahlt – Zahlungsfrist abgelaufen</span>;
    return null;
  };
  const dateStr = formatDate(selectedDate);
  // APPOINTMENT_PHASES_v1 -- Master-Termine (is_phase_master=1) werden NICHT direkt gerendert.
  // Stattdessen werden die Phase-Children gerendert. Alle anderen Termine normal.
  const dayAppts = appointments.filter(a => {
    if (a.date !== dateStr) return false;
    if (a.status === "cancelled") return false;
    if (a.isPhaseMaster || a.is_phase_master) return false; // Master: nur Container, nicht visuell
    return true;
  });

  const prevDay = () => setSelectedDate(new Date(selectedDate.getTime() - 86400000));
  const nextDay = () => setSelectedDate(new Date(selectedDate.getTime() + 86400000));
  const prevMonth = () => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  const nextMonth = () => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  const prevYear = () => setSelectedDate(new Date(selectedDate.getFullYear() - 1, selectedDate.getMonth(), 1));
  const nextYear = () => setSelectedDate(new Date(selectedDate.getFullYear() + 1, selectedDate.getMonth(), 1));

  // SALON_CLOSURE_v1 -- Betriebsurlaub des Salons laden (laeuft ueber calHolidays mit: Tages-Banner, Monats-/Jahresmarker)
  const [calClosures, setCalClosures] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.request("/salon-closures").then(r => { if (!cancelled && r && r.ok && Array.isArray(r.data)) setCalClosures(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [ovReloadTick]);
  // Feiertage Bayern - rollend 24 Monate ab heute (dynamisch)
  const calBaseHolidays = (() => {
    const out = []; const today = new Date(); today.setHours(0,0,0,0);
    const endD = new Date(today); endD.setMonth(endD.getMonth() + 24);
    const fmt = d => formatDate(d); // HOLIDAY_TZ_FIX_v1 -- lokale Zeit statt UTC: toISOString() schob bewegliche Feiertage (z.B. Fronleichnam) in DE einen Tag zu früh
    const addDays = (d,n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
    const easter = y => { const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,ff=Math.floor((b+8)/25),g=Math.floor((b-ff+1)/3); const h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7; const m=Math.floor((a+11*h+22*l)/451),mo=Math.floor((h+l-7*m+114)/31),da=((h+l-7*m+114)%31)+1; return new Date(y,mo-1,da); };
    for (let y = today.getFullYear(); y <= endD.getFullYear(); y++) {
      const e = easter(y);
      const items = [
        { date: `${y}-01-01`, name: "Neujahr" },
        { date: `${y}-01-06`, name: "Heilige Drei Könige" },
        { date: fmt(addDays(e,-2)), name: "Karfreitag" },
        { date: fmt(addDays(e,1)), name: "Ostermontag" },
        { date: `${y}-05-01`, name: "Tag der Arbeit" },
        { date: fmt(addDays(e,39)), name: "Christi Himmelfahrt" },
        { date: fmt(addDays(e,50)), name: "Pfingstmontag" },
        { date: fmt(addDays(e,60)), name: "Fronleichnam" },
        { date: `${y}-08-15`, name: "Mariä Himmelfahrt" },
        { date: `${y}-10-03`, name: "Tag der Dt. Einheit" },
        { date: `${y}-11-01`, name: "Allerheiligen" },
        { date: `${y}-12-25`, name: "1. Weihnachtstag" },
        { date: `${y}-12-26`, name: "2. Weihnachtstag" }
      ];
      const ts = fmt(today), es = fmt(endD);
      items.forEach(h => { if (h.date >= ts && h.date <= es) out.push(h); });
    }
    return out.sort((a,b) => a.date.localeCompare(b.date));
  })();
  const calHolidays = (() => {
    const out = [...calBaseHolidays];
    const seen = new Set(out.map(h => h.date));
    (calClosures || []).forEach(c => {
      const d = new Date(c.from_date + "T00:00:00"), end = new Date(c.to_date + "T00:00:00");
      let guard = 0;
      while (d <= end && guard++ < 400) {
        const ds = formatDate(d);
        if (!seen.has(ds)) { out.push({ date: ds, name: c.label || "Betriebsurlaub", closure: true }); seen.add(ds); }
        d.setDate(d.getDate() + 1);
      }
    });
    return out.sort((a,b) => a.date.localeCompare(b.date));
  })();

  const hours = [];
  for (let h = gridStartHour; h < gridEndHour; h++) hours.push(h); // CAL_DISPLAY_WINDOW_v1

  // CAL_LUXURY_v4 -- echter Apple-Calendar-Algorithmus:
  // 1. Greedy column assignment: jeder Termin in die erste Spalte ohne direkten Konflikt
  // 2. Pro Termin: _totalCols = max _col aller direkt überlappenden Termine + 1
  // 3. Cards die mit niemandem direkt überlappen → _totalCols=1 (volle Breite)
  // So bleibt Sophia (9-13:05) volle Breite obwohl Pause sie indirekt mit Yvonne (14:00+) verbindet.
  const layoutAppts = (appts) => {
    if (!appts.length) return [];
    const sorted = [...appts].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    const result = sorted.map(apt => ({ ...apt, _col: 0, _totalCols: 1, _layout: "full" }));
    // Step 1: Greedy column-assign — erste Spalte ohne direkten Konflikt
    const colEnd = []; // colEnd[i] = end-time des letzten Termins in dieser Spalte
    for (const apt of result) {
      const aS = timeToMinutes(apt.startTime);
      const aE = aS + (apt.totalDuration || 30);
      let placed = false;
      for (let i = 0; i < colEnd.length; i++) {
        if (colEnd[i] <= aS) {
          colEnd[i] = aE;
          apt._col = i;
          placed = true;
          break;
        }
      }
      if (!placed) {
        apt._col = colEnd.length;
        colEnd.push(aE);
      }
    }
    // Step 2: _totalCols = max _col der direkt überlappenden Termine + 1
    // CONFLICT_VISUAL_v2 -- Zusätzlich _realConflict: echtes Warndreieck NUR wenn zwei BESCHÄFTIGTE
    // Termine verschiedener Kunden/Gruppen überlappen. Liegt ein busy-Termin in einer FREMDEN
    // Einwirkzeit (idle-Phase, busy_pct=0) — gewollte Zwei-Kunden-Überlappung — ist das KEIN Konflikt.
    // Das Side-by-Side-Layout ("split") bleibt unverändert (beide Karten sollen sichtbar sein).
    for (const apt of result) {
      const aS = timeToMinutes(apt.startTime);
      const aE = aS + (apt.totalDuration || 30);
      // IDLE_PHASE_BLOCK_v1 -- blockierte Einwirkzeit zaehlt nicht mehr als "frei" -> Ueberlappung = echter Konflikt
      const aIdle = apt.phaseParentId && (apt.phaseBusyPct === 0 || apt.phaseBusyPct === "0") && !apt.phaseKeepBusy;
      const aGroup = apt.phaseParentId || apt.id;
      let maxCol = apt._col;
      let overlapCount = 0;
      let realConflict = false;
      for (const other of result) {
        if (other === apt) continue;
        const bS = timeToMinutes(other.startTime);
        const bE = bS + (other.totalDuration || 30);
        if (aS < bE && bS < aE) {
          maxCol = Math.max(maxCol, other._col);
          overlapCount++;
          const bIdle = other.phaseParentId && (other.phaseBusyPct === 0 || other.phaseBusyPct === "0") && !other.phaseKeepBusy; // IDLE_PHASE_BLOCK_v1
          const bGroup = other.phaseParentId || other.id;
          if (!aIdle && !bIdle && aGroup !== bGroup) realConflict = true;
        }
      }
      apt._totalCols = maxCol + 1;
      apt._layout = apt._totalCols > 1 ? "split" : "full";
      apt._realConflict = realConflict;
    }
    return result;
  };

  const getApptStyle = (apt, emp, overrideDur) => {
    const startMin = timeToMinutes(apt.startTime) - gridStartHour * 60; // CAL_DISPLAY_WINDOW_v1
    const top = (startMin / 60) * 64;
    const dur = (typeof overrideDur === "number" && overrideDur > 0) ? overrideDur : apt.totalDuration;
    const height = (dur / 60) * 64;
    // CAL_LUXURY_v3 -- klares Side-by-Side mit deutlichem Gap. Cascade verworfen.
    const _layout = apt._layout || "full";
    const isOverlap = _layout === "split";
    const colWidth = 100 / (apt._totalCols || 1);
    const leftPct = (apt._col || 0) * colWidth;
    const zIndex = 2;
    const empColor = emp?.color || "#9CA3AF";
    // VACATION_AUTO_SYNC_v1 -- vacation_auto wird auch als Blocker behandelt (gestreift, kein "Kunde"-Label)
    const isBlocker = apt.source === "blocker" || apt.source === "vacation_auto";
    // APPOINTMENT_PHASES_v1 -- Idle-Phase (Einwirkzeit): transparent/luftig damit Mariella sieht "hier frei"
    const isIdlePhase = apt.phaseParentId && (apt.phaseBusyPct === 0 || apt.phaseBusyPct === "0");
    // APPOINTMENT_ASSISTANT_v1 -- Assistenz-Block (anderer Mitarbeiter hilft beim Termin)
    const isAssistant = apt.source === "assistant";
    if (isAssistant) {
      // Master-Stylist-Farbe via phase_parent — fallback: aktueller emp-Color
      return {
        position: "absolute", top, height: Math.max(height, 24), borderRadius: 6,
        padding: "4px 8px", fontSize: 10, cursor: "pointer", zIndex: 2, lineHeight: 1.2,
        background: `repeating-linear-gradient(135deg, ${empColor}22 0 6px, ${empColor}10 6px 12px)`,
        border: `1.5px dashed ${empColor}`,
        color: COLORS.text, fontWeight: 600,
        left: `calc(${leftPct}% + 2px)`, width: `calc(${colWidth}% - 4px)`,
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
        boxShadow: "none",
        transition: "background 0.12s ease",
      };
    }
    if (isIdlePhase) {
      return {
        position: "absolute", top, height: Math.max(height, 22), borderRadius: 8,
        padding: "4px 8px", fontSize: 10, cursor: "pointer", zIndex: 1, lineHeight: 1.2,
        background: "rgba(255,255,255,0.02)",
        border: `1px dashed ${empColor}66`,
        color: COLORS.textLight,
        fontStyle: "italic", fontWeight: 500,
        left: `calc(${leftPct}% + 2px)`, width: `calc(${colWidth}% - 4px)`,
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
        boxShadow: "none",
        transition: "background 0.12s ease",
      };
    }
    /* CAL_DAY_POLISH_v1 -- Status-aware top-border-color */
    const statusBorder =
      apt.status === "confirmed" ? "transparent" :
      apt.status === "pending"   ? "#F59E0B" :
      (apt.status === "no_show" || apt.status === "noshow") ? "#EF4444" : "transparent"; /* PREPAY_LEDGER_v1: DB-Wert ist no_show */
    /* BLOCKER_VISUAL_v2 -- "Quiet Block": neutral, abgedämpft, keine Streifen.
       Halbtransparent dunkler als Spalten-Hintergrund, subtile Dot-Textur,
       inset-Shadow für "eingesenkten" Look. Kein Border (sonst wirkt's wie Baustelle). */
    if (isBlocker) {
      // BLOCKER_TYPE_COLOR_v1 -- Abwesenheits-/Block-Typen farblich: Krank=rot, Urlaub=orange, Vertreter=blau, Neukunde=gruen
      const _bn = (apt.notes || "").toLowerCase();
      const _bc = _bn.includes("krank") ? "#EF4444"
                : (_bn.includes("urlaub") || apt.source === "vacation_auto") ? "#F59E0B"
                : _bn.includes("vertret") ? "#3B82F6"
                : _bn.includes("neukunde") ? "#10B981"
                : (blockerKind(apt.notes).color || null);   // PAUSE_INTERN_SPLIT_v1 -- neue interne Typen farbig
      if (_bc) {
        return {
          position: "absolute", top, height: Math.max(height, 28), borderRadius: 6,
          padding: "2px 8px", fontSize: 10.5, cursor: "pointer", zIndex: 2, lineHeight: 1.2,
          backgroundColor: `${_bc}1F`,
          backgroundImage: `radial-gradient(circle, ${_bc}40 0.5px, transparent 1px)`,
          backgroundSize: "9px 9px",
          backgroundPosition: "center",
          borderLeft: `3px solid ${_bc}`,
          color: _bc,
          fontStyle: "normal", fontWeight: 700, letterSpacing: 0.2,
          left: `calc(${leftPct}% + 2px)`, width: `calc(${colWidth}% - 4px)`,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          transition: "background 0.12s ease",
        };
      }
      return {
        position: "absolute", top, height: Math.max(height, 28), borderRadius: 6,
        padding: "2px 8px", fontSize: 10.5, cursor: "pointer", zIndex: 2, lineHeight: 1.2,
        // Mehrlagiges Background: Dot-Pattern OBEN auf einer dunkleren Tinte (CSS-Var greift in Dark+Light)
        backgroundColor: "var(--cardHover, rgba(0,0,0,0.06))",
        backgroundImage: "radial-gradient(circle, rgba(128,128,128,0.18) 0.5px, transparent 1px)",
        backgroundSize: "9px 9px",
        backgroundPosition: "center",
        border: "none",
        color: "var(--textLight, rgba(120,120,128,0.85))",
        fontStyle: "normal", fontWeight: 600, letterSpacing: 0.2,
        left: `calc(${leftPct}% + 2px)`, width: `calc(${colWidth}% - 4px)`,
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.10), inset 0 2px 4px rgba(0,0,0,0.06)",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        transition: "background 0.12s ease",
      };
    }
    // CAL_LUXURY_v3 + CAL_LUXURY_v5 -- min-height runter (war 60, jetzt 24): vermeidet
    // dass 30-min Cards in den nachfolgenden Termin reinquellen.
    return {
      position: "absolute", top, height: Math.max(height, 24),
      borderRadius: 10,
      padding: isOverlap ? "6px 8px" : "6px 10px 6px 12px",
      fontSize: isOverlap ? 10.5 : 11.5,
      cursor: "pointer", zIndex, lineHeight: 1.3,
      background: isOverlap
        ? `linear-gradient(135deg, ${empColor}48, ${empColor}30)`
        : `linear-gradient(135deg, ${empColor}26, ${empColor}14)`,
      borderLeft: `3px solid ${empColor}`,
      borderTop: statusBorder !== "transparent" ? `2px solid ${statusBorder}` : "none",
      // CAL_LUXURY_v3 — bei Overlap: 8px Gap zwischen Cards (statt 4px)
      left: `calc(${leftPct}% + ${isOverlap ? 5 : 3}px)`,
      width: `calc(${colWidth}% - ${isOverlap ? 10 : 6}px)`,
      overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
      boxShadow: isOverlap
        ? `0 2px 10px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.10)`
        : `0 1px 3px rgba(0,0,0,0.08), 0 1px 1px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.06)`,
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
    };
  };

  // SEASONAL_DECOR_v1 -- Saison nach HEUTE (Badge/Herz/Klammer), 🍻 auf Karten nur, wenn auch der
  // angezeigte Tag im Zeitraum liegt. Schalter pro Salon (salons.seasonal_decor_enabled, Default AN);
  // im Demo-Modus fehlt der Schlüssel -> AN (gut fürs Verkaufsgespräch).
  const seasonalOn = enabledFeatures.seasonal_decor !== false;
  const season = seasonalOn ? activeSeason(today) : null;
  const seasonOnSelected = !!season && dateInSeason(season, selectedDate);
  // Handy (< 768): neben "Kalender" ist kein Platz (h1 würde "Kalender…" abschneiden) -> Badge wandert
  // verkürzt in die Untertitel-Zeile. Tablet/Desktop: eine Zeile neben dem Titel (Toby-Regel i).
  const _narrowHead = typeof window !== "undefined" && window.innerWidth < 768;
  const _subtitleText = calView === "day" ? "Tagesansicht" : calView === "month" ? "Monatsübersicht" : "Jahresübersicht";

  return (
    <div>
      {/* SEASONAL_DECOR_v1 (i): Badge in EINER Zeile neben "Kalender" (h1 ist nowrap); compact = Kopf enger (h) */}
      <Header compact
        title={season && !_narrowHead ? <>Kalender<SeasonBadge season={season} /></> : "Kalender"}
        subtitle={_subtitleText} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* SEASONAL_DECOR_v1: auf dem Handy passt neben "Kalender" nichts mehr (Kopfzeile ~160 px breit,
              h1/Untertitel schneiden mit "…" ab) -> Badge steht dort verkürzt in der Werkzeugleiste vor Tag/Monat/Jahr */}
          {season && _narrowHead && <SeasonBadge season={season} />}
          {/* View Toggle */}
          <div style={{ display: "flex", background: COLORS.background, borderRadius: 10, padding: 3 }}>
            {[{k:"day",l:"Tag"},{k:"month",l:"Monat"},{k:"year",l:"Jahr"}].map(v => (
              <button key={v.k} onClick={() => setCalView(v.k)}
                style={{ padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
                  background: calView === v.k ? "var(--card)" : "transparent", color: calView === v.k ? COLORS.primary : COLORS.textLight,
                  boxShadow: calView === v.k ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                {v.l}
              </button>
            ))}
          </div>
          {/* Navigation */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Btn variant="ghost" size="sm" onClick={calView === "day" ? prevDay : calView === "month" ? prevMonth : prevYear}><ChevronLeft size={16} /></Btn>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}><span onClick={() => { setShowDatePicker(s => !s); setDpMonth(selectedDate.getMonth()); setDpYear(selectedDate.getFullYear()); }} style={{ fontSize: window.innerWidth < 768 ? 12 : 15, fontWeight: 700, textAlign: "center", width: window.innerWidth < 768 ? 180 : 260, cursor: "pointer", padding: "4px 8px", borderRadius: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }} title={calView === "day" ? selectedDate.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : undefined}>
              {/* CALENDAR_HEADER_STABLE_v1 -- feste width (statt minWidth), damit ChevronLeft/Right
                  beim Tageswechsel nicht mehr horizontal springen.
                  Mobile: 180px (kurze Labels), Desktop: 260px (lange Labels passen) */}
              {calView === "day" ? `${selectedDate.toLocaleDateString("de-DE", { weekday: window.innerWidth < 768 ? "short" : "long", day: "numeric", month: window.innerWidth < 768 ? "short" : "long" })}${window.innerWidth < 768 ? ` · KW ${getISOWeek(selectedDate)}` : ""}` /* DAY_STRIP_OCC_v1: am Desktop steht die KW jetzt vorne an der Tagesleiste */
                : calView === "month" ? selectedDate.toLocaleDateString("de-DE", { month: "long", year: "numeric" })
                : String(selectedDate.getFullYear())}
            </span>
                    {showDatePicker && <div onClick={() => setShowDatePicker(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} />}
                    {showDatePicker && (
                      <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", zIndex: 1000, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", minWidth: 260 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span onClick={e => { e.stopPropagation(); if(dpMonth===0){setDpMonth(11);setDpYear(y=>y-1);}else{setDpMonth(m=>m-1);} }} style={{ cursor:"pointer", padding:"2px 8px", fontSize:18, userSelect:"none" }}>‹</span>
                          <span style={{ fontWeight: 600, fontSize: 13 }}>{new Date(dpYear, dpMonth).toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</span>
                          <span onClick={e => { e.stopPropagation(); if(dpMonth===11){setDpMonth(0);setDpYear(y=>y+1);}else{setDpMonth(m=>m+1);} }} style={{ cursor:"pointer", padding:"2px 8px", fontSize:18, userSelect:"none" }}>›</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, textAlign: "center" }}>
                          {["Mo","Di","Mi","Do","Fr","Sa","So"].map(d=><div key={d} style={{ fontSize:10, color:COLORS.textLight, padding:"2px 0", fontWeight:600 }}>{d}</div>)}
                          {(()=>{ const fd=new Date(dpYear,dpMonth,1).getDay(); const sh=fd===0?6:fd-1; const dim=new Date(dpYear,dpMonth+1,0).getDate(); const c=[]; for(let i=0;i<sh;i++)c.push(<div key={"e"+i}/>); for(let d=1;d<=dim;d++){const isT=d===today.getDate()&&dpMonth===today.getMonth()&&dpYear===today.getFullYear();const isS=d===selectedDate.getDate()&&dpMonth===selectedDate.getMonth()&&dpYear===selectedDate.getFullYear();c.push(<div key={d} onClick={e=>{e.stopPropagation();setSelectedDate(new Date(dpYear,dpMonth,d));setShowDatePicker(false);if(calView!=="day")setCalView("day");}} style={{padding:"6px 0",borderRadius:8,fontSize:13,cursor:"pointer",background:isS?COLORS.primary:isT?"rgba(139,92,246,0.15)":"transparent",color:isS?"#fff":COLORS.text,fontWeight:isT?700:400}}>{d}</div>);}return c;})()}
                        </div>
                        <div style={{ marginTop:8, textAlign:"center" }}>
                          <span onClick={e=>{e.stopPropagation();setSelectedDate(today);setShowDatePicker(false);if(calView!=="day")setCalView("day");}} style={{ cursor:"pointer", fontSize:12, color:COLORS.primary, fontWeight:600 }}>Heute</span>
                        </div>
                      </div>
                    )}
                    </div>
            <Btn variant="ghost" size="sm" onClick={calView === "day" ? nextDay : calView === "month" ? nextMonth : nextYear}><ChevronRight size={16} /></Btn>
          </div>
          <Btn variant="secondary" size="sm" onClick={() => setSelectedDate(today)}>Heute</Btn>
        </div>
        {calView === "day" && <Btn onClick={() => { setBookingStartNewCustomer(false); setShowBooking(true); }} size={window.innerWidth < 768 ? "sm" : undefined}><Plus size={16} /> Neuer Termin</Btn>}
        {/* CAL_NEW_CUST_QUICK_BOOK_v1 -- 2. Button für "Neukunde + Termin" direkt aus Kalender (Julia braucht Speed an der Kasse) */}
        {calView === "day" && setCustomers && <Btn variant="secondary" onClick={() => { setBookingStartNewCustomer(true); setShowBooking(true); }} size={window.innerWidth < 768 ? "sm" : undefined} title="Neuen Kunden anlegen und gleich Termin vergeben"><UserPlus size={16} /> Neukunde + Termin</Btn>}
        {/* KASSE_CAL_BUTTON_v1 -- direkt vom Kalender in die Shearly Kasse (ein Gerät reicht) */}
        {posShearly && <span data-feature="KASSE_CAL_BUTTON_v1"><Btn variant="secondary" onClick={openKasse} disabled={kasseBusy} size={window.innerWidth < 768 ? "sm" : undefined} title="Shearly Kasse in einem neuen Tab öffnen">{kasseBusy ? "Öffne …" : "🧾 Kasse"}</Btn></span>}
        {/* PAUSE_DATE_PREFILL_v1 -- angezeigten Tag als Datum vorausfüllen: dateStr (String) statt selectedDate (Date-Objekt -> Datumsfeld blieb leer) */}
        <BlockerButton onGoToShifts={onGoToShifts} employees={employees} appointments={appointments} setAppointments={setAppointments} defaultDate={dateStr} btnSize={window.innerWidth < 768 ? "sm" : undefined} triggerOpen={pauseSignal} triggerPrefill={pausePrefill} />
      </div>

      {/* Quick Day Chips */}
                {calView === "day" && (
                  /* QUICK_DAY_PILLS_v3 -- Kalenderwoche Mo-So + Wochen-Chevrons.
                     Pills bleiben in einer Woche, switchen erst wenn user explizit
                     auf Wochen-Chevron klickt ODER per Top-Arrow in andere Woche wechselt. */
                  /* CAL_NAV_FIXED_v1 (Toby 29.07.) -- Wochen-Pfeile FEST links/rechts AUSSERHALB des
                     Scroll-Bereichs + Pills mit fester Mindestbreite: Pfeile verrutschen nicht mehr,
                     egal welcher Tag angeklickt ist oder wie lang die Tages-Beschriftungen sind. */
                  <div style={{ display: "flex", gap: 6, padding: "4px 0 2px", alignItems: "center" }}>
                    {(() => {
                      // Montag der Woche von selectedDate berechnen (ISO: Mo=1, So=0 → -6)
                      const selDay = selectedDate.getDay();
                      const daysToMonday = selDay === 0 ? -6 : 1 - selDay;
                      const monday = new Date(selectedDate.getFullYear(), selectedDate.getMonth(),
                                              selectedDate.getDate() + daysToMonday);
                      const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const tt = todayMid.toDateString();
                      const tomorrow = new Date(todayMid.getTime() + 86400000).toDateString();

                      const prevWeek = () => {
                        const d = new Date(selectedDate.getFullYear(), selectedDate.getMonth(),
                                           selectedDate.getDate() - 7);
                        setSelectedDate(d); setShowDatePicker(false);
                      };
                      const nextWeek = () => {
                        const d = new Date(selectedDate.getFullYear(), selectedDate.getMonth(),
                                           selectedDate.getDate() + 7);
                        setSelectedDate(d); setShowDatePicker(false);
                      };
                      const chevronStyle = {
                        width: 28, height: 28, borderRadius: 14, border: "1px solid var(--border)",
                        background: "var(--card)", color: COLORS.textLight, fontSize: 13, fontWeight: 700,
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "background 0.15s ease, color 0.15s ease",
                      };

                      // DAY_STRIP_OCC_v1 (Toby 23.09.2026: "A und B bauen")
                      // A: Pfeile direkt an den Tages-Chips (vorher hing "›" ganz rechts neben dem Lebkuchenherz,
                      //    weil der Chip-Bereich flex:1 die ganze Breite nahm), KW davor, Heute mit Ring.
                      //    Chips mit FESTER Breite -> die Pfeile verrutschen beim Wochenwechsel nicht (CAL_NAV_FIXED_v1).
                      // B: unter jedem Tag Auslastung des Teams (occTeamDay = gleiche Rechnung wie die Monatsansicht):
                      //    Balken + "x Std frei" / "voll" / "zu" / Feiertag. Vergangene Tage ohne Anzeige.
                      const occTone = (pct) => pct >= 85 ? COLORS.danger : pct >= 60 ? COLORS.warning : COLORS.success;
                      const occText = (o) => {
                        if (o.avail <= 0) return "zu";
                        if (o.free < 30) return "voll";
                        if (o.free < 60) return "30 Min frei";
                        return `${String(Math.floor(o.free / 30) / 2).replace(".", ",")} Std frei`;
                      };
                      const pills = [];
                      for (let i = 0; i < 7; i++) {
                        const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
                        const isA = d.toDateString() === selectedDate.toDateString();
                        const dow = d.getDay();
                        const isWeekend = (dow === 0 || dow === 6);
                        const bg = isA
                          ? COLORS.primary
                          : (isWeekend ? "rgba(156,163,175,0.18)" : "var(--cardHover)");
                        const dt = d.toDateString();
                        const isTodayChip = dt === tt;
                        const isPhone = window.innerWidth < 768; // Handy: "Di 22." (Monat steht oben im Titel), schmalere Chips
                        let label;
                        if (isTodayChip) label = "Heute";
                        else if (dt === tomorrow) label = "Morgen";
                        else label = isPhone ? `${["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][dow]} ${d.getDate()}.`
                                             : d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
                        const isPast = d < todayMid;
                        const hol = calHolidays.find(h => h.date === formatDate(d));
                        const o = isPast || hol ? null : occTeamDay(d);
                        const subText = hol ? (hol.closure ? "Betriebsurlaub" : "Feiertag") : (o ? occText(o) : "");
                        const fillPct = o && o.pct != null ? Math.max(4, o.pct) : 0;
                        pills.push(
                          <div key={i} onClick={() => { setSelectedDate(d); setShowDatePicker(false); }}
                               title={hol ? hol.name : (o && o.tip) || undefined}
                               ref={isA ? (el => {
                                 // Handy: gewaehlten Tag in Sicht halten -- nur wenn sich die Auswahl aendert, damit ein
                                 // Neuladen der Termine den Streifen nicht zuruecksetzt, waehrend jemand wischt
                                 // (nur die Leiste seitlich schieben, nie die Seite -- kein Springen nach oben)
                                 if (el && stripSelRef.current !== dt) {
                                   stripSelRef.current = dt;
                                   try {
                                     const box = el.parentElement, er = el.getBoundingClientRect(), br = box.getBoundingClientRect();
                                     if (box.scrollWidth > box.clientWidth) box.scrollLeft += (er.left + er.width / 2) - (br.left + br.width / 2); // gewaehlten Tag mittig
                                   } catch (e) {}
                                 }
                               }) : undefined}
                               style={{ padding: "5px 6px 6px", borderRadius: 12, fontSize: 12, cursor: "pointer",
                                        background: bg, color: isA ? "#fff" : COLORS.text,
                                        fontWeight: isA ? 600 : 400, whiteSpace: "nowrap", flexShrink: 0,
                                        width: isPhone ? 80 : 98, textAlign: "center", boxSizing: "border-box",
                                        border: `1.5px solid ${isTodayChip && !isA ? COLORS.primary : "transparent"}`,
                                        transition: "background 0.15s ease, transform 0.15s ease" }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
                            <div style={{ visibility: isPast ? "hidden" : "visible", marginTop: 4 }}>
                              <div style={{ height: 3, borderRadius: 2, overflow: "hidden",
                                            background: isA ? "rgba(255,255,255,0.35)" : "var(--border)" }}>
                                {fillPct > 0 && <div style={{ width: `${fillPct}%`, height: "100%", background: isA ? "#fff" : occTone(o.pct) }} />}
                              </div>
                              <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, lineHeight: 1.2,
                                            color: isA ? "rgba(255,255,255,0.9)" : COLORS.textLight }}>{subText || " "}</div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <>
                          {window.innerWidth >= 768 && (
                            <span title="Kalenderwoche" style={{ fontSize: 11, fontWeight: 600, color: COLORS.textLight, whiteSpace: "nowrap", flexShrink: 0, padding: "0 2px" }}>KW {getISOWeek(monday)}</span>
                          )}
                          <button key="prev" onClick={prevWeek} title="Vorherige Woche"
                                  style={chevronStyle}>‹</button>
                          <div className="shearly-daystrip" style={{ display: "flex", gap: 6, overflowX: "auto", WebkitOverflowScrolling: "touch", alignItems: "center", flex: "0 1 auto", minWidth: 0 }}>
                            {pills}
                          </div>
                          <button key="next" onClick={nextWeek} title="Nächste Woche"
                                  style={chevronStyle}>›</button>
                          {/* SEASONAL_DECOR_v1: Lebkuchenherz bleibt rechts aussen (die Waescheklammer haengt an right:22 und haelt es) */}
                          {season && <div style={{ marginLeft: "auto", display: "flex", flexShrink: 0 }}><SeasonCorner season={season} /></div>}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* CALENDAR_KW_HOLIDAYS_v1 -- Feiertags-Banner (Day-View, nutzt calHolidays = Bayern + bundesweit) */}
                {calView === "day" && (() => {
                  const hol = calHolidays.find(h => h.date === formatDate(selectedDate));
                  if (!hol) return null;
                  return (
                    <div style={{ padding: "10px 14px", background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 12, fontSize: 13, color: COLORS.textLight, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{hol.closure ? "🏖" : "🎉"}</span>
                      <strong style={{ color: COLORS.text }}>{hol.name}</strong>
                      <span>· {hol.closure ? "Betriebsurlaub – online nicht buchbar" : "Feiertag"}</span>
                    </div>
                  );
                })()}

                {/* ── Day View ── */}
      {calView === "day" && (() => {
        // CAL_DAY_POLISH_v3 -- adaptive Spaltenbreite je nach Anzahl Mitarbeiter + Bildschirm.
        // 1-3 MA: feste Breite (gedeckelt, sonst zu breit). 4+ MA: ECHTE Flex-Verteilung (flex:1)
        // damit Spalten den GESAMTEN Card-Bereich füllen — kein leerer Platz rechts wie bei Salon Wolf.
        const N = Math.max(employees.length, 1);
        const sidebarApprox = window.innerWidth < 768 ? 0 : 280;
        const padApprox = 48;
        const available = Math.max(window.innerWidth - sidebarApprox - padApprox - 56, 240);
        let colWidth;
        if (window.innerWidth < 768) colWidth = 180;
        else if (N <= 2) colWidth = Math.min(440, Math.max(280, Math.floor(available / N)));
        else if (N === 3) colWidth = Math.min(360, Math.max(260, Math.floor(available / N)));
        else colWidth = Math.max(220, Math.floor(available / N)); // min als Sicherheit + für cardWidthApprox
        // CAL_DAY_POLISH_v3: ab 4 MA Desktop → flex statt fixed-width
        const useFlexWidth = N >= 4 && window.innerWidth >= 768;
        const innerWidth = N * colWidth + 56;
        const flexMinWidth = N * 220 + 56; // Mindestbreite damit bei schmalen Screens horizontal gescrollt wird
        const headerHeight = 64; // CAL_AVATAR_BIG_v2: Stats neben dem Foto statt darunter -> zurueck auf 64
                                 // (Now-Line + Zeitspalten-Spacer rechnen automatisch mit, haengen an dieser Variable)
        // NOW_LINE_GLOBAL_v1 -- berechne Top fuer horizontale Now-Line (nur heute)
        const _today = new Date();
        const _isToday = _today.toDateString() === selectedDate.toDateString();
        const _nowMin = _isToday ? (_today.getHours() * 60 + _today.getMinutes() - gridStartHour * 60) : -1; // CAL_DISPLAY_WINDOW_v1
        const _nowTop = (_nowMin >= 0 && _nowMin <= (gridEndHour - gridStartHour) * 60) ? (headerHeight + (_nowMin / 60) * 64) : null;
        return (
        <>
        {/* SEASONAL_DECOR_v1: Wäscheklammer "Difigiano" hängt an der Oberkante des Rasters (0-px-Wrapper, pointer-events none).
            SEASON_PEG_SMALL_TEAM_v1 (Toby 19.09.2026, Foto aus Salon 12: "die klammer fehlt zum wiesn design" --
            "handy ohne klammer, und sie soll das herz halten"): bis dahin hing die Klammer an `useFlexWidth`,
            also an "4+ Mitarbeiter". In Salons mit 1-3 Mitarbeitern (Bio Beauty Hair, Désirée, Roslyn, Sesto)
            fehlte sie deshalb, und das Lebkuchenherz hing ohne Halt in der Luft -- obwohl dort MEHR Platz ist,
            nicht weniger. Die Klammer sitzt am rechten Rand der vollen Breite (right: 22), also genau unter dem
            Herz neben den Tages-Chips: sie hält es, unabhängig von der Anzahl der Spalten. Auf dem Handy bleibt
            sie aus (zu eng). Toby-Regel bleibt gewahrt: rechts aussen steht kein Name und kein Termin. */}
        {season && window.innerWidth >= 768 && <SeasonPeg season={season} />}
        <Card style={{ padding: 0, overflow: "auto" }}>
          <div style={{ display: "flex", width: useFlexWidth ? "100%" : innerWidth, minWidth: useFlexWidth ? flexMinWidth : undefined, position: "relative" }}>
            {/* NOW_LINE_GLOBAL_v1 — eine horizontale Linie ueber ALLE Mitarbeiter */}
            {_nowTop !== null && (
              <div className="shearly-now-line" style={{ top: _nowTop }} />
            )}
            {/* Time column — CAL_DAY_POLISH_v1: box-shadow statt border (robuster bei Windows-Skalierung)
                CAL_TIMECOL_MOBILE_FIX_v1: text-align right + leicht breiter auf Mobile damit "08:00" nicht
                an der Bildkante hängt + sauber an der Trennlinie zur Mitarbeiter-Spalte ausgerichtet. */}
            <div style={{ width: window.innerWidth < 768 ? 52 : 56, flexShrink: 0, boxShadow: `inset -1px 0 0 ${COLORS.border}` }}>
              <div style={{ height: headerHeight, boxShadow: `inset 0 -1px 0 ${COLORS.border}` }} />
              {hours.map(h => (
                <div key={h} style={{ height: 64, boxShadow: `inset 0 -1px 0 ${COLORS.border}`, padding: "4px 6px 4px 4px", fontSize: window.innerWidth < 768 ? 10.5 : 11, color: COLORS.textLight, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {`${String(h).padStart(2, "0")}:00`}
                </div>
              ))}
            </div>

            {/* Employee columns */}
            {employees.map(emp => {
              const empAppts = dayAppts.filter(a => a.employeeId === emp.id);
              const isEmpty = empAppts.length === 0;
              const empColor = emp.color || "#9CA3AF";
              /* EMPLOYEE_SORT_v2 -- Long-Press auf Header startet Drag-Mode */
              const isDragging = empDragId === emp.id;
              const isDropTarget = empDropTargetId === emp.id && empDragId !== emp.id;
              const headerClasses = (isDragging ? "shearly-emp-drag-active " : "") + (isDropTarget ? "shearly-emp-drop-target " : "");
              // EMPLOYEE_SORT_v2.4 -- userSelect war zu aggressiv auf gesamter Spalte und hat
              // HTML5-Drag&Drop von Terminen gebrochen. Jetzt: nur kosmetisch (Tap-Highlight aus),
              // userSelect/touchCallout nur dort wo nötig (Header + Empty-State-Text + Termin-Karte).
              // CAL_DAY_POLISH_v3 -- bei ≥4 MA flex:1 statt fester Breite.
              const colStyle = useFlexWidth
                ? { flex: "1 1 0", minWidth: 220 }
                : { width: colWidth, flexShrink: 0 };
              return (
                <div key={emp.id} style={{ ...colStyle, boxShadow: `inset -1px 0 0 ${COLORS.border}`, position: "relative", WebkitTapHighlightColor: "transparent" }}>
                  {/* Color-Strip oben: 4px in MA-Farbe */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: empColor, zIndex: 3 }} />
                  <div
                    data-emp-header={emp.id}
                    className={headerClasses}
                    onMouseDown={(e) => { if (e.button === 0) startEmpPress(emp.id, e.clientX, e.clientY); }}
                    onMouseUp={cancelEmpPress}
                    onMouseLeave={cancelEmpPress}
                    onTouchStart={(e) => startEmpPress(emp.id, e.touches[0].clientX, e.touches[0].clientY)}
                    onTouchCancel={cancelEmpPress}
                    /* EMP_SORT_ANDROID_FIX_v1 -- Android-Chrome zeigte beim Long-Press das System-
                       Kontextmenue (Drucken/Teilen) statt den Drag-Mode zu starten -> hier wie bei
                       den Termin-Karten unterdruecken, damit der Long-Press durchlaeuft. */
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                      height: headerHeight,
                      boxShadow: `inset 0 -1px 0 ${COLORS.border}`,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                      paddingTop: 6,
                      background: `linear-gradient(180deg, ${empColor}0d 0%, transparent 100%)`,
                      cursor: isDragging ? "grabbing" : "grab",
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      WebkitTouchCallout: "none",
                      // EMPLOYEE_SORT_v2.3 -- iPad-Fix: touchAction "none" PERMANENT auf MA-Header,
                      // damit Safari beim Touch-Start NICHT auf Scroll/Swipe entscheidet (die Entscheidung
                      // fällt VOR dem ersten touchmove, also Late-Switch hilft nicht).
                      touchAction: "none",
                      transition: "background 0.15s",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* EMPLOYEE_AVATAR_UPLOAD_v1 -- Avatar-Bild im Kalender-Header (mit Fallback-Bubble)
                          CAL_AVATAR_BIG_v1 (Toby 31.07.) -- deutlich größer + Rounded Square statt 26px-Kreis
                          CAL_AVATAR_BIG_v2 (Toby 31.07.) -- Stats-Zeile („Keine Termine"/„N Termine · X €")
                          neben das Foto unter den Namen gerückt statt eigener Zeile darunter →
                          Kopfbalken wieder auf 64px runter, Foto bestimmt die Höhe */}
                      <EmployeeAvatar emp={{ ...emp, color: empColor }} size={46} fontSize={17} borderWidth={2} shape="square" />
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap" }}>{emp.name}</span>
                          <EmployeeBadges employeeId={emp.id} badgeAwards={badgeAwards} size={20} />
                        </div>
                        <div style={{ fontSize: 10, color: COLORS.textLight, fontWeight: 500 }}>
                          {/* CAL_HEADER_REAL_COUNT_v1 (Toby 31.07.) -- nur ECHTE Kundentermine zählen:
                              Urlaub/Pause/Assistenz raus, Phasen-Gruppe = 1 Termin (nicht jede Phase einzeln),
                              Preis kommt bei Phasen-Terminen vom (ausgeblendeten) Master statt 0 € der Kinder */}
                          {(() => {
                            const _seen = new Set();
                            let _n = 0, _sum = 0;
                            empAppts.forEach(a => {
                              if (a.source === "blocker" || a.source === "vacation_auto" || a.source === "assistant") return;
                              if (a.phaseParentId) {
                                if (!_seen.has(a.phaseParentId)) {
                                  _seen.add(a.phaseParentId); _n++;
                                  const _m = appointments.find(x => x.id === a.phaseParentId);
                                  _sum += (_m && _m.totalPrice) || 0;
                                }
                              } else { _n++; _sum += a.totalPrice || 0; }
                            });
                            return _n === 0 ? "Keine Termine" : `${_n} Termin${_n === 1 ? "" : "e"} · ${Math.round(_sum)} €`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* DRAG_DROP_v1 + DRAG_TIME_HINT_v1 -- Drop-Container mit Live-Uhrzeit-Anzeige beim Drag
                      CAL_CLICK_TO_BOOK_v1 -- Klick auf leere Zeit-Fläche öffnet "Neuer Termin"-Dialog
                      mit Datum + Mitarbeiter + Uhrzeit (15-Min-Snap) bereits vorausgefüllt. */}
                  <div data-drop-col={emp.id} style={{ position: "relative" }}
                       onClick={(e) => {
                         // Nur reagieren wenn auf leere Fläche geklickt (nicht auf eine Termin-Card)
                         if (e.target.closest("[data-appt-id]")) return;
                         try {
                           const rect = e.currentTarget.getBoundingClientRect();
                           const y = Math.max(0, e.clientY - rect.top);
                           const totalMin = Math.round((y / 64 * 60) / 15) * 15;
                           const h = gridStartHour + Math.floor(totalMin / 60); // CAL_DISPLAY_WINDOW_v1
                           const m = totalMin % 60;
                           if (h < gridStartHour || h > gridEndHour + 1) return;
                           const time = String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
                           // CAL_SLOT_CHOOSER_v1 -- erst fragen: Termin buchen oder Pause?
                           setSlotChoice({ employeeId: emp.id, date: dateStr, startTime: time });
                         } catch (_) {}
                       }}
                       onDragOver={(e) => {
                         e.preventDefault();
                         e.dataTransfer.dropEffect = "move";
                         try {
                           const rect = e.currentTarget.getBoundingClientRect();
                           const offY = dragOffsetYRef.current || 0;
                           const y = Math.max(0, e.clientY - rect.top - offY);
                           const mins = Math.round((y / 64 * 60) / 15) * 15;
                           const h = gridStartHour + Math.floor(mins / 60); // CAL_DISPLAY_WINDOW_v1
                           const m = mins % 60;
                           if (h >= gridStartHour && h <= gridEndHour + 1) {
                             const t = String(h).padStart(2,"0") + ":" + String(m).padStart(2,"0");
                             setDragHint({ empId: emp.id, time: t, x: e.clientX, y: e.clientY });
                             lastDragOverRef.current = { empId: emp.id, time: t }; // CAL_TOUCH_MOVE_v3
                           }
                         } catch (e) {}
                       }}
                       onDragLeave={() => { setDragHint(null); lastDragOverRef.current = null; }}
                       onDrop={(e) => { setDragHint(null); handleApptDrop(e, emp.id); }}>
                    {hours.map(h => (
                      /* HALF_HOUR_LINE_v2 -- Hour-Cell mit Bottom-Border + absolutes Sub-Div bei 50% Höhe für :30-Linie.
                         Vorherige boxShadow-Spread-Variante hat die Linien komplett verschluckt. */
                      <div key={h} className="shearly-time-slot" style={{
                        height: 64,
                        boxShadow: `inset 0 -1px 0 ${COLORS.border}`,
                        cursor: "copy",
                        position: "relative"
                      }}>
                        <div style={{
                          position: "absolute",
                          left: 0, right: 0, top: 31,
                          height: 1,
                          background: COLORS.border,
                          opacity: 0.35,
                          pointerEvents: "none"
                        }} />
                      </div>
                    ))}
                    {/* SHIFT_WINDOW_CAL_v1 -- Nicht-Arbeitszeiten ausgrauen: vor Schichtbeginn,
                        nach Schichtende bzw. ganzer Tag bei frei/krank/Urlaub. So sieht das Team
                        auf einen Blick, wer von wann bis wann da ist (z.B. "kommt erst 10 Uhr").
                        pointer-events:none -> stoert weder Klick-zum-Buchen noch Drag&Drop. */}
                    {(() => {
                      const win = dayShiftWindows[String(emp.id)];
                      if (!win || win.status === "unmanaged") return null;
                      const PXH = 64;                      // px pro Stunde (= Rasterhoehe/Stunde)
                      const gridTopMin = gridStartHour * 60; // CAL_DISPLAY_WINDOW_v1 -- Rasterbeginn (Standard 08:00)
                      const gridMins = hours.length * 60;  // sichtbarer Bereich in Minuten
                      const gridH = hours.length * PXH;
                      const rects = [];
                      if (win.status === "off") {
                        // frei / krank / Urlaub / kein Schichteintrag -> ganzer Tag grau
                        rects.push({ top: 0, height: gridH });
                      } else if (win.status === "window" && typeof win.start === "number" && typeof win.end === "number") {
                        // Grau VOR Schichtbeginn (z.B. 08:00-10:00 wenn MA erst um 10 kommt)
                        if (win.start > gridTopMin) {
                          const endRel = Math.min(win.start - gridTopMin, gridMins);
                          if (endRel > 0) rects.push({ top: 0, height: (endRel / 60) * PXH });
                        }
                        // Grau NACH Schichtende
                        if (win.end < gridTopMin + gridMins) {
                          const startRel = Math.max(win.end - gridTopMin, 0);
                          const topPx = (startRel / 60) * PXH;
                          rects.push({ top: topPx, height: gridH - topPx });
                        }
                      }
                      // SHIFT_BREAK_BOOK_v1 (Toby 29.09.2026, Meldung Hamza) -- Pause aus dem Schichtplan als
                      // graues Feld "Pause" (vorher unsichtbar, das Termin-Fenster zeigte die Zeit aber als belegt).
                      // Nur innerhalb der Arbeitszeit; Klicks gehen durch (Buchen in der Pause mit Rückfrage).
                      let pause = null;
                      if (win.status === "window" && typeof win.break_start === "number" && typeof win.break_end === "number") {
                        const bs = Math.max(win.break_start, win.start, gridTopMin);
                        const be = Math.min(win.break_end, win.end, gridTopMin + gridMins);
                        if (be > bs) {
                          pause = { top: ((bs - gridTopMin) / 60) * PXH, height: ((be - bs) / 60) * PXH,
                                    label: "Pause " + pdFmt(win.break_start) + "–" + pdFmt(win.break_end)
                                      + (win.break_day === "moved" ? " · nur heute" : "") };   // PAUSE_DAY_v1
                        }
                      }
                      // PAUSE_DAY_v1 -- an diesem Tag gestrichene Pause: gestrichelter Hinweis an der Schichtplan-Zeit
                      let noPause = null;
                      if (win.status === "window" && win.break_day === "none"
                          && typeof win.plan_break_start === "number" && typeof win.plan_break_end === "number") {
                        const bs = Math.max(win.plan_break_start, win.start, gridTopMin);
                        const be = Math.min(win.plan_break_end, win.end, gridTopMin + gridMins);
                        if (be > bs) noPause = { top: ((bs - gridTopMin) / 60) * PXH, height: ((be - bs) / 60) * PXH };
                      }
                      // PAUSE_DAY_v1 -- Inhaber/Salon-Login: Klick aufs Pausenfeld öffnet „Pause nur für diesen Tag“.
                      // Alle anderen: Klicks gehen wie bisher durch (Buchen in der Pause mit Rückfrage).
                      const _openPd = (e) => { e.stopPropagation(); openPauseDay(emp, win); };
                      return rects.map((r, i) => (
                        <div key={"offshift-" + i} className="shearly-offshift" style={{ top: r.top, height: r.height }} />
                      )).concat(noPause ? [
                        <div key="shift-nobreak" data-shift-nobreak="1"
                          style={{ position: "absolute", left: 4, right: 4, top: noPause.top, height: noPause.height, border: `1px dashed ${COLORS.border}`,
                                   borderRadius: 6, display: "flex", alignItems: "flex-start", justifyContent: "center", overflow: "hidden", pointerEvents: "none" }}>
                          {noPause.height >= 16 && (
                            <span onClick={canEditDayBreak ? _openPd : undefined} title={canEditDayBreak ? "Pause für diesen Tag ändern" : undefined}
                                  style={{ marginTop: 2, padding: "0 6px", borderRadius: 4, fontSize: 10.5, fontWeight: 600, lineHeight: "14px",
                                           color: COLORS.textLight, background: "var(--card)", opacity: 0.9, whiteSpace: "nowrap",
                                           pointerEvents: canEditDayBreak ? "auto" : "none", cursor: canEditDayBreak ? "pointer" : "default" }}>⏸ Pause entfällt heute</span>
                          )}
                        </div>
                      ] : []).concat(pause ? [
                        <div key="shift-break" className="shearly-offshift" data-shift-break="1"
                          onClick={canEditDayBreak ? _openPd : undefined}
                          title={canEditDayBreak ? "Pause für diesen Tag ändern" : undefined}
                          style={{ top: pause.top, height: pause.height, display: "flex", alignItems: "flex-start", justifyContent: "center", overflow: "hidden",
                                   ...(canEditDayBreak ? { pointerEvents: "auto", cursor: "pointer" } : {}) }}>
                          {pause.height >= 16 && (
                            <span style={{ marginTop: 2, padding: "0 6px", borderRadius: 4, fontSize: 10.5, fontWeight: 600, lineHeight: "14px",
                                           color: COLORS.textLight, background: "var(--card)", opacity: 0.9, whiteSpace: "nowrap",
                                           maxWidth: "calc(100% - 4px)", overflow: "hidden", textOverflow: "ellipsis" }}>⏸ {pause.label}</span>
                          )}
                        </div>
                      ] : []);
                    })()}
                    {/* CAL_DAY_POLISH_v1 -- Empty-State pro Mitarbeiter wenn keine Termine */}
                    {/* EXTRA_SHIFT_CAL_v1 -- bei Extra-Schicht / Krank stattdessen entsprechendes Label zeigen */}
                    {isEmpty && (() => {
                      const dayOv = (dayOverrides || []).find(o => Number(o.employee_id) === Number(emp.id));
                      const ovType = dayOv && dayOv.override_type;
                      // EMPLOYEE_SORT_v2.4 -- userSelect:none nur auf Empty-State-Box (pointer-events:none macht es nicht selektierbar via Click, aber Touch braucht trotzdem userSelect:none)
                      const emptyBoxStyle = { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6, pointerEvents: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" };
                      if (ovType === "sick") {
                        return (
                          <div style={emptyBoxStyle}>
                            <span style={{ fontSize: 28, opacity: 0.55 }}>🤒</span>
                            <span style={{ fontSize: 11, color: COLORS.danger, fontWeight: 700 }}>Heute krank</span>
                          </div>
                        );
                      }
                      if (ovType === "extra" && dayOv.start_time && dayOv.end_time) {
                        const s = String(dayOv.start_time).slice(0,5);
                        const e = String(dayOv.end_time).slice(0,5);
                        // EXTRA_SHIFT_CAL_DELETE_v1 (Toby 21.07.) -- Extra-Schicht direkt im Kalender
                        // entfernbar (vorher nur im Schichtplan; dort unerreichbar wenn zusätzlich
                        // Urlaub am selben Tag angezeigt wurde). Box ist jetzt klickbar.
                        return (
                          <div style={emptyBoxStyle}>
                            <button
                              onClick={async () => {
                                if (!confirm(`Extra-Schicht ${s}–${e} für ${emp.name} entfernen?\n\nDanach gilt wieder die reguläre Schicht dieses Tages.`)) return;
                                const res = await api.request("/shift-overrides/" + dayOv.id, { method: "DELETE" });
                                if (res && res.ok) {
                                  setOvReloadTick(t => t + 1);
                                  try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
                                } else {
                                  alert("Entfernen fehlgeschlagen: " + ((res && res.data && res.data.error) || "Server-Fehler"));
                                }
                              }}
                              title="Klicken zum Entfernen der Extra-Schicht"
                              style={{ pointerEvents: "auto", background: "transparent", border: `1px dashed ${COLORS.success}55`, borderRadius: 10, padding: "8px 14px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 24, opacity: 0.6 }}>➕</span>
                              <span style={{ fontSize: 11, color: COLORS.success, fontWeight: 700 }}>Extra-Schicht</span>
                              <span style={{ fontSize: 10, color: COLORS.textLight }}>{s}–{e}</span>
                              <span style={{ fontSize: 9, color: COLORS.textLight }}>Klick = entfernen</span>
                            </button>
                          </div>
                        );
                      }
                      // CAL_EMPTY_BLANK_v1 -- leere Spalte (kein Termin, kein Override): gar nichts anzeigen
                      // (vorher ☕ "Heute frei" -- war irrefuehrend, wenn MA arbeitet aber noch keine Buchung hat)
                      return null;
                    })()}
                    {/* NOW_LINE_GLOBAL_v1: pro-Spalten-Linie entfernt — globale Linie wird im inneren Flex-Container gerendert. */}
                    {layoutAppts(empAppts).map(apt => {
                      const cust = customers.find(c => c.id === apt.customerId);
                      // APPOINTMENT_PHASES_v1 -- Phase-Child zeigt phaseLabel als Service-Name
                      // CAL_CARD_SERVICE_NAME_FIX_v1 -- Fallback auf apt.serviceNames (auch für inaktive Services!)
                      const svcs = apt.phaseParentId && apt.phaseLabel
                        ? apt.phaseLabel
                        : (apt.serviceIds||[]).map(sid => {
                            const s = services.find(x => x.id === sid);
                            if (s) return s.name;
                            // Fallback: aus apt.serviceNames an gleichem Index
                            const idx = (apt.serviceIds||[]).indexOf(sid);
                            return (apt.serviceNames||[])[idx];
                          }).filter(Boolean).join(", ");
                      // CAL_LUXURY_v5 -- "short" wenn <= 35 Min: kein Service-Line zeigen
                      const isShort = (apt.totalDuration || 30) <= 35;
                      // ONLINE_NOTE_CAL_v1 -- 💬 neben dem Namen, wenn der Termin eine Notiz hat (Volltext als Tooltip)
                      const _aptNote = aptNoteOf(apt);
                      const _noteMark = _aptNote ? <span title={_aptNote} style={{ flexShrink: 0, marginLeft: 4, fontSize: 10, lineHeight: 1 }}>💬</span> : null;
                      // BOOKING_PAY_ONLINE_v1 (Spec 3a) -- 💳 online bezahlt und noch nicht kassiert, ⏳ Online-Zahlung laeuft
                      // (Zahlung haengt am Gesamttermin; Phasen-Teile zeigen den Stand des Masters)
                      const _payApt = apt.phaseParentId ? (appointments.find(a => a.id === apt.phaseParentId) || apt) : apt;
                      const _payMark = _payApt.prepayStatus === "paid" && !_payApt.posReceiptNumber
                        ? <span data-pay-mark="paid" title={`Online bezahlt: ${fmtEur(_payApt.prepaidAmount)}`} style={{ flexShrink: 0, marginLeft: 4, fontSize: 10, lineHeight: 1 }}>💳</span>
                        : _payApt.prepayStatus === "pending"
                          ? <span data-pay-mark="pending" title="Online-Zahlung läuft" style={{ flexShrink: 0, marginLeft: 4, fontSize: 10, lineHeight: 1 }}>⏳</span>
                          : null;
                      // CAL_LUXURY_v4_DEBUG entfernt (war: const _dbg = ...)
                      // PHASE_BOOKING_v1 -- finde idle-Phasen (Einwirkzeit) fuer diesen Termin
                      // Wenn der Termin Service-Phasen mit busy_pct < 50 hat, zeige sie
                      // als gestreiften Overlay im Termin (visuell als "Stylist hier frei").
                      const idlePhases = (() => {
                        const out = [];
                        const seen = new Set();
                        (apt.serviceIds || []).forEach(sid => {
                          const svc = services.find(s => s.id === sid);
                          if (!svc || !Array.isArray(svc.phases)) return;
                          svc.phases.forEach(p => {
                            if (p.busy_pct >= 50) return;
                            const key = `${p.start_offset_min}-${p.duration_min}`;
                            if (seen.has(key)) return;
                            seen.add(key);
                            // Phase-Top relativ zum Termin-Anfang
                            const phStartPct = (p.start_offset_min / Math.max(apt.totalDuration, 1)) * 100;
                            const phHeightPct = (p.duration_min / Math.max(apt.totalDuration, 1)) * 100;
                            if (phStartPct + phHeightPct <= 100.5) {
                              // IDLE_FLAT_BLOCK_v1 -- Offset/Dauer mitgeben, damit das Auswahl-Fenster
                              // die echte Uhrzeit der Einwirkzeit anzeigen kann.
                              out.push({ topPct: phStartPct, heightPct: phHeightPct, label: p.label || "Einwirkzeit",
                                         offsetMin: p.start_offset_min, durMin: p.duration_min });
                            }
                          });
                        });
                        return out;
                      })();
                      return (
                        <div key={apt.id}
                             className={"shearly-apt-card" + (highlightedApptId === apt.id ? " shearly-apt-card-focus" : "") + (longPressActive === apt.id ? " shearly-apt-card-longpress" : "")}
                             data-appt-id={apt.id}
                             /* PHASE_RESIZE_CASCADE_v1 -- kurzer Hinweis: Ziehen wirkt auf den ganzen Termin */
                             title={apt.phaseParentId ? "Teil eines Phasen-Termins — Ziehen am unteren Rand verlängert den ganzen Termin (spätere Phasen rutschen mit)" : undefined}
                             /* CAL_MOBILE_PROTECT_v1 + CAL_TOUCH_LONGPRESS_v1 -- Drag (HTML5 native) nur auf
                                Desktop (>= 768 px). Auf Touch-Geräten wird ohnehin Touch-Drag genutzt (Long-Press). */
                             /* CAL_TOUCH_MOVE_v1 -- HTML5-Drag auf Android AUS (geht dort eh nicht per Finger + loest System-Kontextmenue aus); iPad/Desktop unveraendert */
                             draggable={apt.status !== "cancelled" && window.innerWidth >= 768 && !isAndroidTouch}
                             onDragStart={(e) => {
                               e.dataTransfer.setData("text/apt-id", String(apt.id));
                               // CAL_DAY_POLISH_v1 -- speichere offsetY (wo im Termin geklickt wurde)
                               // damit beim Drop die Position korrekt korrigiert wird
                               const _offY = e.nativeEvent.offsetY || 0;
                               e.dataTransfer.setData("text/offset-y", String(_offY));
                               // DRAG_TIME_HINT_v1 -- Ref fuer dragover (dataTransfer.getData waehrend dragover oft leer)
                               dragOffsetYRef.current = _offY;
                               e.dataTransfer.effectAllowed = "move";
                             }}
                             onDragEnd={() => {
                               // CAL_TOUCH_MOVE_v3 -- Android-Chrome feuert kein 'drop' per Finger -> beim dragend
                               // an der zuletzt angezeigten Position speichern. Desktop/iPad: 'drop' hat lastDragOverRef
                               // schon geleert -> kein Doppel-Speichern. onDragLeave leert es beim Verlassen aller Spalten.
                               const tgt = lastDragOverRef.current;
                               lastDragOverRef.current = null;
                               setDragHint(null);
                               if (tgt && tgt.empId != null) commitApptMove(apt.id, tgt.empId, tgt.time, formatDate(selectedDate));
                             }}
                             /* CAL_TOUCH_MOVE_v1 -- Touch-Drag (Android) + System-Kontextmenue unterdruecken */
                             onTouchStart={isAndroidTouch && apt.status !== "cancelled" ? (e) => startApptTouchDrag(e, apt) : undefined}
                             onContextMenu={(e) => e.preventDefault()}
                             /* EMPLOYEE_SORT_v2.4 -- Termin-Card: iPad-Aktionsmenü (Drucken/Umbenennen) blocken,
                                userSelect:none damit Long-Press nicht Text markiert. HTML5-Drag bleibt aktiv. */
                             style={{...getApptStyle(apt, emp, resizePreview && resizePreview.id === apt.id ? resizePreview.newDur : null), cursor: apt.status !== "cancelled" && window.innerWidth >= 768 ? "grab" : "pointer", WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none", WebkitTapHighlightColor: "transparent", opacity: touchDragId === apt.id ? 0.55 : 1, transition: "opacity 0.12s"}}
                             onClick={(e) => {
                               /* APT_RESIZE_v1.1 -- Click direkt nach Resize unterdruecken */
                               if (suppressNextClickRef.current) { e.preventDefault(); e.stopPropagation(); return; }
                               e.stopPropagation();
                               // APPOINTMENT_ASSISTANT_v1 -- Klick auf Assistant-Block
                               // APPT_ASSIST_REMOVE_v1 (Toby 18.09.2026: "direkt auf den Termin in der Assistenz
                               // klicken und dort auf Löschen drücken") -- eigenes Fenster mit "Assistenz löschen"
                               // statt direkt den Haupttermin zu öffnen; der Haupttermin bleibt von dort erreichbar.
                               if (apt.source === "assistant" && apt.phaseParentId) {
                                 setAssistantTarget({ block: apt, masterId: apt.phaseParentId, emp });
                                 return;
                               }
                               // IDLE_PHASE_CLICK_BOOK_v1 + IDLE_PHASE_BLOCK_v1 -- Klick auf Einwirkzeit-Card
                               // öffnet das Auswahl-Fenster: Termin buchen ODER Einwirkzeit blockieren/freigeben
                               if (apt.phaseParentId && (apt.phaseBusyPct === 0 || apt.phaseBusyPct === "0")) {
                                 setIdlePhaseChooser({ apt, emp, dateStr });
                                 return;
                               }
                               setSelectedApt(apt);
                             }}
                             title={`${cust?.name || apt.customerName || "Kunde"} — ${svcs}\n${apt.startTime} – ${getEndTime(apt.startTime, apt.totalDuration)}\n(Ziehen zum Verschieben · unten greifen zum Verlängern)`}>
                          {/* PHASE_BOOKING_v1 -- Einwirkzeit als gestreifter Overlay-Bereich.
                              IDLE_FLAT_BLOCK_v1 -- bei Terminen OHNE Phasen-Children ist die Flaeche
                              jetzt antippbar (vorher pointerEvents:none = reine Deko): Tipp oeffnet
                              das Auswahl-Fenster zum Sperren/Freigeben. Ist gesperrt, zeigt sie das
                              Schloss-Muster statt der "frei"-Streifen. */}
                          {idlePhases.map((ph, idx) => {
                            const idleLocked = !!apt.phaseKeepBusy;
                            return (
                            <div key={"ph-"+idx}
                                 title={idleLocked
                                   ? "Einwirkzeit gesperrt — Online-Buchung, Zoey und KI-Telefon sehen die Zeit als belegt (tippen zum Freigeben)"
                                   : (isKosmetikSalon() ? "Einwirkzeit — Mitarbeiter/in frei für anderen Termin (tippen zum Sperren)" : "Einwirkzeit — Stylist frei für anderen Termin (tippen zum Sperren)")}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setIdlePhaseChooser({ apt, emp, dateStr, flatPhase: ph });
                                 }}
                                 style={{
                                   position: "absolute",
                                   top: `${ph.topPct}%`, height: `${ph.heightPct}%`,
                                   left: 0, right: 0, zIndex: 2,
                                   background: idleLocked
                                     ? "repeating-linear-gradient(45deg, rgba(220,38,38,0.30) 0 5px, rgba(220,38,38,0.12) 5px 10px)"
                                     : "repeating-linear-gradient(45deg, rgba(255,255,255,0.45) 0 4px, rgba(255,255,255,0.18) 4px 8px)",
                                   borderTop: "1px dashed " + (idleLocked ? "rgba(220,38,38,0.75)" : "rgba(255,255,255,0.55)"),
                                   borderBottom: "1px dashed " + (idleLocked ? "rgba(220,38,38,0.75)" : "rgba(255,255,255,0.55)"),
                                   cursor: "pointer",
                                 }} />
                            );
                          })}
                          {/* APT_BADGES_v1 -- Status-Badges oben rechts (Online/KI, Neukunde, No-Show-Risiko) */}
                          {apt.source !== "blocker" && (() => {
                            const badges = getApptBadges(apt, cust);
                            if (!badges.length) return null;
                            return (
                              <div style={{ position: "absolute", top: 2, right: 4, display: "flex", gap: 2 }}>
                                {badges.map(b => (
                                  <span key={b.key} title={b.title} style={{ fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 6, background: b.color, color: "white", lineHeight: 1.2 }}>{b.label}</span>
                                ))}
                              </div>
                            );
                          })()}
                          {/* CAL_LUXURY_v4_DEBUG entfernt — kurze cards brauchen Platz */}
                          {/* BLOCKER_VISUAL_v1 -- Pausen ohne "Termin"-Look (kein Service, keine Resize-Handle, keine Uhrzeit-Card)
                              VACATION_AUTO_SYNC_v1 -- vacation_auto auch als Blocker
                              APPOINTMENT_PHASES_v1 -- Idle-Phase = transparente Karte mit Hinweis "frei" */}
                          {apt.source === "assistant" ? (
                            // APPOINTMENT_ASSISTANT_v1 -- Assistenz-Block bei Helfer-Mitarbeiter
                            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", lineHeight: 1.2 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {apt.phaseLabel || "🤝 Assistenz"}
                              </div>
                              <div style={{ fontSize: 9, opacity: 0.7 }}>
                                {apt.startTime}–{getEndTime(apt.startTime, apt.totalDuration)}
                              </div>
                            </div>
                          ) : (apt.source === "blocker" || apt.source === "vacation_auto") ? (
                            // BLOCKER_VISUAL_v2 -- "Quiet Block": Icon + Label zentriert, semibold, klar lesbar
                            apt.source === "vacation_auto" ? (
                              <>
                                {seasonOnSelected && isSeasonAbsence(apt) && <SeasonAbsenceMark season={season} />}
                                <span style={{ fontSize: 14, opacity: 0.55 }}>🏖</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {(apt.notes || "Urlaub").replace(/\s*\(vac#\d+\)/, "")}
                                </span>
                              </>
                            ) : (
                              <>
                                {/* SEASONAL_DECOR_v1: 🍻 nur bei Urlaub/Feierabend/früher gehen (isSeasonAbsence), kein Zusatztext */}
                                {seasonOnSelected && isSeasonAbsence(apt) && <SeasonAbsenceMark season={season} />}
                                <span style={{ fontSize: 14, opacity: 0.55 }}>{blockerKind(apt.notes).icon /* PAUSE_INTERN_SPLIT_v1 -- Symbol je Typ */}</span>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {apt.notes || "Pause"}
                                </span>
                              </>
                            )
                          ) : (apt.phaseParentId && (apt.phaseBusyPct === 0 || apt.phaseBusyPct === "0")) ? (
                            // IDLE_PHASE_BLOCK_v1 -- blockierte Einwirkzeit: Schloss + "blockiert" statt "frei"
                            <>
                              <div style={{ fontSize: 10, fontWeight: 600, color: apt.phaseKeepBusy ? "#DC2626" : COLORS.textLight, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {/* IDLE_FREE_LABEL_v1 (Toby 29.07.) -- steht im Einwirk-Fenster bereits ein anderer
                                    Termin, ist der Stylist NICHT mehr frei -> „belegt" statt „Pia frei" */}
                                {(() => {
                                  const _first = emp?.name?.split(" ")[0] || "Stylist";
                                  if (apt.phaseKeepBusy) return `🔒 ${apt.phaseLabel || "Einwirkzeit"} · blockiert`;
                                  const _s0 = timeToMinutes(apt.startTime), _e0 = _s0 + (apt.totalDuration || 0);
                                  const _occupied = empAppts.some(o =>
                                    o.id !== apt.id && o.phaseParentId !== apt.phaseParentId &&
                                    o.status !== "cancelled" &&
                                    o.source !== "blocker" && o.source !== "vacation_auto" &&
                                    !(o.phaseParentId && (o.phaseBusyPct === 0 || o.phaseBusyPct === "0") && !o.phaseKeepBusy) &&
                                    timeToMinutes(o.startTime) < _e0 && _s0 < timeToMinutes(o.startTime) + (o.totalDuration || 0));
                                  return `⏳ ${apt.phaseLabel || "Einwirkzeit"} · ${_first} ${_occupied ? "belegt" : "frei"}`;
                                })()}
                              </div>
                              <div style={{ fontSize: 9, opacity: 0.7, marginTop: 1 }}>
                                {cust?.name || apt.customerName || "Kunde"} · {apt.startTime}-{getEndTime(apt.startTime, apt.totalDuration)}
                              </div>
                            </>
                          ) : apt.phaseParentId ? (
                            // APPOINTMENT_PHASES_v1 -- Busy-Phase Card: adaptive Layout
                            // CAL_CARD_ADAPTIVE_v1: bei breiter Card (>= 260px) inline, bei kurzer Card (< 30min) nur 1 Zeile
                            (() => {
                              const cardWidthApprox = (colWidth || 240) * (apt._totalCols || 1 ? 1/(apt._totalCols||1) : 1);
                              const isVeryShort = (apt.totalDuration || 30) < 30;
                              const isWideEnough = cardWidthApprox >= 260 && (apt._totalCols || 1) === 1;
                              if (isVeryShort) {
                                // Eine Zeile: Kunde · Phase-Label (kompakt, Uhrzeit weg)
                                return (
                                  <div style={{ fontSize: 10.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                                    <span style={{ fontWeight: 700 }}>{cust?.name || apt.customerName || "Kunde"}</span>
                                    {_noteMark}{_payMark}
                                    <span style={{ opacity: 0.75, marginLeft: 4 }}>· {apt.phaseLabel || "Phase"}</span>
                                  </div>
                                );
                              }
                              if (isWideEnough) {
                                // Breite Card: Name links, Phase-Label rechts
                                return (
                                  <>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, overflow: "hidden", lineHeight: 1.2 }}>
                                      <span style={{ fontWeight: 700, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cust?.name || apt.customerName || "Kunde"}</span>
                                      {_noteMark}{_payMark}
                                      <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{apt.phaseLabel || "Phase"}</span>
                                    </div>
                                    <div style={{ fontSize: 9, opacity: 0.65 }}>
                                      {apt.startTime}–{getEndTime(apt.startTime, apt.totalDuration)}
                                    </div>
                                  </>
                                );
                              }
                              // Standard: untereinander
                              return (
                                <>
                                  <div style={{ fontWeight: 700, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2, display: "flex", alignItems: "center", minWidth: 0 }}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{cust?.name || apt.customerName || "Kunde"}</span>{_noteMark}{_payMark}</div>
                                  <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.95, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>{apt.phaseLabel || "Phase"}</div>
                                  <div style={{ fontSize: 9, opacity: 0.65 }}>
                                    {apt.startTime}–{getEndTime(apt.startTime, apt.totalDuration)}
                                  </div>
                                </>
                              );
                            })()
                          ) : (
                            <>
                            {/* CAL_CARD_UNIFIED_v1 -- "Name · Service" einheitlich in einer Zeile, Uhrzeit untendrunter.
                                Vorher: bei breiten Cards rechts-bündig (space-between) — sah unruhig aus.
                                Jetzt überall: Vorname Nachname · Dienstleistung */}
                            {(() => {
                              if (isShort) {
                                return (
                                  <div style={{ fontSize: 10.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                                    <span style={{ fontWeight: 700 }}>{cust?.name || apt.customerName || "Kunde"}</span>
                                    {_noteMark}{_payMark}
                                    {svcs && <span style={{ opacity: 0.75, marginLeft: 4 }}>· {svcs}</span>}
                                  </div>
                                );
                              }
                              // CAL_TILE_SERVICES_v1 (17.09.2026, Michi/Christiane: WSF + Wimpern gebucht, Kachel zeigte nur WSF,
                              // weil Name + alle Leistungen in EINER nowrap-Zeile mit "…" standen -> zweite Leistung nie sichtbar,
                              // Termin wurde für falsch gehalten und Wimpern doppelt nachgetragen).
                              // Jetzt: Name eigene Zeile (+N-Kennzeichen bei mehreren Leistungen, wird nie abgeschnitten),
                              // Leistungen darunter mit Umbruch, so viele Zeilen wie die Kachelhöhe hergibt (45 Min = 1, 60 = 2, 75 = 3).
                              const _svcCount = apt.phaseParentId && apt.phaseLabel ? 1 : (apt.serviceIds || []).length;
                              const _tileDur = resizePreview && resizePreview.id === apt.id ? resizePreview.newDur : (apt.totalDuration || 30);
                              const _tilePx = (_tileDur / 60) * 64;            // wie getApptStyle
                              const _svcLines = Math.max(1, Math.floor((_tilePx - 4 - 14 - 12) / 13.2)); // Padding, Namenszeile, Uhrzeitzeile; 11 px × 1,2
                              return (
                                <>
                                  <div style={{ display: "flex", alignItems: "center", fontSize: 11, lineHeight: 1.25, minWidth: 0 }}>
                                    <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{cust?.name || apt.customerName || "Kunde"}</span>
                                    {_noteMark}{_payMark}
                                    {_svcCount > 1 && (
                                      <span title={`${_svcCount} Leistungen: ${svcs}`}
                                            style={{ flexShrink: 0, marginLeft: 4, fontSize: 9, fontWeight: 700, lineHeight: 1.3, padding: "0 4px", borderRadius: 6, background: "rgba(128,128,128,0.22)", border: "1px solid rgba(128,128,128,0.65)" }}>
                                        +{_svcCount - 1}
                                      </span>
                                    )}
                                  </div>
                                  {svcs && (
                                    <div style={{ fontSize: 11, fontWeight: 500, opacity: 0.9, lineHeight: 1.2, whiteSpace: "normal", overflow: "hidden",
                                                  display: "-webkit-box", WebkitLineClamp: _svcLines, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}>
                                      {svcs}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 10, opacity: 0.7 }}>
                                    {apt.startTime} – {getEndTime(apt.startTime,
                                      resizePreview && resizePreview.id === apt.id ? resizePreview.newDur : apt.totalDuration)}
                                    {resizePreview && resizePreview.id === apt.id && (
                                      <span style={{ marginLeft: 6, fontWeight: 700, color: COLORS.primary }}>· {resizePreview.newDur} Min</span>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                              {/* APT_RESIZE_v1 -- Resize-Handle am unteren Rand
                                  CAL_TOUCH_LONGPRESS_v1: Phone (<480px) komplett blocken,
                                  Tablet/iPad (>=480) erlaubt Resize aber MIT 1-Sek-Long-Press,
                                  Desktop wie immer sofort.
                                  PHASE_RESIZE_CASCADE_v1 (Toby 21.07., ersetzt PHASE_RESIZE_LOCK_v1):
                                  Phasen-Blöcke sind wieder ziehbar — der Server verlängert dabei den
                                  GANZEN Termin (spätere Phasen rutschen, Master-Dauer wächst mit). */}
                              {apt.status !== "cancelled" && window.innerWidth >= 480 && (
                                <div
                                  className="shearly-apt-resize-handle"
                                  /* APPT_TAP_HANDLE_v1 -- nach einem Touch schickt der Browser ein nachgemachtes mousedown/mouseup;
                                     das wuerde sonst sofort einen Maus-Resize starten und den Klick unterdruecken */
                                  onMouseDown={(e) => { if (Date.now() - lastHandleTouchRef.current < 800) return; startResize(e, apt); }}
                                  onTouchStart={(e) => { lastHandleTouchRef.current = Date.now(); startResizeWithGuard(e, apt); }}
                                  /* APT_RESIZE_ANDROID_FIX_v1 -- wie EMP_SORT_ANDROID_FIX: Android-Chrome zeigte beim
                                     Long-Press das System-Kontextmenu statt Resize zu starten -> Geste abgebrochen.
                                     preventDefault + Callout/Select aus. iPad/Desktop unveraendert. */
                                  onContextMenu={(e) => e.preventDefault()}
                                  /* APPT_TAP_HANDLE_v1 (Toby 22.09.2026, Galaxy Tab bei Sesto: kurze Termine liessen sich per
                                     Touch oft nicht oeffnen) -- kurzer Tipp auf den Verlaengern-Streifen oeffnet jetzt den Termin
                                     (Klick laeuft zur Karte durch). Nach echtem Ziehen/Maus-Resize bleibt er unterdrueckt. */
                                  onClick={(e) => { if (suppressNextClickRef.current) { e.preventDefault(); e.stopPropagation(); } }}
                                  draggable={false}
                                  onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                  title="Termin verlängern (ziehen)"
                                  style={{
                                    position: "absolute", left: 6, right: 6, bottom: 0,
                                    /* APPT_TAP_HANDLE_v1 -- Touch + kurze Karte (< 40 px): Streifen 6 statt 12 px */
                                    height: (isTouchDevice && ((apt.totalDuration || 30) / 60) * 64 < 40) ? 6 : 12,
                                    cursor: "ns-resize", zIndex: 4, touchAction: "none",
                                    WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none",
                                    borderRadius: "0 0 6px 6px",
                                  }} />
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
        </>
        );
      })()}

      {/* DRAG_TIME_HINT_v1 -- Floating-Tooltip mit Ziel-Uhrzeit beim Verschieben */}
      {dragHint && (
        <div style={{
          position: "fixed",
          left: dragHint.x + 16, top: dragHint.y - 12,
          background: COLORS.primary, color: "white",
          padding: "6px 12px", borderRadius: 8,
          fontSize: 13, fontWeight: 700,
          boxShadow: "0 4px 12px rgba(124,58,237,0.4)",
          pointerEvents: "none",
          zIndex: 99999,
          fontVariantNumeric: "tabular-nums",
        }}>
          ⏰ {dragHint.time}
        </div>
      )}

      {selectedApt && (() => {
        const apt = selectedApt;
        const cust = customers.find(c => c.id === apt.customerId);
        // PHASE_MASTER_SYNC_v1 -- eine Phase zeigt Leistungen + Preis des Gesamttermins (vorher „–“ / „0 €“)
        const phaseMaster = isPhasePart(apt) ? appointments.find(a => Number(a.id) === Number(apt.phaseParentId)) : null;
        const svcSrc = phaseMaster || apt;
        const svcs = svcSrc.serviceIds ? (svcSrc.serviceIds||[]).map(sid => services.find(s => s.id === sid)?.name).filter(Boolean).join(", ") : "";
        const emp = employees.find(e => e.id === apt.employeeId);
        // BLOCKER_DETAIL_v1 -- Blocker (Krank/Urlaub/Pause...) zeigt Typ statt "Kunde: Unbekannt"
        const isBlk = apt.source === "blocker" || apt.source === "vacation_auto";
        const blkLabel = (apt.notes || (apt.source === "vacation_auto" ? "Urlaub" : "Eintrag")).replace(/\s*\(vac#\d+\)/, "").replace(/\s*\[Serie:[^\]]*\]/, "");
        // CAL_SERVICE_CHANGE_PRICE_v1 -- kassierter Termin: Leistungen nur noch an der Kasse ändern (Server lehnt es ab)
        // KASSIEREN_LINK_v1 -- Zahlung hängt am Gesamttermin: eine Phase zeigt und kassiert den Stand des Masters
        const payApt = phaseMaster || apt;
        const aptPaid = payApt.paymentStatus === "paid" || !!payApt.posReceiptNumber;
        const isEditing = editAptData !== null;
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setSelectedApt(null); setEditAptData(null); setAptNoteEdit(null); setAptOnceEdit(null); }}>
            <div style={{ background: COLORS.card, borderRadius: 12, padding: 24, minWidth: 340, maxWidth: 440, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: "0 0 16px", color: COLORS.text, fontSize: 18 }}>{isEditing ? "Termin bearbeiten" : "Termindetails"}</h3>
              {!isEditing ? (
                <div style={{ fontSize: 14, lineHeight: 2, color: COLORS.text }}>
                  <div><strong>{isBlk ? "Eintrag" : "Kunde"}:</strong> {isBlk ? blkLabel : (cust?.name || apt.customerName || "Unbekannt")}</div>
                  {/* NO_SHOW_MARK_v1 -- rote Markierung: Kunde ist schon einmal nicht erschienen, ohne abzusagen */}
                  {!isBlk && cust && (cust.noShows || 0) >= 1 ? (
                    <div data-noshow-mark="1" style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.4)", borderRadius: 6, padding: "4px 10px", margin: "4px 0", fontSize: 13, lineHeight: 1.4, color: "#DC2626", fontWeight: 700 }}>
                      🚫 {cust.noShows}× nicht erschienen, ohne abzusagen{cust.lastNoShow ? " · zuletzt am " + String(cust.lastNoShow).slice(0, 10).split("-").reverse().join(".") : ""}
                    </div>
                  ) : null}
                  {/* APT_PHONE_v1 -- Telefonnummer direkt im Termin-Fenster, anklickbar zum Anrufen (kein Wechsel ins CRM noetig) */}
                  {cust?.phone ? <div><strong>Telefon:</strong> <a href={`tel:${String(cust.phone).replace(/\s/g,"")}`} style={{ color: COLORS.primary, textDecoration: "none", fontWeight: 600 }}>{cust.phone}</a></div> : null}
                  {/* CUST_NOTE_BOOKING_v1 + APT_NOTE_EDIT_v1 -- dauerhafte Kunden-Notiz anzeigen UND direkt bearbeiten (synchron mit CRM/BookingModal) */}
                  {cust ? (
                    aptNoteEdit === null ? (
                      <div style={{ background: `${COLORS.warning}14`, border: `1px solid ${COLORS.warning}55`, borderRadius: 6, padding: "6px 10px", margin: "4px 0", fontSize: 13, lineHeight: 1.4, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div><strong>📌 Dauerhaft:</strong> {cust.notes ? cust.notes : <span style={{ color: COLORS.textLight, fontStyle: "italic" }}>keine</span>}</div>
                        <button onClick={() => setAptNoteEdit(cust.notes || "")} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.primary, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", padding: 0 }}>✎ {cust.notes ? "ändern" : "hinzufügen"}</button>
                      </div>
                    ) : (
                      <div style={{ background: `${COLORS.warning}14`, border: `1px solid ${COLORS.warning}55`, borderRadius: 6, padding: "8px 10px", margin: "4px 0" }}>
                        <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13 }}>📌 Dauerhafte Notiz zum Kunden <span style={{ fontWeight: 400, color: COLORS.textLight }}>(erscheint bei jedem Termin)</span></label>
                        <textarea value={aptNoteEdit} onChange={e => setAptNoteEdit(e.target.value)} rows={2} placeholder="z.B. kommt immer 10 Min zu spät" style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                        <div style={{ fontSize: 10.5, color: COLORS.textLight, marginTop: 4, lineHeight: 1.35 }}>🔒 Gesundheitsangaben (z. B. Allergien) nur mit ausdrücklicher Einwilligung des Kunden eintragen. {/* AI_HEALTH_HINT_v1 / Compliance B10 */}</div>
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                          <button disabled={aptNoteSaving} onClick={async () => {
                            setAptNoteSaving(true);
                            try {
                              const res = await api.updateCustomer(cust.id, { notes: aptNoteEdit });
                              if (res && res.ok) {
                                if (setCustomers) setCustomers(prev => prev.map(c => c.id === cust.id ? { ...c, notes: aptNoteEdit } : c));
                                setAptNoteEdit(null);
                              } else { alert("Notiz konnte nicht gespeichert werden."); }
                            } catch (e) { alert("Notiz konnte nicht gespeichert werden."); }
                            finally { setAptNoteSaving(false); }
                          }} style={{ padding: "6px 14px", background: COLORS.primary, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>{aptNoteSaving ? "Speichern…" : "Speichern"}</button>
                          <button disabled={aptNoteSaving} onClick={() => setAptNoteEdit(null)} style={{ padding: "6px 14px", background: COLORS.border, color: COLORS.text, border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Abbrechen</button>
                        </div>
                      </div>
                    )
                  ) : null}
                  {/* ONLINE_NOTE_CAL_v1 (Toby 06.10.2026) -- Termin-Notiz (Anmerkung aus der Online-Buchung, Haarlänge,
                      Import-Notiz) war im Kalender nirgends zu sehen. Nur lesend; die Kartei-Notiz oben bleibt wie sie ist. */}
                  {/* APT_NOTE_ONCE_v1 -- jetzt auch eintragbar/änderbar (vorher nur lesend); bei Phasen für den ganzen Termin */}
                  {!isBlk ? (aptOnceEdit === null ? (
                    <div style={{ background: `${COLORS.primary}14`, border: `1px solid ${COLORS.primary}55`, borderRadius: 6, padding: "6px 10px", margin: "4px 0", fontSize: 13, lineHeight: 1.4, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        <strong>💬 Nur dieser Termin{(((isPhasePart(apt) && appointments.find(a => Number(a.id) === Number(apt.phaseParentId))) || apt).source === "online" && aptNoteOf(apt)) ? " (vom Kunden online)" : ""}:</strong>{" "}
                        {aptNoteOf(apt) ? aptNoteOf(apt) : <span style={{ color: COLORS.textLight, fontStyle: "italic" }}>keine</span>}
                      </div>
                      <button onClick={() => setAptOnceEdit(aptNoteOf(apt))} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.primary, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", padding: 0 }}>✎ {aptNoteOf(apt) ? "ändern" : "hinzufügen"}</button>
                    </div>
                  ) : (
                    <div style={{ background: `${COLORS.primary}14`, border: `1px solid ${COLORS.primary}55`, borderRadius: 6, padding: "8px 10px", margin: "4px 0" }}>
                      <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13 }}>💬 Notiz nur für diesen Termin <span style={{ fontWeight: 400, color: COLORS.textLight }}>(erscheint beim nächsten Termin nicht)</span></label>
                      <textarea value={aptOnceEdit} onChange={e => setAptOnceEdit(e.target.value)} rows={2} maxLength={1000} placeholder="z.B. Sohn kommt mit und wird auch geschnitten" style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                        <button disabled={aptOnceSaving} onClick={async () => {
                          setAptOnceSaving(true);
                          try { await saveAptOnceNote(apt, aptOnceEdit); setAptOnceEdit(null); }
                          catch (e) { alert("Notiz konnte nicht gespeichert werden."); }
                          finally { setAptOnceSaving(false); }
                        }} style={{ padding: "6px 14px", background: COLORS.primary, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>{aptOnceSaving ? "Speichern…" : "Speichern"}</button>
                        <button disabled={aptOnceSaving} onClick={() => setAptOnceEdit(null)} style={{ padding: "6px 14px", background: COLORS.border, color: COLORS.text, border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Abbrechen</button>
                      </div>
                    </div>
                  )) : null}
                  <div><strong>Mitarbeiter:</strong> {emp?.name || "–"}</div>
                  {/* APPT_ASSIST_REMOVE_v1 -- Assistenz war bisher nur im Bearbeiten-Fenster sichtbar.
                      Jetzt steht sie in den Termindetails und laesst sich dort in einem Klick entfernen,
                      ohne den Termin anzufassen. */}
                  {apt.assistantEmployeeId ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div><strong>Assistenz:</strong> 🤝 {employees.find(e => Number(e.id) === Number(apt.assistantEmployeeId))?.name || "Mitarbeiter"}{apt.assistantDurationMin ? ` · ${apt.assistantDurationMin} Min.` : ""}</div>
                      <button disabled={assistantBusy} onClick={async () => {
                        if (!window.confirm("Assistenz entfernen?\n\nDer Termin selbst bleibt bestehen — nur der Assistenz-Eintrag beim zweiten Mitarbeiter wird gelöscht.")) return;
                        setAssistantBusy(true);
                        await removeAssistant(apt.id);
                        setAssistantBusy(false);
                      }} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap", padding: 0 }}>
                        {assistantBusy ? "…" : "✕ entfernen"}
                      </button>
                    </div>
                  ) : null}
                  <div><strong>Dienstleistungen:</strong> {svcs || "–"}</div>
                  <div><strong>Datum:</strong> {formatDateShort(apt.date)}</div>
                  <div><strong>Uhrzeit:</strong> {apt.startTime} – {getEndTime(apt.startTime, apt.totalDuration)}</div>
                  <div><strong>Dauer:</strong> {apt.totalDuration} Min.</div>
                  {/* PHASE_MASTER_SYNC_v1 -- Ablauf der Phasen + Weg zum ganzen Termin */}
                  {(isPhasePart(apt) || apt.isPhaseMaster || apt.is_phase_master) && (() => {
                    const mid = isPhasePart(apt) ? apt.phaseParentId : apt.id;
                    const grp = phaseGroupOf(mid);
                    if (!grp.length) return null;
                    return (
                      <div style={{ margin: "8px 0 4px", padding: "10px 12px", borderRadius: 10, background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, lineHeight: 1.45 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: COLORS.textLight, marginBottom: 6 }}>
                          {isPhasePart(apt) ? `Teil eines Termins mit ${grp.length} Phasen` : "Ablauf"}
                        </div>
                        {grp.map(p => {
                          const idle = p.phaseBusyPct === 0 || p.phaseBusyPct === "0";
                          const pEmp = employees.find(e => e.id === p.employeeId);
                          const here = Number(p.id) === Number(apt.id);
                          return (
                            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, fontWeight: here ? 700 : 500, color: idle ? COLORS.textLight : COLORS.text }}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{idle ? "⏳ " : ""}{p.phaseLabel || "Phase"}{pEmp && Number(pEmp.id) !== Number(apt.employeeId) ? ` · ${pEmp.name.split(" ")[0]}` : ""}</span>
                              <span style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{p.startTime}–{getEndTime(p.startTime, p.totalDuration)}</span>
                            </div>
                          );
                        })}
                        {isPhasePart(apt) && (
                          <button onClick={() => openWholeAppt(mid)}
                            style={{ marginTop: 10, width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.primary}`, background: "transparent", color: COLORS.primary, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                            Ganzen Termin öffnen
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  <div><strong>Preis:</strong> {(phaseMaster || apt).totalPrice}€{phaseMaster ? <span style={{ color: COLORS.textLight, fontSize: 12 }}> · ganzer Termin</span> : null}</div>
                  <div><strong>Status:</strong> <span style={{ color: apt.status === "cancelled" ? COLORS.danger : apt.status === "no_show" ? "#DC2626" : COLORS.success, fontWeight: apt.status === "no_show" ? 700 : undefined }}>{apt.status === "cancelled" ? "Storniert" : apt.status === "confirmed" ? "Bestätigt" : apt.status === "no_show" ? "Nicht erschienen (No-Show)" : apt.status}</span></div>
                  {/* CHECKOUT_v1 -- Zahlungsstatus */}
                  <div><strong>Zahlung:</strong> {payApt.paymentStatus === "paid"
                    ? <span style={{ color: COLORS.success }}>✓ Bezahlt {payApt.paymentMethod === "cash" ? "(Bar)" : payApt.paymentMethod === "card" ? "(Karte)" : payApt.paymentMethod === "qr" ? "(QR/SumUp)" : payApt.paymentMethod === "online" ? "(online)" : payApt.paymentMethod ? `(${payApt.paymentMethod})` : ""}{payApt.posReceiptNumber ? ` · Bon ${payApt.posReceiptNumber}` : ""}</span>
                    : payApt.paymentStatus === "partial" ? <span style={{ color: COLORS.warning }}>Teilweise bezahlt</span>
                    : payApt.paymentStatus === "refunded" ? <span style={{ color: COLORS.textLight }}>Erstattet</span>
                    : payApt.paymentStatus === "prepay_pending" ? <span style={{ color: COLORS.warning }}>{payApt.prepayKind === "optional" ? "Online-Zahlung läuft" : "Vorkasse ausstehend"}</span>
                    : <span style={{ color: COLORS.warning }}>Offen</span>}
                  </div>
                  {/* BOOKING_PREPAY_v1 -- Online-Vorkasse (SumUp) */}
                  {prepayLine(payApt) && <div><strong>{payApt.prepayKind === "optional" ? "Online-Zahlung" : "Vorkasse"}:</strong> {prepayLine(payApt)}</div>}
                </div>
              ) : (
                <div style={{ fontSize: 14, color: COLORS.text }}>
                  {/* CAL_BLOCKER_SUBJECT_v1 -- interner Termin: Art bleibt, Betreff änderbar (gilt nur für diesen Tag) */}
                  {apt.source === "blocker" && editAptData.blkSubject != null ? (
                  <div style={{ marginBottom: 12 }}>
                    {splitBlockerNotes(apt.notes).kind ? <div style={{ marginBottom: 6 }}><strong>Art:</strong> {blockerKind(apt.notes).icon} {splitBlockerNotes(apt.notes).kind}</div> : null}
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Betreff</label>
                    <input data-blk-subject="1" type="text" value={editAptData.blkSubject} maxLength={300}
                      placeholder={(BLOCKER_KINDS.find(k => k.type === splitBlockerNotes(apt.notes).kind) || {}).hint || "z. B. Grund, Ort, Name"}
                      onChange={e => setEditAptData({ ...editAptData, blkSubject: e.target.value })}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box", background: "var(--card)", color: COLORS.text }} />
                    {splitBlockerNotes(apt.notes).series ? <div style={{ marginTop: 4, fontSize: 11.5, color: COLORS.textLight }}>Teil einer Serie: die Änderung gilt nur für diesen Tag.</div> : null}
                  </div>
                  ) : (
                  <div style={{ marginBottom: 12 }}><strong>{isBlk ? "Eintrag" : "Kunde"}:</strong> {isBlk ? blkLabel : (cust?.name || apt.customerName || "Unbekannt")}</div>
                  )}
                  {/* APPT_SERVICE_EDIT_v1 -- Service änderbar im Edit-Modal
                      PHASE_MASTER_SYNC_v1 -- bei einer einzelnen Phase keine Leistungsauswahl mehr (wirkte nur
                      auf die Phase und zerlegte den Termin); Leistungen gehören zum ganzen Termin.
                      CAL_BLOCKER_SUBJECT_v1 -- interne Termine/Urlaub haben keine Leistungen: keine Auswahl. */}
                  {isBlk ? null : isPhasePart(apt) ? (
                  <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, fontSize: 13, lineHeight: 1.5 }}>
                    <div><strong>{apt.phaseLabel || "Phase"}</strong> <span style={{ color: COLORS.textLight }}>· Teil eines Termins mit {phaseGroupOf(apt.phaseParentId).length} Phasen</span></div>
                    <div style={{ color: COLORS.textLight, fontSize: 12, marginTop: 2 }}>Uhrzeit, Dauer und Mitarbeiter gelten für diese Phase. Leistungen, Preis und Verschieben des ganzen Ablaufs im Gesamttermin.</div>
                    <button type="button" onClick={() => openWholeAppt(apt.phaseParentId)}
                      style={{ marginTop: 8, padding: "7px 12px", borderRadius: 8, border: `1px solid ${COLORS.primary}`, background: "transparent", color: COLORS.primary, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                      Ganzen Termin öffnen
                    </button>
                  </div>
                  ) : aptPaid ? (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>{(apt.serviceIds || []).length > 1 ? "Dienstleistungen" : "Dienstleistung"}</label>
                    <div style={{ fontSize: 14 }}>{((apt.serviceNames && apt.serviceNames.length) ? apt.serviceNames : (apt.serviceIds || []).map(id => (services.find(s => s.id === id) || {}).name).filter(Boolean)).join(", ") || "—"}</div>
                    <div data-paid-lock="1" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: COLORS.text, padding: "6px 10px", borderRadius: 8, background: "rgba(245,158,11,0.12)", borderLeft: "2px solid #F59E0B" }}>
                      Bereits kassiert: Leistungen bitte an der Kasse ändern (Storno und neuer Bon), damit Kalender und Beleg zusammenpassen.
                    </div>
                  </div>
                  ) : (
                  <div style={{ marginBottom: 12 }}>
                    {/* APPT_EDIT_MULTI_SERVICE_v1 (17.09.2026, Michi: bei zwei gebuchten Leistungen war nur die erste
                        bearbeitbar) -- Liste aller Leistungen: jede Zeile einzeln änderbar, ✕ entfernt (mind. eine bleibt),
                        "+ Leistung hinzufügen". Server (service_ids) unverändert. Dauer: bei genau einer Leistung wie bisher
                        automatisch deren Standarddauer, bei mehreren bleibt die Dauer und ein Knopf bietet die Summe an.
                        Ersetzt den Warnhinweis aus CAL_TILE_SERVICES_v1. */}
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>{(editAptData.serviceIds || []).length > 1 ? "Dienstleistungen" : "Dienstleistung"}</label>
                    {(() => {
                      const ids = (editAptData.serviceIds || []).map(Number);
                      const activeSvcs = (services || []).filter(s => s.is_active !== 0 && s.is_active !== false);
                      const baseDur = (id) => { const sv = services.find(x => x.id === id); return sv ? Number(sv.baseDuration || sv.base_duration || 0) : 0; };
                      const applyIds = (next) => {
                        // APPT_EDIT_DUR_AUTO_v1 (Toby 17.09.2026: "wenn ich die Bartpflege wieder rauslösche, passt sich
                        // die Zeit auch wieder an") -- Dauer folgt IMMER der Summe der Standarddauern; die vorherige
                        // Dauer bleibt als "zurück" erreichbar, damit von Hand gesetzte Zeiten nicht verloren gehen.
                        const upd = { ...editAptData, serviceIds: next };
                        const sum = next.reduce((acc, id) => acc + baseDur(id), 0);
                        const cur = Number(editAptData.totalDuration) || 0;
                        if (sum > 0 && sum !== cur) {
                          upd.totalDuration = sum;
                          upd.durAuto = { prev: cur, to: sum };
                        }
                        setEditAptData(upd);
                      };
                      const selStyle = { flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box", background: "var(--card)", color: COLORS.text };
                      const options = (currentId) => {
                        const groups = {};
                        activeSvcs.forEach(sv => { const cat = sv.category || "Sonstiges"; (groups[cat] = groups[cat] || []).push(sv); });
                        // Leistung, die der Termin hat, aber inzwischen inaktiv ist: trotzdem anzeigen, sonst springt das Feld
                        const cur = currentId && !activeSvcs.some(sv => sv.id === currentId) ? services.find(sv => sv.id === currentId) : null;
                        return [
                          cur ? <option key={"cur" + cur.id} value={cur.id}>{cur.name} (inaktiv)</option> : null,
                          ...Object.keys(groups).sort().map(cat => (
                            <optgroup key={cat} label={cat}>
                              {groups[cat].map(sv => (
                                <option key={sv.id} value={sv.id} disabled={sv.id !== currentId && ids.includes(sv.id)}>
                                  {sv.name} ({sv.baseDuration || sv.base_duration} Min.)
                                </option>
                              ))}
                            </optgroup>
                          )),
                        ];
                      };
                      return (
                        <div>
                          {ids.length === 0 && (
                            <select value="" onChange={e => { const v = Number(e.target.value); if (v) applyIds([v]); }} style={{ ...selStyle, width: "100%" }}>
                              <option value="">— Service wählen —</option>
                              {options(null)}
                            </select>
                          )}
                          {ids.map((id, idx) => (
                            <div key={idx + "-" + id} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                              <select value={id} onChange={e => { const v = Number(e.target.value); if (!v) return; const next = ids.slice(); next[idx] = v; applyIds(next); }} style={selStyle}>
                                {options(id)}
                              </select>
                              {ids.length > 1 && (
                                <button type="button" title="Leistung entfernen" onClick={() => applyIds(ids.filter((_, i) => i !== idx))}
                                  style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, border: "1px solid " + COLORS.border, background: "var(--cardHover)", color: COLORS.danger, cursor: "pointer", fontSize: 15, fontWeight: 700 }}>✕</button>
                              )}
                            </div>
                          ))}
                          {ids.length > 0 && !apt.phaseParentId && (
                            <select value="" onChange={e => { const v = Number(e.target.value); if (v) applyIds([...ids, v]); }}
                              style={{ ...selStyle, width: "100%", color: COLORS.primary, borderStyle: "dashed" }}>
                              <option value="">+ Leistung hinzufügen</option>
                              {options(null)}
                            </select>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  )}
                  {/* APPT_EDIT_PRICE_v1 -- Preis im Edit-Modal sichtbar (read-only, kommt aus appointment_services-Snapshot)
                      CAL_SERVICE_CHANGE_PRICE_v1 -- neue Leistungen: der Server rechnet den Preis beim Speichern neu */}
                  {!isBlk && (
                  <div style={{ marginBottom: 12, padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, fontSize: 13 }}>
                    <span style={{ color: COLORS.textLight }}>💰 Aktueller Preis: </span>
                    <strong style={{ fontSize: 15, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{(phaseMaster || apt).totalPrice} €</strong>{phaseMaster ? <span style={{ fontSize: 11, color: COLORS.textLight }}> · ganzer Termin</span> : null}
                    {JSON.stringify((editAptData.serviceIds || []).map(Number).sort()) !== JSON.stringify((apt.serviceIds || []).map(Number).sort()) && (editAptData.serviceIds || []).length > 0
                      ? <div data-price-recalc="1" style={{ marginTop: 4, fontSize: 12, color: COLORS.text }}>Neue Leistungen: der Preis wird beim Speichern neu berechnet (Preis der Leistung bzw. Stufe der Mitarbeiterin).</div>
                      : <span style={{ fontSize: 11, color: COLORS.textLight, marginLeft: 8 }}>(zum Buchungszeitpunkt fixiert — bleibt bei Mitarbeiterwechsel gleich)</span>}
                  </div>
                  )}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Datum</label>
                    <input type="date" value={editAptData.date} onChange={e => setEditAptData({...editAptData, date: e.target.value})} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box" }} />
                    {/* PHASE_MASTER_SYNC_v1 -- vorher sagen, was beim Speichern mit den übrigen Phasen passiert */}
                    {isPhasePart(apt) && editAptData.date && editAptData.date !== apt.date && (
                      <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: COLORS.text, padding: "6px 10px", borderRadius: 8, background: `${COLORS.primary}12`, borderLeft: `2px solid ${COLORS.primary}` }}>
                        Neuer Tag: alle {phaseGroupOf(apt.phaseParentId).length} Phasen ziehen mit auf den {formatDateShort(editAptData.date)}, mit demselben Zeitversatz.
                      </div>
                    )}
                    {(apt.isPhaseMaster || apt.is_phase_master) && phaseGroupOf(apt.id).length > 0 && (
                      editAptData.date !== apt.date || editAptData.startTime !== apt.startTime || Number(editAptData.employeeId) !== Number(apt.employeeId) || Number(editAptData.totalDuration) !== Number(apt.totalDuration)
                    ) && (
                      <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.45, color: COLORS.text, padding: "6px 10px", borderRadius: 8, background: `${COLORS.primary}12`, borderLeft: `2px solid ${COLORS.primary}` }}>
                        Alle {phaseGroupOf(apt.id).length} Phasen ziehen mit. Eine geänderte Dauer verlängert oder verkürzt die letzte Phase.
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Uhrzeit</label>
                    <input type="time" value={editAptData.startTime} onChange={e => setEditAptData({...editAptData, startTime: e.target.value})} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box" }} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Dauer (Min.)</label>
                    <input type="number" value={editAptData.totalDuration} onChange={e => setEditAptData({...editAptData, totalDuration: Number(e.target.value), durAuto: null})} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box" }} />
                    {/* APPT_EDIT_DUR_AUTO_v1 -- Hinweis + ein Klick zurueck zur vorherigen (oft von Hand gesetzten) Dauer */}
                    {editAptData.durAuto && editAptData.durAuto.prev > 0 && (
                      <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textLight }}>
                        Dauer von {editAptData.durAuto.prev} auf {editAptData.durAuto.to} Min. angepasst ·{" "}
                        <button type="button" onClick={() => setEditAptData({ ...editAptData, totalDuration: editAptData.durAuto.prev, durAuto: null })}
                          style={{ background: "none", border: "none", padding: 0, color: COLORS.primary, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>zurück</button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Mitarbeiter</label>
                    <select value={editAptData.employeeId} onChange={e => setEditAptData({...editAptData, employeeId: Number(e.target.value)})} style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 14, boxSizing: "border-box" }}>
                      {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  {/* APPOINTMENT_ASSISTANT_v1 -- Assistenz (optional, blockt 2. Mitarbeiter für X Min ab Termin-Start); nicht bei internen Terminen */}
                  {!isBlk && (
                  <div style={{ marginBottom: 12, padding: 10, background: "var(--cardHover)", borderRadius: 8, border: `1px dashed ${COLORS.border}` }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                      🤝 Assistenz <span style={{ fontWeight: 400, color: COLORS.textLight, fontSize: 11 }}>(optional, 2. Mitarbeiter hilft)</span>
                    </label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <select
                        value={editAptData.assistantEmployeeId || ""}
                        onChange={e => setEditAptData({...editAptData, assistantEmployeeId: e.target.value ? Number(e.target.value) : null, assistantDurationMin: e.target.value && !editAptData.assistantDurationMin ? 30 : editAptData.assistantDurationMin})}
                        style={{ flex: 1, minWidth: 140, padding: "8px 10px", borderRadius: 6, border: "1px solid " + COLORS.border, fontSize: 13, background: "var(--card)", color: COLORS.text }}>
                        <option value="">— keine —</option>
                        {employees.filter(e => e.id !== Number(editAptData.employeeId)).map(e => (
                          <option key={e.id} value={e.id}>{e.name}</option>
                        ))}
                      </select>
                      {editAptData.assistantEmployeeId && (
                        <>
                          <span style={{ fontSize: 12, color: COLORS.textLight }}>für</span>
                          <input type="number" min="5" max="240" step="5"
                            value={editAptData.assistantDurationMin || 30}
                            onChange={e => setEditAptData({...editAptData, assistantDurationMin: Number(e.target.value) || 30})}
                            style={{ width: 70, padding: "8px 10px", borderRadius: 6, border: "1px solid " + COLORS.border, fontSize: 13, background: "var(--card)", color: COLORS.text }} />
                          <span style={{ fontSize: 12, color: COLORS.textLight }}>Min ab Termin-Start</span>
                        </>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                {!isEditing ? (
                  <>
                    {/* CHECKOUT_v1 -- Kassieren-Button: nur wenn nicht bezahlt + nicht storniert
                        BOOKING_PREPAY_v1 -- waehrend eine Online-Vorkasse aussteht ebenfalls nicht */}
                    {/* BOOKING_PAY_ONLINE_v1 -- online bezahlt, aber noch kein Bon: mit Shearly Kasse weiter kassierbar (Zusatzleistungen; die Kasse verrechnet den Online-Betrag) */}
                    {(payApt.paymentStatus !== "paid" || (payApt.paymentMethod === "online" && !payApt.posReceiptNumber && posShearly)) && apt.status !== "cancelled" && apt.status !== "no_show" && payApt.prepayStatus !== "pending" && ( /* NO_SHOW_MARK_v1: kein Kassieren bei Nicht erschienen */
                      <button style={{ flex: 1, minWidth: 90, padding: "10px", background: COLORS.success, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 }}
                        /* KASSE_CAL_BUTTON_v1 -- mit Shearly Kasse sofort in die Kasse (Termin vorbelegt), ohne Zwischenfenster;
                           das Fenster geht nur mit auf, damit ein Fehler dort sichtbar wird (bei Erfolg schliesst es sich) */
                        onClick={() => { setCheckoutApt(payApt); if (posShearly) openKasseForAppt(payApt); }}>💳 Kassieren</button>
                    )}
                    {/* BOOKING_PREPAY_v1 -- bezahlte Vorkasse erstatten (SumUp-Refund), z. B. nach Storno/Widerruf */}
                    {payApt.prepayStatus === "paid" && (
                      <button style={{ flex: 1, minWidth: 90, padding: "10px", background: "transparent", color: COLORS.text, border: `1.5px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => openRefund(payApt)}>↩ Vorkasse erstatten</button>
                    )}
                    <button style={{ flex: 1, minWidth: 90, padding: "10px", background: COLORS.primary, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => setEditAptData({ date: apt.date, startTime: apt.startTime, totalDuration: apt.totalDuration, employeeId: apt.employeeId, serviceIds: apt.serviceIds || [], assistantEmployeeId: apt.assistantEmployeeId || null, assistantDurationMin: apt.assistantDurationMin || null, blkSubject: apt.source === "blocker" ? splitBlockerNotes(apt.notes).subject : null /* CAL_BLOCKER_SUBJECT_v1 */ })}>Bearbeiten</button>
                    {/* NO_SHOW_MARK_v1 -- nur Inhaber + Salon-Login, erst ab Terminbeginn, nicht kassiert/storniert */}
                    {canEditDayBreak && !isBlk && cust && apt.status !== "cancelled" && apt.status !== "no_show" && apt.paymentStatus !== "paid" && aptStarted(apt) && (
                      <button data-noshow-btn="1" disabled={noShowBusy} onClick={() => markNoShow(apt, false)}
                        style={{ flex: 1, minWidth: 90, padding: "10px", background: "transparent", color: "#DC2626", border: "1.5px solid #DC2626", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>🚫 Nicht erschienen</button>
                    )}
                    {canEditDayBreak && !isBlk && apt.status === "no_show" && (
                      <button data-noshow-undo="1" disabled={noShowBusy} onClick={() => markNoShow(apt, true)}
                        style={{ flex: 1, minWidth: 90, padding: "10px", background: "transparent", color: COLORS.text, border: `1.5px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>↩ Doch erschienen</button>
                    )}
                    {/* CANCEL_REASON_v1 -- oeffnet CancelDialog mit Grund-Auswahl */}
                    {apt.status !== "cancelled" && <button style={{ flex: 1, minWidth: 90, padding: "10px", background: "#f59e0b", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => setCancelTarget(apt)}>Stornieren</button>}
                    {/* APPT_DELETE_OUT_v1 -- "Löschen"-Button entfernt (DSGVO/Buchhaltung: Termine müssen storniert werden, nicht hart gelöscht). Backend-DELETE bleibt für Admin-Bereinigung. */}
                    <button style={{ flex: 1, minWidth: 90, padding: "10px", background: COLORS.border, color: COLORS.text, border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => { setSelectedApt(null); setAptNoteEdit(null); setAptOnceEdit(null); }}>Schließen</button>
                  </>
                ) : (
                  <>
                    <button style={{ flex: 1, padding: "10px", background: COLORS.success, color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={async () => {
                      try {
                        const payload = {
                          date: editAptData.date,
                          start_time: editAptData.startTime,
                          total_duration: editAptData.totalDuration,
                          // APPOINTMENT_ASSISTANT_v1
                          assistant_employee_id: editAptData.assistantEmployeeId || null,
                          assistant_duration_min: editAptData.assistantEmployeeId ? (editAptData.assistantDurationMin || 30) : null,
                        };
                        // SALON_LOGIN_MOVE_v1 (Toby 07.10.2026) -- Mitarbeiter nur senden, wenn er sich ändert
                        if (Number(editAptData.employeeId) !== Number(apt.employeeId)) payload.employee_id = editAptData.employeeId;
                        // APPT_SERVICE_EDIT_v1 -- nur senden wenn sich die Services geändert haben
                        const newIds = (editAptData.serviceIds || []).map(Number).sort();
                        const oldIds = (apt.serviceIds || []).map(Number).sort();
                        if (JSON.stringify(newIds) !== JSON.stringify(oldIds) && newIds.length > 0) {
                          payload.service_ids = newIds;
                        }
                        // CAL_BLOCKER_SUBJECT_v1 -- geänderter Betreff eines internen Termins (Art + Serien-Vermerk bleiben)
                        let newNotes = null;
                        if (apt.source === "blocker" && editAptData.blkSubject != null) {
                          const parts = splitBlockerNotes(apt.notes);
                          const subj = String(editAptData.blkSubject).trim();
                          if (subj !== String(parts.subject || "").trim()) {
                            if (parts.kind === "Reserviert für Kundin" && !subj) { alert("Bitte den Namen der Kundin eintragen"); return; }
                            newNotes = joinBlockerNotes({ ...parts, subject: subj });
                            if (!newNotes.trim()) { alert("Bitte einen Betreff eintragen"); return; }
                            payload.notes = newNotes;
                          }
                        }
                        let res = await api.updateAppointment(apt.id, payload);
                        // MANUAL_OVERLAP_OK_v1 -- Konflikt beim Bearbeiten: nach Bestätigung bewusst überlappen
                        if (res && res.status === 409 && res.data && res.data.overlap_confirmable) {
                          if (window.confirm((res.data.error || "Der Mitarbeiter hat in diesem Zeitraum bereits einen Termin.") + "\n\nTrotzdem speichern? Die Termine überschneiden sich dann bewusst.")) {
                            res = await api.updateAppointment(apt.id, { ...payload, allow_overlap: true });
                          }
                        }
                        if (res.ok) {
                          // MANUAL_SHIFT_HINT_v1 -- Änderung außerhalb der Schicht geht durch, nur Hinweis anzeigen
                          if (res.data && res.data.shift_hint) alert(res.data.shift_hint);
                          const updated = {...apt,
                            date: editAptData.date, startTime: editAptData.startTime,
                            totalDuration: editAptData.totalDuration, employeeId: editAptData.employeeId,
                            serviceIds: newIds.length > 0 ? newIds : apt.serviceIds,
                            assistantEmployeeId: editAptData.assistantEmployeeId || null,
                            assistantDurationMin: editAptData.assistantDurationMin || null,
                          };
                          // CAL_BLOCKER_SUBJECT_v1 / CAL_SERVICE_CHANGE_PRICE_v1 -- neuer Betreff bzw. neu berechneter Preis sofort sichtbar
                          if (newNotes != null) updated.notes = newNotes;
                          if (payload.service_ids) {
                            updated.serviceNames = newIds.map(id => (services.find(s => s.id === id) || {}).name).filter(Boolean);
                            if (res.data && res.data.total_price != null) updated.totalPrice = Number(res.data.total_price);
                          }
                          setAppointments(prev => prev.map(a => a.id === apt.id ? updated : a));
                          setSelectedApt(updated);
                          setEditAptData(null);
                          // Bei Service-Change ODER Assistant-Change: appointments neu fetchen (wegen Phase-Children / Assistant-Block)
                          try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch(_) {}
                        } else {
                          alert((res.data && res.data.error) || "Fehler beim Speichern.");
                        }
                      } catch(e) { alert("Fehler: " + e.message); }
                    }}>Speichern</button>
                    <button style={{ flex: 1, padding: "10px", background: COLORS.border, color: COLORS.text, border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => setEditAptData(null)}>Abbrechen</button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* BOOKING_PREPAY_v1 -- Vorkasse erstatten (SumUp-Refund ueber die Transaktion des Checkouts) */}
      {refundApt && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
             onClick={() => { if (!refundBusy) setRefundApt(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: COLORS.card, borderRadius: 14, padding: 22, width: "100%", maxWidth: 440, boxShadow: "0 12px 40px rgba(0,0,0,0.32)" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, marginBottom: 4 }}>↩ Vorkasse erstatten</div>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 14 }}>
              {(customers.find(c => c.id === refundApt.customerId) || {}).name || refundApt.customerName || "Kunde"} · {formatDateShort(refundApt.date)} {refundApt.startTime} · Vorkasse {fmtEur(refundApt.prepaidAmount)}
            </div>
            {/* WIDERRUF_PREPAY_v1 -- ein fristgerechter gesetzlicher Widerruf sticht die Absagefrist:
                volle Erstattung, keine Ausfallgebühr, auch kurz vor dem Termin. Steht deshalb ÜBER
                dem normalen Absage-Hinweis und ersetzt ihn, damit nicht zwei Regeln nebeneinander
                stehen und der Salon die falsche befolgt. */}
            {refundInfo && refundInfo.widerruf && refundInfo.widerruf.in_time && (
              <div style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.55, padding: "10px 12px", background: "rgba(220,38,38,0.09)", borderRadius: 8, marginBottom: 12, borderLeft: "2px solid #DC2626" }}>
                <div style={{ fontWeight: 800, marginBottom: 3 }}>Gesetzlicher Widerruf</div>
                {refundInfo.widerruf.hint}
                {refundInfo.widerruf.days_left != null && (
                  <div style={{ marginTop: 4, color: refundInfo.widerruf.is_overdue ? "#991B1B" : COLORS.textLight, fontWeight: refundInfo.widerruf.is_overdue ? 700 : 400 }}>
                    {refundInfo.widerruf.is_overdue
                      ? `Die Rückzahlungsfrist ist seit ${Math.abs(refundInfo.widerruf.days_left)} Tag(en) abgelaufen.`
                      : `Noch ${refundInfo.widerruf.days_left} Tag(e) bis zur Rückzahlungsfrist.`}
                  </div>
                )}
              </div>
            )}
            {refundInfo && !(refundInfo.widerruf && refundInfo.widerruf.in_time) && (
              <div style={{ fontSize: 12.5, color: COLORS.text, lineHeight: 1.55, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8, marginBottom: 12, borderLeft: "2px solid " + (refundInfo.late_cancellation ? COLORS.warning : COLORS.success) }}>
                {refundInfo.late_cancellation
                  ? <>Absage <b>nach</b> der Frist ({refundInfo.cancellation_hours} Std. vorher): du darfst die Ausfallgebühr von <b>{fmtEur(refundInfo.noshow_fee)}</b> einbehalten. Vorschlag: <b>{fmtEur(refundInfo.suggested_amount)}</b> erstatten.</>
                  : <>Absage innerhalb der Frist ({refundInfo.cancellation_hours} Std. vorher) oder Termin noch offen: Vorschlag <b>volle Erstattung {fmtEur(refundInfo.suggested_amount)}</b>.</>}
                {refundInfo.widerruf && refundInfo.widerruf.in_time === false && (
                  <div style={{ marginTop: 5, color: COLORS.textLight }}>{refundInfo.widerruf.hint}</div>
                )}
              </div>
            )}
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Erstattungsbetrag (€)</label>
            <input type="number" step="0.01" min="0.01" max={(refundInfo && refundInfo.refundable_amount != null ? refundInfo.refundable_amount : refundApt.prepaidAmount) || undefined} value={refundAmount} onChange={e => setRefundAmount(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "var(--bg)", color: COLORS.text, fontSize: 15, fontWeight: 700, boxSizing: "border-box" }} />
            <div style={{ fontSize: 11.5, color: COLORS.textLight, margin: "6px 0 12px", lineHeight: 1.5 }}>
              Das Geld geht über {refundApt.prepayProvider === "paypal" ? "PayPal" : refundApt.prepayProvider === "stripe" ? "Stripe" : "SumUp"} an das Zahlungsmittel des Kunden zurück (Dauer je nach Bank einige Tage). Die Erstattung lässt sich nicht rückgängig machen.
            </div>
            {/* PREPAY_LEDGER_v1 -- Teil-Erstattung: der einbehaltene Teil braucht einen Beleg (Belegausgabepflicht,
                USt-Zeitpunkt) -> Ausfallgebuehr-Bon in der Shearly Kasse (Kassieren -> Termine -> Ausfallgebuehr) */}
            {(() => {
              const amtN = Number(String(refundAmount).replace(",", "."));
              const alreadyRetained = refundInfo && refundInfo.retained_amount != null ? Number(refundInfo.retained_amount) : 0;
              const refundable = refundInfo && refundInfo.refundable_amount != null ? Number(refundInfo.refundable_amount) : (Number(refundApt.prepaidAmount) || 0);
              /* WIDERRUF_PREPAY_v1 -- bei fristgerechtem Widerruf darf NICHTS einbehalten werden.
                 Der bisherige Hinweis "Ausfallgebühr in der Kasse buchen" wäre hier die falsche
                 Anleitung, deshalb steht an seiner Stelle der Weg zurück: Bon stornieren. */
              const wrInTime = !!(refundInfo && refundInfo.widerruf && refundInfo.widerruf.in_time);
              if (alreadyRetained > 0) {
                return <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.55, padding: "10px 12px", background: wrInTime ? "rgba(220,38,38,0.09)" : "var(--cardHover)", borderRadius: 8, marginBottom: 12, borderLeft: "2px solid " + (wrInTime ? "#DC2626" : COLORS.textLight) }}>
                  Ausfallgebühr <b>{fmtEur(alreadyRetained)}</b> ist bereits in der Kasse gebucht{refundInfo.retained_ref ? ` (Bon ${refundInfo.retained_ref})` : ""} – erstattbar ist hier noch höchstens <b>{fmtEur(refundable)}</b>.
                  {wrInTime && <div style={{ marginTop: 4, fontWeight: 700 }}>
                    Bei diesem Widerruf ist die Ausfallgebühr nicht zulässig: bitte zuerst den Bon {posShearly ? "in der Kasse (Kassieren → Bons → Storno)" : "in deiner Kasse"} stornieren, danach lässt sich hier die volle Vorkasse erstatten.
                  </div>}
                </div>;
              }
              const keep = refundable - (amtN > 0 ? amtN : 0);
              if (!(amtN > 0) || keep <= 0.004) return null;
              if (wrInTime) {
                return <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.55, padding: "10px 12px", background: "rgba(220,38,38,0.09)", borderRadius: 8, marginBottom: 12, borderLeft: "2px solid #DC2626" }}>
                  Du würdest <b>{fmtEur(keep)}</b> einbehalten. Bei einem fristgerechten Widerruf ist das <b>nicht zulässig</b> – der volle Betrag von {fmtEur(refundable)} gehört zurück.
                </div>;
              }
              return <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.55, padding: "10px 12px", background: "rgba(245,158,11,0.10)", borderRadius: 8, marginBottom: 12, borderLeft: "2px solid " + COLORS.warning }}>
                Du behältst <b>{fmtEur(keep)}</b> ein. <b>Einbehaltene Ausfallgebühr in der Kasse buchen:</b> {posShearly ? "Kassieren → Termine → Ausfallgebühr" : "in deiner Kasse als Ausfallgebühr belegen"} (Beleg für den Kunden, Umsatzsteuer-Zeitpunkt für die Steuerkanzlei).
              </div>;
            })()}
            {refundError && (
              <div style={{ fontSize: 12, color: "#991B1B", padding: "10px 12px", marginBottom: 10, background: "rgba(239,68,68,0.10)", borderRadius: 6, borderLeft: "2px solid " + COLORS.danger }}>⚠ {refundError}</div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={doRefund} disabled={refundBusy}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 13, cursor: refundBusy ? "wait" : "pointer", opacity: refundBusy ? 0.6 : 1 }}>
                {refundBusy ? "Wird erstattet…" : "Jetzt erstatten"}
              </button>
              <button onClick={() => setRefundApt(null)} disabled={refundBusy}
                style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHECKOUT_v1 -- Kassieren-Modal mit 3 Optionen: Bar / QR / Reader */}
      {checkoutApt && (() => {
        const apt = checkoutApt;
        const cust = customers.find(c => c.id === apt.customerId);
        const svcs = apt.serviceIds ? (apt.serviceIds||[]).map(sid => services.find(s => s.id === sid)?.name).filter(Boolean).join(", ") : "";
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
               onClick={() => { if (!checkoutBusy) { setCheckoutApt(null); setSumupCheckout(null); setSumupError(""); } }}>
            <div onClick={e => e.stopPropagation()} style={{ background: COLORS.card, borderRadius: 14, padding: 22, width: "100%", maxWidth: 460, boxShadow: "0 12px 40px rgba(0,0,0,0.32)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg, #10B981 0%, #059669 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>💳</div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text, lineHeight: 1.2 }}>Kassieren</div>
                  <div style={{ fontSize: 12, color: COLORS.textLight }}>{cust?.name || apt.customerName || "Kunde"} · {svcs || "—"}</div>
                </div>
                <div style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: COLORS.text }}>{apt.totalPrice} €</div>
              </div>

              {/* SUMUP_v1 -- Wenn Checkout-URL erhalten, zeige sie an */}
              {sumupCheckout ? (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 12 }}>
                    Kunde scannt diesen Link mit dem Handy oder gibt ihn im Browser ein:
                  </div>
                  <div style={{ background: "#fff", padding: 14, borderRadius: 10, marginBottom: 12 }}>
                    <img src={qrDataUrl(sumupCheckout.url)} /* SELF_HOST_QR_v1: QR lokal erzeugt, vorher api.qrserver.com (Checkout-URL + IP an Drittanbieter) */
                         alt="QR-Code" style={{ width: 240, height: 240, display: "block", margin: "0 auto" }} />
                  </div>
                  <div style={{ background: "var(--cardHover)", padding: "10px 12px", borderRadius: 8, fontSize: 11, color: COLORS.text, wordBreak: "break-all", fontFamily: "monospace", marginBottom: 10 }}>
                    {sumupCheckout.url}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => { navigator.clipboard.writeText(sumupCheckout.url); }}
                      style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.primary, background: COLORS.primary, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                      📋 Link kopieren
                    </button>
                    <button onClick={() => { setSumupCheckout(null); setSumupError(""); }}
                      style={{ flex: 1, padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                      Zurück
                    </button>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: COLORS.textLight }}>
                    Sobald der Kunde zahlt, wird der Termin automatisch als bezahlt markiert.
                  </div>
                </div>
              ) : (
              <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                {/* Bar — funktioniert sofort */}
                <button
                  disabled={checkoutBusy}
                  onClick={() => posShearly ? openKasseForAppt(apt) : markApptPaid(apt, "cash")} /* POS_CHECKOUT_DEEPLINK_v1 */
                  style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, border: "1.5px solid " + COLORS.success, background: "rgba(16,185,129,0.08)", cursor: checkoutBusy ? "wait" : "pointer", textAlign: "left", width: "100%", opacity: checkoutBusy ? 0.6 : 1 }}>
                  <span style={{ fontSize: 22 }}>💵</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{posShearly ? "Bar · in der Shearly Kasse kassieren" : "Bar erhalten"}</div>
                    <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>
                      {posShearly
                        ? "Öffnet die Kasse mit Kunde, Mitarbeiter und Leistungen vorbelegt · der Bon meldet den Termin als bezahlt zurück"
                        : "Termin als bezahlt markieren · Bargeld läuft über deine Kasse"}
                    </div>
                  </div>
                </button>

                {/* Karte am Reader — manuell bestätigen, immer da */}
                <button
                  disabled={checkoutBusy}
                  onClick={() => markApptPaid(apt, "card")}
                  style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, border: "1.5px solid " + COLORS.info, background: "rgba(59,130,246,0.08)", cursor: checkoutBusy ? "wait" : "pointer", textAlign: "left", width: "100%", opacity: checkoutBusy ? 0.6 : 1 }}>
                  <span style={{ fontSize: 22 }}>💳</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Karte am Reader</div>
                    <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>
                      {posInfo.provider
                        ? `Über deinen ${posLabel}-Reader kassieren · hier nur als bezahlt markieren`
                        : "Über deinen Karten-Reader kassieren · hier nur als bezahlt markieren"}
                    </div>
                  </div>
                </button>

                {/* QR-Code — NUR wenn SumUp konfiguriert. Sonst weggelassen. */}
                {sumupReady && (
                  <button
                    disabled={checkoutBusy}
                    onClick={() => requestSumupCheckout(apt)}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 10, border: "1.5px solid " + COLORS.primary, background: "rgba(124,58,237,0.08)", cursor: checkoutBusy ? "wait" : "pointer", textAlign: "left", width: "100%", opacity: checkoutBusy ? 0.6 : 1 }}>
                    <span style={{ fontSize: 22 }}>📱</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>QR-Code an Kunden zeigen</div>
                      <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>Bezahl-Link via SumUp · Kunde scannt mit Handy {checkoutBusy ? "· Bitte warten…" : ""}</div>
                    </div>
                  </button>
                )}
              </div>

              {sumupError && (
                <div style={{ fontSize: 12, color: "#991B1B", padding: "10px 12px", marginBottom: 10, background: "rgba(239,68,68,0.10)", borderRadius: 6, borderLeft: "2px solid " + COLORS.danger }}>
                  ⚠ {sumupError}
                </div>
              )}

              {/* CHECKOUT_v1 -- Provider-spezifische Hinweis-Box im Footer */}
              {posInfo.loaded && (
                <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10, padding: "8px 10px", background: "var(--cardHover)", borderRadius: 6, borderLeft: "2px solid " + (sumupReady ? COLORS.success : posInfo.provider ? COLORS.info : COLORS.warning), lineHeight: 1.5 }}>
                  {sumupReady && (
                    <>✓ <b>SumUp aktiv:</b> Du kannst per QR-Code online kassieren — Kunde scannt + zahlt, Termin wird automatisch als bezahlt markiert.</>
                  )}
                  {!sumupReady && posInfo.provider === "sumup" && (
                    <>⚠ <b>SumUp gewählt, aber nicht verbunden:</b> trag API-Key + Merchant-Code unter <em>Sidebar → Kassensystem</em> ein und teste die Verbindung. Dann kannst du auch QR-Codes erzeugen.</>
                  )}
                  {!sumupReady && posInfo.provider && posInfo.provider !== "sumup" && (
                    <>ℹ <b>{posLabel} verbunden:</b> Kassieren läuft physisch über deinen {posLabel}-Reader oder andere Hardware. In Shearly nur als bezahlt markieren (Bar oder Karte). Online-QR-Bezahlung via SumUp möglich falls gewünscht (zusätzlich oder statt {posLabel}).</>
                  )}
                  {!sumupReady && !posInfo.provider && (
                    <>ℹ <b>Kein Kassensystem verbunden:</b> Du kassierst manuell — Termin als Bar oder Karte als bezahlt markieren. Für Online-Bezahlung per QR-Code unter <em>Sidebar → Kassensystem</em> SumUp einrichten.</>
                  )}
                </div>
              )}

              <button onClick={() => { setCheckoutApt(null); setSumupCheckout(null); setSumupError(""); }} disabled={checkoutBusy}
                style={{ width: "100%", padding: "10px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontWeight: 600, fontSize: 13, cursor: checkoutBusy ? "wait" : "pointer" }}>
                Abbrechen
              </button>
              </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Month View ── CALENDAR_KW_HOLIDAYS_v1: 8-Spalten-Grid (KW + 7 Wochentage) */}
      {calView === "month" && (() => {
        const year = selectedDate.getFullYear(), month = selectedDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        // Monday-based: 0=Mo, 6=So
        const startDow = (firstDay.getDay() + 6) % 7;
        const weeks = [];
        let week = Array(startDow).fill(null);
        for (let d = 1; d <= daysInMonth; d++) {
          week.push(d);
          if (week.length === 7) { weeks.push(week); week = []; }
        }
        if (week.length > 0) { while (week.length < 7) week.push(null); weeks.push(week); }
        const todayStr = formatDate(today);
        return (
          <Card style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "40px repeat(7, 1fr)", gap: 2 }}>
              <div style={{ textAlign: "center", fontWeight: 700, fontSize: 10, color: COLORS.textLight, padding: "8px 0" }}>KW</div>
              {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map(d => (
                <div key={d} style={{ textAlign: "center", fontWeight: 700, fontSize: 12, color: d === "So" ? COLORS.danger : COLORS.textLight, padding: "8px 0" }}>{d}</div>
              ))}
              {weeks.flatMap((week, wIdx) => {
                const firstReal = week.find(d => d != null);
                const refDate = firstReal ? new Date(year, month, firstReal) : null;
                const kw = refDate ? getISOWeek(refDate) : "";
                const cells = [
                  <div key={"kw"+wIdx} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: COLORS.textLight, alignSelf: "center", padding: 4 }}>{kw}</div>
                ];
                week.forEach((day, di) => {
                  if (!day) { cells.push(<div key={"e"+wIdx+"_"+di} style={{ padding: 8 }} />); return; }
                  const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isToday = ds === todayStr;
                  const hol = calHolidays.find(h => h.date === ds);
                  const dayDate = new Date(year, month, day);
                  const isSun = dayDate.getDay() === 0;
                  const appts = appointments.filter(a => a.date === ds && a.status !== "cancelled");
                  cells.push(
                    <div key={"d"+wIdx+"_"+di} onClick={() => setMonthDetail({ ds, dayDate })}
                      style={{
                        padding: "6px 4px", minHeight: 72, borderRadius: 8, cursor: "pointer",
                        background: isToday ? `${COLORS.primary}08` : hol ? `${COLORS.danger}05` : "var(--card)",
                        border: isToday ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                        transition: "all 0.15s",
                        opacity: hol ? 0.85 : 1
                      }}
                      onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = `${COLORS.primary}06`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isToday ? `${COLORS.primary}08` : hol ? `${COLORS.danger}05` : "var(--card)"; }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: isToday ? 800 : 600, color: (isSun || hol) ? COLORS.danger : isToday ? COLORS.primary : COLORS.text }}>{day}</span>
                        {appts.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.primary, background: `${COLORS.primary}12`, borderRadius: 8, padding: "1px 5px" }}>{appts.length}</span>}
                      </div>
                      {hol && <div style={{ fontSize: 9, color: COLORS.danger, fontWeight: 600, marginBottom: 2 }} title={hol.name}>{hol.name}</div>}
                      {/* OCC_MINI_TIMELINE_v1 -- Mini-Zeitleiste je Mitarbeiter: Blöcke positionsgenau, Lücken sichtbar (gleiche Logik wie Lücken-Panel; ersetzt den Prozent-Füllbalken aus OCCUPANCY_BAR_v1) */}
                      {(() => {
                        // DAY_STRIP_OCC_v1 -- Rechnung jetzt in occEmpDay (gleiche Logik, auch fuer die Tagesleiste)
                        const rows = employees.map(emp => occEmpDay(emp, dayDate, ds)).filter(Boolean);
                        if (rows.length === 0) return null;
                        const shown = rows.slice(0, 5);
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {shown.map(({ emp, sh, winStart, winEnd, span, blocks, gaps, freeMin, pct }) => {
                              const gapStr = gaps.map(g => `${occFmt(g[0])}–${occFmt(g[1])}`).join(", ");
                              const tip = `${emp.name}: ${pct}% belegt` + (freeMin > 0 ? ` · frei ${gapStr}` : " · ausgebucht") + (sh ? "" : " · nicht im Schichtplan");
                              return (
                                <div key={emp.id} title={tip} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                  <div style={{ width: 5, height: 5, borderRadius: "50%", background: emp.color, flexShrink: 0 }} />
                                  <div style={{ flex: 1, height: 5, borderRadius: 3, background: `${COLORS.border}`, overflow: "hidden", position: "relative" }}>
                                    {blocks.map((b, bi) => {
                                      const left = ((Math.max(b.s, winStart) - winStart) / span) * 100;
                                      const width = ((Math.min(b.e, winEnd) - Math.max(b.s, winStart)) / span) * 100;
                                      if (width <= 0) return null;
                                      return (
                                        <div key={bi} style={{ position: "absolute", top: 0, bottom: 0, left: left + "%", width: width + "%",
                                          background: b.type === "break" ? `${COLORS.textLight}55` : emp.color }} />
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                            {rows.length > shown.length && <div style={{ fontSize: 8, color: COLORS.textLight, marginTop: 1 }}>+{rows.length - shown.length} weitere</div>}
                          </div>
                        );
                      })()}
                    </div>
                  );
                });
                return cells;
              })}
            </div>
          </Card>
        );
      })()}

      {/* OCCUPANCY_BAR_v1 -- Tages-Timeline beim Klick auf einen Tag in der Monatsuebersicht */}
      {calView === "month" && monthDetail && (() => {
        const { ds, dayDate } = monthDetail;
        const dk = occDayKey(dayDate);
        const title = dayDate.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
        const rows = employees.map(emp => {
          const sh = occWeekShift(emp.id, dk, ds);
          const dayAppts = appointments
            .filter(a => a.employeeId === emp.id && a.date === ds && a.status !== "cancelled" && !(a.isPhaseMaster || a.is_phase_master))
            .map(a => ({ s: occToMin(a.startTime), e: occToMin(a.startTime) + (a.totalDuration || 0) }))
            .sort((x, y) => x.s - y.s);
          if (!sh && dayAppts.length === 0) return null;
          return { emp, sh, dayAppts };
        }).filter(Boolean);
        return (
          <Card style={{ padding: 16, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Lücken am {title}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="secondary" size="sm" onClick={() => { setSelectedDate(dayDate); setCalView("day"); setMonthDetail(null); }}>Tagesansicht öffnen</Btn>
                <Btn variant="ghost" size="sm" onClick={() => setMonthDetail(null)}>Schließen</Btn>
              </div>
            </div>
            {rows.length === 0 ? (
              <div style={{ color: COLORS.textLight, fontSize: 13, padding: "8px 0" }}>Niemand eingeteilt und keine Termine an diesem Tag.</div>
            ) : rows.map(({ emp, sh, dayAppts }) => {
              const winStart = sh ? occToMin(sh.start_time) : (dayAppts.length ? dayAppts[0].s : 8 * 60);
              const winEnd = sh ? occToMin(sh.end_time) : (dayAppts.length ? dayAppts[dayAppts.length - 1].e : 18 * 60);
              const span = Math.max(1, winEnd - winStart);
              const blocks = dayAppts.map(a => ({ ...a, type: "appt" }));
              { const _pb = occBreakOf(emp.id, ds, sh); if (_pb) blocks.push({ s: _pb.s, e: _pb.e, type: "break" }); } /* PAUSE_DAY_v1 */
              blocks.sort((a, b) => a.s - b.s);
              const gaps = [];
              let cursor = winStart;
              blocks.forEach(b => {
                const bs = Math.max(b.s, winStart), be = Math.min(b.e, winEnd);
                if (bs > cursor) gaps.push([cursor, bs]);
                cursor = Math.max(cursor, be);
              });
              if (cursor < winEnd) gaps.push([cursor, winEnd]);
              const freeMin = gaps.reduce((s, g) => s + (g[1] - g[0]), 0);
              return (
                <div key={emp.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: emp.color }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</span>
                    <span style={{ fontSize: 11, color: COLORS.textLight }}>
                      {sh ? `${occFmt(winStart)}–${occFmt(winEnd)} · ` : "nicht eingeteilt · "}
                      {freeMin > 0 ? `${Math.floor(freeMin / 60)}h ${freeMin % 60}min frei` : "ausgebucht"}
                    </span>
                  </div>
                  <div style={{ position: "relative", height: 18, borderRadius: 5, background: `${COLORS.success}14`, overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
                    {blocks.map((b, bi) => {
                      const left = ((Math.max(b.s, winStart) - winStart) / span) * 100;
                      const width = ((Math.min(b.e, winEnd) - Math.max(b.s, winStart)) / span) * 100;
                      if (width <= 0) return null;
                      return (
                        <div key={bi} title={b.type === "break" ? `Pause ${occFmt(b.s)}–${occFmt(b.e)}` : `Termin ${occFmt(b.s)}–${occFmt(b.e)}`}
                          style={{ position: "absolute", top: 0, bottom: 0, left: left + "%", width: width + "%",
                            background: b.type === "break" ? `${COLORS.textLight}55` : emp.color,
                            backgroundImage: b.type === "break" ? "repeating-linear-gradient(45deg,transparent,transparent 3px,rgba(255,255,255,0.4) 3px,rgba(255,255,255,0.4) 6px)" : "none" }} />
                      );
                    })}
                  </div>
                  {gaps.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                      {gaps.map((g, gi) => (
                        <span key={gi} style={{ fontSize: 11, color: COLORS.success, background: `${COLORS.success}14`, borderRadius: 6, padding: "1px 6px", fontWeight: 600 }}>
                          frei {occFmt(g[0])}–{occFmt(g[1])}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: COLORS.textLight }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: COLORS.primary, display: "inline-block" }} /> belegt</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: `${COLORS.success}14`, border: `1px solid ${COLORS.border}`, display: "inline-block" }} /> frei</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: `${COLORS.textLight}55`, display: "inline-block" }} /> Pause</span>
            </div>
          </Card>
        );
      })()}

      {/* ── Year View ── */}
      {calView === "year" && (() => {
        const year = selectedDate.getFullYear();
        const todayStr = formatDate(today);
        const monthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
        return (
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(3, 1fr)", gap: 16 }}>
            {Array.from({ length: 12 }, (_, m) => {
              const firstDay = new Date(year, m, 1);
              const daysInMonth = new Date(year, m + 1, 0).getDate();
              const startDow = (firstDay.getDay() + 6) % 7;
              const weeks = [];
              let week = Array(startDow).fill(null);
              for (let d = 1; d <= daysInMonth; d++) {
                week.push(d);
                if (week.length === 7) { weeks.push(week); week = []; }
              }
              if (week.length > 0) { while (week.length < 7) week.push(null); weeks.push(week); }
              const isCurrentMonth = today.getFullYear() === year && today.getMonth() === m;
              return (
                <Card key={m} style={{ padding: 12 }}>
                  <div onClick={() => { setSelectedDate(new Date(year, m, 1)); setCalView("month"); }}
                    style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, cursor: "pointer", color: isCurrentMonth ? COLORS.primary : COLORS.text }}>
                    {monthNames[m]}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, fontSize: 10 }}>
                    {["M", "D", "M", "D", "F", "S", "S"].map((d, i) => (
                      <div key={i} style={{ textAlign: "center", fontWeight: 700, color: i === 6 ? COLORS.danger : COLORS.textLight, padding: "2px 0" }}>{d}</div>
                    ))}
                    {weeks.flat().map((day, i) => {
                      if (!day) return <div key={i} />;
                      const ds = `${year}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      const isToday = ds === todayStr;
                      const hol = calHolidays.find(h => h.date === ds);
                      const dayDate = new Date(year, m, day);
                      const isSun = dayDate.getDay() === 0;
                      return (
                        <div key={i} onClick={() => { setSelectedDate(dayDate); setCalView("day"); }}
                          style={{
                            textAlign: "center", padding: "3px 0", borderRadius: 4, cursor: "pointer", fontSize: 11,
                            fontWeight: isToday ? 800 : hol ? 700 : 400,
                            color: hol ? "white" : isSun ? COLORS.danger : isToday ? COLORS.primary : COLORS.text,
                            background: hol ? COLORS.danger : isToday ? `${COLORS.primary}15` : "transparent",
                          }}
                          title={hol ? hol.name : ""}>
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        );
      })()}

      <BookingModal
        open={showBooking}
        onClose={() => { setShowBooking(false); setBookingStartNewCustomer(false); setBookingPrefill(null); }}
        employees={employees}
        customers={customers}
        setCustomers={setCustomers}
        services={services}
        appointments={appointments}
        setAppointments={setAppointments}
        defaultDate={(bookingPrefill && bookingPrefill.date) || dateStr}
        startInNewCustomerMode={bookingStartNewCustomer}
        preselectEmployeeId={bookingPrefill && bookingPrefill.employeeId}
        preselectStartTime={bookingPrefill && bookingPrefill.startTime}
      />

      {/* PAUSE_DAY_v1 (Toby 07.10.2026) -- Pause nur für diesen Tag ändern (Klick aufs graue Pausenfeld).
          Bestehende Termine bleiben unangetastet; wirkt auf Kalender, Online-Buchung, Zoey und KI-Telefon. */}
      {pauseDay && (() => {
        const w = pauseDay.win || {};
        const hasPlan = typeof w.plan_break_start === "number" && typeof w.plan_break_end === "number";
        const hasEff = typeof w.break_start === "number" && typeof w.break_end === "number";
        const dLabel = new Date(pauseDay.date + "T12:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
        const inp = { display: "block", marginTop: 4, padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 15,
                      width: "100%", boxSizing: "border-box", background: "var(--card)", color: COLORS.text };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10500 }}
               onClick={() => { if (!pauseDayBusy) setPauseDay(null); }}>
            <div data-pause-day-modal="1" style={{ background: "var(--card)", borderRadius: 16, padding: 24, width: Math.min(380, window.innerWidth - 32) }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: "0 0 4px", fontSize: 18 }}>Pause am {dLabel}</h3>
              <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 14, lineHeight: 1.45 }}>
                {pauseDay.emp.name} · Schichtplan: {hasPlan ? pdFmt(w.plan_break_start) + "–" + pdFmt(w.plan_break_end) : "keine Pause"}
                {w.break_day === "moved" && hasEff ? " · heute: " + pdFmt(w.break_start) + "–" + pdFmt(w.break_end) : ""}
                {w.break_day === "none" ? " · heute: keine Pause" : ""}
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <label style={{ flex: 1, fontSize: 12, fontWeight: 600, color: COLORS.text }}>von
                  <input type="time" step="300" value={pauseDayForm.from} onChange={e => setPauseDayForm(f => ({ ...f, from: e.target.value }))} style={inp} />
                </label>
                <label style={{ flex: 1, fontSize: 12, fontWeight: 600, color: COLORS.text }}>bis
                  <input type="time" step="300" value={pauseDayForm.to} onChange={e => setPauseDayForm(f => ({ ...f, to: e.target.value }))} style={inp} />
                </label>
              </div>
              {pauseDayErr && <div style={{ color: COLORS.danger, fontSize: 13, marginBottom: 10 }}>{pauseDayErr}</div>}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Btn disabled={pauseDayBusy} onClick={() => savePauseDay("set")}>Nur an diesem Tag speichern</Btn>
                {hasPlan && w.break_day !== "none" && (
                  <Btn variant="secondary" disabled={pauseDayBusy} onClick={() => savePauseDay("none")}>An diesem Tag keine Pause</Btn>
                )}
                {w.break_day && (
                  <Btn variant="secondary" disabled={pauseDayBusy} onClick={() => savePauseDay("plan")}>
                    Zurück auf Schichtplan{hasPlan ? " (" + pdFmt(w.plan_break_start) + "–" + pdFmt(w.plan_break_end) + ")" : ""}
                  </Btn>
                )}
                {hasEff && (
                  <Btn variant="ghost" style={QUIET_BTN_STYLE} disabled={pauseDayBusy} onClick={() => {
                    const p = { employeeId: pauseDay.emp.id, date: pauseDay.date, startTime: pdFmt(w.break_start) };
                    setPauseDay(null); setBookingPrefill(p); setBookingStartNewCustomer(false); setShowBooking(true);
                  }}>Termin in der Pause eintragen</Btn>
                )}
                <Btn variant="ghost" disabled={pauseDayBusy} onClick={() => setPauseDay(null)}>Abbrechen</Btn>
              </div>
              <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 12, lineHeight: 1.4 }}>
                Gilt nur für diesen Tag. Bestehende Termine bleiben unverändert. Für alle Wochen gilt der Schichtplan
                {onGoToShifts && currentUser && currentUser.role !== "employee"
                  ? <> · <span onClick={() => { setPauseDay(null); onGoToShifts(); }} style={{ color: COLORS.primary, cursor: "pointer", fontWeight: 600 }}>Schichtplan öffnen</span></>
                  : null}
              </div>
            </div>
          </div>
        );
      })()}

      {/* CAL_SLOT_CHOOSER_v1 -- Frage-Fenster nach Klick auf leere Zeit-Flaeche: Termin buchen oder Pause.
          zIndex 10500: ueber allen Kalender-Overlays (gleiche Lehre wie idlePhaseChooser). */}
      {slotChoice && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10500 }} onClick={() => setSlotChoice(null)}>
          <div style={{ background: "var(--card)", borderRadius: 16, padding: 24, width: Math.min(360, window.innerWidth - 32) }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 6px", fontSize: 18 }}>Was möchtest du eintragen?</h3>
            <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 18 }}>
              {(() => { const e = (employees || []).find(x => x.id === slotChoice.employeeId); return `${e ? e.name + " · " : ""}${formatDateShort(slotChoice.date)} · ${slotChoice.startTime} Uhr`; })()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Btn onClick={() => { setBookingPrefill(slotChoice); setBookingStartNewCustomer(false); setShowBooking(true); setSlotChoice(null); }}><Calendar size={16} /> Termin buchen</Btn>
              {setCustomers && <Btn variant="secondary" onClick={() => { setBookingPrefill(slotChoice); setBookingStartNewCustomer(true); setShowBooking(true); setSlotChoice(null); }} title="Neuen Kunden anlegen und gleich Termin vergeben"><UserPlus size={16} /> Neukunde + Termin</Btn>}
              {(() => {
                // PAUSE_INTERN_SPLIT_v1 -- neben "Pause" auch "Intern" (Zeit + Mitarbeiter schon eingetragen)
                const openBlock = (mode, minutes) => {
                  const [hh, mm] = String(slotChoice.startTime).split(":").map(Number);
                  const endM = hh * 60 + mm + minutes;
                  const endTime = String(Math.floor(endM / 60)).padStart(2, "0") + ":" + String(endM % 60).padStart(2, "0");
                  setPausePrefill({ employeeId: slotChoice.employeeId, date: slotChoice.date, startTime: slotChoice.startTime, endTime, mode });
                  setPauseSignal(s => s + 1);
                  setSlotChoice(null);
                };
                return (
                  <div style={{ display: "flex", gap: 10 }}>
                    <Btn variant="ghost" style={{ flex: 1, ...QUIET_BTN_STYLE }} onClick={() => openBlock("pause", 30)}>⏸ Pause</Btn>
                    <Btn variant="ghost" style={{ flex: 1, ...QUIET_BTN_STYLE }} onClick={() => openBlock("intern", 60)}>Intern</Btn>
                  </div>
                );
              })()}
              <Btn variant="ghost" onClick={() => setSlotChoice(null)}>Abbrechen</Btn>
            </div>
          </div>
        </div>
      )}

      {/* IDLE_PHASE_BLOCK_v1 -- Auswahl-Fenster beim Klick auf eine Einwirkzeit-Karte.
          zIndex 10500: muss UEBER allen Kalender-Overlays liegen (Lehre aus EMPLOYEE_LIMIT_v1). */}
      {/* APPT_ASSIST_REMOVE_v1 (Toby 18.09.2026) -- Fenster zur 🤝-Assistenz-Karte im Kalender des Helfers.
          "Assistenz löschen" entfernt NUR die Assistenz: die Karte hier und der Hinweis am Haupttermin.
          Der Haupttermin (Zeit, Dauer, Mitarbeiter, Leistungen, Status) bleibt unberührt. */}
      {assistantTarget && (() => {
        const { block, masterId } = assistantTarget;
        const master = appointments.find(a => Number(a.id) === Number(masterId)) || null;
        const helper = assistantTarget.emp || employees.find(e => Number(e.id) === Number(block.employeeId));
        const masterEmp = master ? employees.find(e => Number(e.id) === Number(master.employeeId)) : null;
        const custName = master
          ? (customers.find(c => c.id === master.customerId)?.name || master.customerName || "")
          : String(block.phaseLabel || "").split("→")[1]?.trim() || "";
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
               onClick={() => { if (!assistantBusy) setAssistantTarget(null); }}>
            <div style={{ background: "var(--card)", borderRadius: 14, padding: 22, maxWidth: 430, width: "100%", border: "1px solid var(--border)", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}
                 onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
                🤝 Assistenz · {block.startTime}–{getEndTime(block.startTime, block.totalDuration)}
              </div>
              <div style={{ fontSize: 13, color: "var(--textLight)", lineHeight: 1.6, marginBottom: 16 }}>
                {helper?.name || "Mitarbeiter"} hilft {block.totalDuration} Min. beim Termin
                {custName ? <> von <strong style={{ color: "var(--text)" }}>{custName}</strong></> : null}
                {masterEmp ? <> bei {masterEmp.name}</> : null}.
                <br />
                Das hier ist kein eigener Termin, sondern nur der geblockte Zeitraum der Assistenz.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button disabled={assistantBusy} onClick={async () => {
                  setAssistantBusy(true);
                  const done = await removeAssistant(masterId);
                  setAssistantBusy(false);
                  if (done) setAssistantTarget(null);
                }} style={{ padding: "10px 14px", borderRadius: 8, border: "none", background: COLORS.danger, color: "#fff", fontWeight: 700, cursor: assistantBusy ? "wait" : "pointer", fontSize: 14, opacity: assistantBusy ? 0.7 : 1 }}>
                  {assistantBusy ? "Wird entfernt…" : "🗑 Assistenz löschen — Termin bleibt bestehen"}
                </button>
                {master && (
                  <button disabled={assistantBusy} onClick={() => { setAssistantTarget(null); setSelectedApt(master); }}
                          style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--cardHover)", color: "var(--text)", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                    Haupttermin öffnen
                  </button>
                )}
                <button disabled={assistantBusy} onClick={() => setAssistantTarget(null)}
                        style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--textLight)", cursor: "pointer", fontSize: 13 }}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {idlePhaseChooser && (() => {
        const { apt, emp, dateStr: chooserDate, flatPhase } = idlePhaseChooser;
        const blocked = !!apt.phaseKeepBusy;
        // IDLE_FLAT_BLOCK_v1 -- bei Terminen ohne Phasen-Children kommt das Einwirk-Fenster aus der
        // Leistungs-Vorlage: echte Uhrzeit = Termin-Start + Offset, Dauer = Phasen-Dauer.
        const idleStart = flatPhase ? getEndTime(apt.startTime, flatPhase.offsetMin || 0) : apt.startTime;
        const idleDur = flatPhase ? (flatPhase.durMin || 0) : apt.totalDuration;
        const idleLabel = (flatPhase && flatPhase.label) || apt.phaseLabel || "Einwirkzeit";
        const toggleBlock = async () => {
          try {
            const r = await api.request(`/appointments/${apt.id}/phase-block`, {
              method: "POST", body: JSON.stringify({ blocked: !blocked }),
            });
            if (r.ok) {
              // CAL_INSTANT_REFRESH_v1 -- Karten sofort aktualisieren (Optik + Konflikt-Logik)
              try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
              setIdlePhaseChooser(null);
            } else {
              alert((r.data && r.data.error) || "Aktion fehlgeschlagen — bitte erneut versuchen.");
            }
          } catch (e) { alert("Verbindungsfehler — bitte erneut versuchen."); }
        };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
               onClick={() => setIdlePhaseChooser(null)}>
            <div style={{ background: "var(--card)", borderRadius: 14, padding: 22, maxWidth: 430, width: "100%", border: "1px solid var(--border)", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}
                 onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
                {blocked ? "🔒" : "⏳"} {idleLabel} · {idleStart}–{getEndTime(idleStart, idleDur)}
              </div>
              <div style={{ fontSize: 13, color: "var(--textLight)", lineHeight: 1.5, marginBottom: 16 }}>
                {blocked
                  ? "Diese Einwirkzeit ist blockiert — Online-Buchung, KI-Telefon und Chat sehen die Zeit als belegt."
                  : `${emp?.name?.split(" ")[0] || "Stylist"} ist in dieser Zeit frei — du kannst einen weiteren Termin buchen oder die Zeit blockieren.`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {!blocked && (
                  <button onClick={() => { setBookingPrefill({ employeeId: emp.id, date: chooserDate, startTime: idleStart }); setBookingStartNewCustomer(false); setShowBooking(true); setIdlePhaseChooser(null); }}
                          style={{ padding: "10px 14px", borderRadius: 8, border: "none", background: "#7C3AED", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                    ➕ Termin in der Einwirkzeit buchen
                  </button>
                )}
                <button onClick={toggleBlock}
                        style={{ padding: "10px 14px", borderRadius: 8, border: blocked ? "none" : "1px solid var(--border)", background: blocked ? "#10B981" : "var(--cardHover)", color: blocked ? "#fff" : "var(--text)", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                  {blocked ? "🔓 Einwirkzeit wieder freigeben" : "🔒 Einwirkzeit blockieren — keine weiteren Buchungen"}
                </button>
                {/* PHASE_MASTER_SYNC_v1 -- vorher gab es aus der Einwirkzeit keinen Weg zum Termin selbst
                    (Storno, Kassieren, Verschieben des ganzen Ablaufs) */}
                {!flatPhase && isPhasePart(apt) && (
                  <button onClick={() => openWholeAppt(apt.phaseParentId)}
                          style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--cardHover)", color: "var(--text)", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                    Ganzen Termin öffnen
                  </button>
                )}
                {/* IDLE_PHASE_DELETE_v1 (23.09.2026) -- eine falsch stehende oder verwaiste Einwirkzeit
                    liess sich bisher gar nicht entfernen: der normale Storno-Weg haette über
                    PHASE_CANCEL_CASCADE_v1 den GANZEN Termin des Kunden storniert. Darum ein eigener
                    Knopf mit eigenem Endpunkt — nur für Einwirkzeiten, nie für Arbeitsschritte. */}
                {!flatPhase && isPhasePart(apt) && (
                  <button onClick={async () => {
                    if (!window.confirm(`${idleLabel} ${idleStart}–${getEndTime(idleStart, idleDur)} wirklich entfernen?\n\nNur dieser Abschnitt verschwindet. Der Termin des Kunden und die übrigen Schritte bleiben bestehen und behalten ihre Uhrzeiten.`)) return;
                    try {
                      const r = await api.request(`/appointments/${apt.id}/phase-remove`, { method: "POST", body: "{}" });
                      if (r.ok) {
                        try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
                        setIdlePhaseChooser(null);
                      } else {
                        alert((r.data && r.data.error) || "Entfernen fehlgeschlagen — bitte erneut versuchen.");
                      }
                    } catch (e) { alert("Verbindungsfehler — bitte erneut versuchen."); }
                  }}
                          style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: COLORS.danger, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                    🗑 Einwirkzeit entfernen — Termin bleibt bestehen
                  </button>
                )}
                <button onClick={() => setIdlePhaseChooser(null)}
                        style={{ padding: "9px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--textLight)", cursor: "pointer", fontSize: 13 }}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* CANCEL_REASON_v1 -- Storno-Dialog mit Grund */}
      <CancelDialog
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        customerName={cancelTarget ? (customers.find(c => c.id === cancelTarget.customerId)?.name || "") : ""}
        seriesCount={seriesFutureSiblings(cancelTarget, appointments).length /* SERIES_CANCEL_v1 */}
        onConfirm={async (reason, note, cancelSeries) => {
          if (!cancelTarget) return;
          try {
            const res = await api.updateAppointment(cancelTarget.id, {
              status: "cancelled",
              cancellation_reason: reason,
              cancellation_note: note || null,
              cancel_series: !!cancelSeries, // SERIES_CANCEL_v1
            });
            if (res && res.ok) {
              // SERIES_CANCEL_v1 -- Server meldet die zusätzlich stornierten Serien-IDs zurück
              const _extraIds = (res.data && res.data.series_cancelled_ids) || [];
              setAppointments(prev => prev.map(a => (a.id === cancelTarget.id || _extraIds.includes(a.id))
                ? { ...a, status: "cancelled", cancellation_reason: reason, cancellation_note: note || null }
                : a));
              if (selectedApt && selectedApt.id === cancelTarget.id) {
                setSelectedApt({ ...selectedApt, status: "cancelled", cancellation_reason: reason, cancellation_note: note || null });
              }
              // CAL_INSTANT_REFRESH_v1 -- ganze Phasen-Gruppe wird server-seitig storniert -> sofort neu laden,
              // damit alle Phasen-Karten (inkl. Einwirkzeit) ohne 45s-Wartezeit verschwinden.
              try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
              // VACATION_CONSISTENCY_v1 -- Urlaub wurde komplett gelöscht -> auch Schichtfenster/Overrides frisch laden
              if (res.data && res.data.vacation_deleted) { setOvReloadTick(t => t + 1); setSelectedApt(null); }
              setCancelTarget(null);
            } else {
              // VACATION_CONSISTENCY_v1 -- Server-Meldung zeigen (z.B. „mehrtägiger Urlaub -> im Schichtplan löschen")
              alert((res && res.data && res.data.error) || "Stornieren fehlgeschlagen.");
            }
          } catch (e) {
            alert("Fehler: " + (e && e.message ? e.message : "unbekannt"));
          }
        }}
      />
    </div>
  );
};
// PAUSE_INTERN_SPLIT_v1 (Toby 23.09.2026: "pause ist dann nur pause und intern alles andere", Vorschlaege 1-4 "bau")
// EINE Typ-Liste fuer das Eintrage-Fenster UND die Kalender-Anzeige (Symbol, Farbe). Reihenfolge = Liste bei "Intern".
// Neue Typen aus der tatsaechlichen Nutzung (unter "Pause"/"Sonstiges" standen Kundennamen, Altenheim, "Braut auswaerts",
// "UeStd"): Reserviert fuer Kundin, Ausser Haus, Frueher gehen / Stunden abbauen. "Frueher gehen" steht bewusst vorne,
// damit die Wiesn-Abwesenheit (isSeasonAbsence, "frueher gehen") ihn ohne Sonderregel erkennt.
// Gespeichert wird wie bisher "Typ: Notiz" -- bestehende Eintraege bleiben unveraendert.
// PAUSE_INTERN_COLOR_v1 (Toby 23.09.2026: "mag ich die farbe der pause knopf und intern knopf nicht") -- statt Grau
// ein zarter Ton der Markenfarbe mit feinem Rand (Variante A); hell/dunkel + Farbschema ueber CSS-Variablen (index.html, config.jsx).
// variant "ghost": kein Leucht-Schatten wie beim Hauptknopf, beim Drueberfahren nur das leichte Anheben.
export const QUIET_BTN_STYLE = { background: "var(--tintBg)", color: "var(--tintFg)", boxShadow: "inset 0 0 0 1px var(--tintRing)" };
export const BLOCKER_KINDS = [
    { type: "Pause", icon: "⏸" },
    { type: "Reserviert für Kundin", icon: "📌", color: "#10B981", hint: "Name der Kundin (Pflicht)" },
    { type: "Außer Haus", icon: "🚗", color: "#06B6D4", hint: "z.B. Altenheim, Braut, Hausbesuch" },
    { type: "Früher gehen / Stunden abbauen", icon: "⏱" },
    { type: "Meeting / Besprechung", icon: "👥", color: "#6366F1" },
    { type: "Schulung / Fortbildung", icon: "🎓", color: "#6366F1" },
    { type: "Vertreter-Besuch", icon: "🤝" },
    { type: "Handwerker / Wartung", icon: "🔧" },
    { type: "Lieferung", icon: "📦" },
    { type: "Krank", icon: "🤒" },
    { type: "Urlaub", icon: "🏖" },
    { type: "Sonstiges", icon: "📝" },
];
// fruehere Typ-Namen und Importe (Salonkee/Treatwell) bekommen dasselbe Symbol
const BLOCKER_ALIASES = [
    ["neukunde - zeit unklar", "Reserviert für Kundin"], ["reserviert", "Reserviert für Kundin"],
    ["schulung intern", "Schulung / Fortbildung"], ["fortbildung", "Schulung / Fortbildung"], ["schulung", "Schulung / Fortbildung"],
];
export const blockerKind = (notes) => {
    const t = String(notes || "").trim().toLowerCase();
    const k = BLOCKER_KINDS.find(x => t.startsWith(x.type.toLowerCase()));
    if (k) return k;
    const a = BLOCKER_ALIASES.find(([p]) => t.startsWith(p));
    return (a && BLOCKER_KINDS.find(x => x.type === a[1])) || BLOCKER_KINDS[0];   // unbekannt (z. B. Treatwell) -> wie bisher ⏸
};
// CAL_BLOCKER_SUBJECT_v1 (Toby 09.10.2026: Betreff eines internen Termins nach dem Speichern nicht mehr änderbar) --
// notes eines internen Termins = "<Art>: <Betreff>" + optional " [Serie: …]" (BlockerButton). Zerlegen und wieder
// zusammensetzen, damit Art (Farbe/Symbol) und Serien-Vermerk beim Ändern des Betreffs erhalten bleiben.
export const splitBlockerNotes = (notes) => {
    let t = String(notes || "");
    const m = t.match(/\s*\[Serie:[^\]]*\]/);
    const series = m ? m[0] : "";
    if (m) t = t.replace(m[0], "");
    t = t.trim();
    const k = BLOCKER_KINDS.find(x => t === x.type || t.startsWith(x.type + ":"));
    if (!k) return { kind: "", subject: t, series };
    return { kind: k.type, subject: t.slice(k.type.length).replace(/^:\s*/, ""), series };
};
export const joinBlockerNotes = ({ kind, subject, series }) => {
    const s = String(subject || "").trim();
    return (kind ? kind + (s ? ": " + s : "") : s) + (series || "");
};

// === BlockerButton: self-contained Pause button + modal ===
// INTERNAL_APT_v1 -- Blocker-Modal um interne Termine erweitert (Vertreter, Handwerker, Lieferung, Meeting)
// PAUSE_RECURRING_v1 -- Pause/Intern als Serie (Daily/Weekly/Monthly)
export const BlockerButton = ({ employees, appointments, setAppointments, defaultDate, btnSize, triggerOpen, triggerPrefill, onGoToShifts }) => { /* VACATION_ENTITLEMENT_v1: +onGoToShifts */
    const [showBlocker, setShowBlocker] = React.useState(false);
    const [bMode, setBMode] = React.useState("pause");   // PAUSE_INTERN_SPLIT_v1 -- "pause" | "intern" (ein Fenster, zwei Knoepfe)
    const [bForm, setBForm] = React.useState({
        // BLOCKER_SERIES_TZ_FIX_v1 (14.09.) -- lokale Datumsstrings statt toISOString() (UTC): in DE rutschte
        // jeder Serientermin einen Tag nach vorn (Michi: Mi-Serie landete auf Dienstagen)
        employeeId: "", date: defaultDate || formatDate(new Date()),
        startTime: "09:00", endTime: "10:00", bType: "Pause", notes: "", fullDay: false,
        // PAUSE_RECURRING_v1: Recurrence
        recurrence: "none",                            // none | daily | weekly | monthly
        untilDate: (() => {                            // Default: 4 Wochen voraus
            const d = new Date();
            d.setDate(d.getDate() + 28);
            return formatDate(d);  // BLOCKER_SERIES_TZ_FIX_v1
        })(),
        skipWeekends: true,                             // bei daily: Sa/So skippen (Salon idR geschlossen)
    });
    const [busy, setBusy] = React.useState(false);
    const [progress, setProgress] = React.useState(null);
    React.useEffect(() => { if (defaultDate) setBForm(f => ({...f, date: defaultDate})); }, [defaultDate]);
    // CAL_SLOT_CHOOSER_v1 -- von aussen (Kalender-Klick -> "Pause") vorausgefuellt oeffnen.
    // Zaehler triggerOpen inkrementiert bei jeder Auswahl; >0 = oeffnen + Werte uebernehmen.
    React.useEffect(() => {
        if (!triggerOpen) return;
        const p = triggerPrefill || {};
        const m = p.mode === "intern" ? "intern" : "pause";   // PAUSE_INTERN_SPLIT_v1 -- Kalender-Klick: Pause ODER Intern
        setBMode(m);
        setBForm(f => ({
            ...f,
            employeeId: p.employeeId != null ? String(p.employeeId) : f.employeeId,
            date: p.date || f.date,
            startTime: p.startTime || f.startTime,
            endTime: p.endTime || f.endTime,
            bType: m === "pause" ? "Pause" : "", notes: "", fullDay: false, recurrence: "none",   // INTERN_NO_DEFAULT_v1
        }));
        setShowBlocker(true);
    }, [triggerOpen]);
    // INTERNAL_APT_v1 -> PAUSE_INTERN_SPLIT_v1 -- Typen kommen aus BLOCKER_KINDS; "Intern" = alles ausser Pause
    const internTypes = BLOCKER_KINDS.filter(k => k.type !== "Pause").map(k => k.type);
    const bTypes = bMode === "pause" ? ["Pause"] : internTypes;
    const kindHint = (BLOCKER_KINDS.find(k => k.type === bForm.bType) || {}).hint;
    const openMode = (m) => {
        // BLOCKER_DATE_FROM_CALENDAR_v1 (Toby 14.09.) -- beim Oeffnen immer den im Kalender angezeigten Tag nehmen,
        // nicht das zuletzt benutzte Datum (Michi: Samstag-Urlaub landete auf dem Freitag -> Konfliktmeldung)
        setBMode(m);
        setBForm(f => ({...f, date: defaultDate || formatDate(new Date()), notes: "",
                        // INTERN_NO_DEFAULT_v1 (Toby 23.09.2026) -- keine Vorauswahl: vorher stand automatisch "Reserviert fuer Kundin"
                        // drin (Pflichtfeld Name) -> wirkte, als braeuchte jeder interne Termin einen Kundennamen, und wer die Art
                        // nicht umstellte, legte Meetings als Reservierung ab. Jetzt "– Art wählen –", Eintragen erinnert daran.
                        bType: m === "pause" ? "Pause" : ""}));
        setShowBlocker(true);
    };
    const addMin = (t, n) => { const p = String(t).split(":"); const m = Number(p[0]) * 60 + Number(p[1] || 0) + n;
        return String(Math.floor(m / 60) % 24).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0"); };
    const toM = t => { const p = String(t).split(":"); return Number(p[0]) * 60 + Number(p[1] || 0); };
    const dur = bForm.fullDay ? 720 : Math.max(toM(bForm.endTime) - toM(bForm.startTime), 15);

    // PAUSE_RECURRING_v1: Liste aller Datums-Strings vom Start- bis End-Datum gemaess recurrence
    const computeOccurrences = () => {
        const start = new Date(bForm.date + "T00:00:00");
        const until = new Date(bForm.untilDate + "T00:00:00");
        if (until < start) return [bForm.date];
        const dates = [];
        let cur = new Date(start);
        let safety = 0;
        while (cur <= until && safety < 366) {
            safety++;
            const dow = cur.getDay(); // 0 = So, 6 = Sa
            const dateStr = formatDate(cur);  // BLOCKER_SERIES_TZ_FIX_v1 -- lokal, passt jetzt auch zu getDay() fuer Sa/So
            // BLOCKER_WEEKEND_SKIP_DAILY_ONLY_v1 (14.09.) -- "Wochenenden ueberspringen" gilt nur fuer Taeglich (nur dort
            // sichtbar); vorher verwarf eine woechentliche/monatliche Serie ab Samstag still alle Eintraege ("Erstellt 0")
            const skipWE = bForm.recurrence === "daily" && bForm.skipWeekends && (dow === 0 || dow === 6);
            if (!skipWE) dates.push(dateStr);
            if (bForm.recurrence === "daily") cur.setDate(cur.getDate() + 1);
            else if (bForm.recurrence === "weekly") cur.setDate(cur.getDate() + 7);
            else if (bForm.recurrence === "monthly") cur.setMonth(cur.getMonth() + 1);
            else break;
        }
        return dates;
    };

    const handleBlocker = async () => {
        if (bForm.bType === "Urlaub") return; // VACATION_ENTITLEMENT_v1 -- Urlaub nur ueber den Schichtplan
        if (!bForm.employeeId) { alert("Bitte Mitarbeiter auswählen"); return; }
        if (bMode === "intern" && !bForm.bType) { alert("Bitte die Art wählen"); return; }   // INTERN_NO_DEFAULT_v1
        // PAUSE_INTERN_SPLIT_v1 -- eine Reservierung ohne Namen hilft niemandem
        if (bForm.bType === "Reserviert für Kundin" && !String(bForm.notes || "").trim()) { alert("Bitte den Namen der Kundin eintragen"); return; }
        setBusy(true);
        try {
            const dates = bForm.recurrence === "none" ? [bForm.date] : computeOccurrences();
            const st = bForm.fullDay ? "08:00" : bForm.startTime;
            const dur_min = bForm.fullDay ? 720 : dur;
            const notesBase = bForm.bType + (bForm.notes ? ": " + bForm.notes : "");
            const seriesNote = bForm.recurrence === "none" ? "" : ` [Serie: ${bForm.recurrence === "daily" ? "täglich" : bForm.recurrence === "weekly" ? "wöchentlich" : "monatlich"} bis ${bForm.untilDate}]`;
            // PAUSE_VACATION_SKIP_v1 -- Tage wo Mitarbeiter Urlaub hat (vacation_auto)
            // VORAB übersprungen statt 409-Konflikt. Saubere Meldung statt "Zeitkonflikt".
            const empId = Number(bForm.employeeId);
            const vacationDates = new Set(
              (appointments || [])
                .filter(a => a && a.source === "vacation_auto"
                          && Number(a.employeeId || a.employee_id) === empId)
                .map(a => a.date)
            );
            let ok = 0, conflicts = 0, fails = 0, vacationSkipped = 0;
            // BLOCKER_SHIFT_EXEMPT_v1 -- echte Server-Meldung merken statt pauschal "Zeitkonflikt"
            let lastConflictMsg = "";
            // BLOCKER_OVERLAP_CONFIRM_v1 (Toby 14.09.) -- Ueberschneidungen sammeln und wie im Buchungsfenster
            // nach Rueckfrage bewusst zulassen (Server: allow_overlap, MANUAL_OVERLAP_OK_v1). Vorher gab es fuer
            // Pause/Urlaub keinen Weg, an Tagen mit Terminen/Blockern einzutragen.
            const confirmable = [];
            const addToState = (a) => setAppointments(prev => [...prev, {
                id: a.id, customerId: a.customer_id, employeeId: a.employee_id,
                serviceIds: (a.services || []).map(s => s.id), date: a.date,
                startTime: a.start_time, totalDuration: a.total_duration,
                totalPrice: a.total_price, status: a.status, notes: a.notes,
                source: a.source,
            }]);
            for (let i = 0; i < dates.length; i++) {
                setProgress(`${i+1}/${dates.length}`);
                const d = dates[i];
                if (vacationDates.has(d)) {
                    vacationSkipped++;
                    continue;
                }
                const payload = {
                    employee_id: empId, date: d, start_time: st,
                    total_duration: dur_min, total_price: 0, source: "blocker",
                    status: "confirmed", notes: notesBase + seriesNote, service_ids: []
                };
                const res = await api.createAppointment(payload);
                if (res.status === 409) {
                    lastConflictMsg = (res.data && res.data.error) || lastConflictMsg;
                    if (res.data && res.data.overlap_confirmable) confirmable.push(payload); else conflicts++;
                    continue;
                }
                if (res.ok && res.data) { addToState(res.data); ok++; } else { fails++; }
            }
            if (confirmable.length) {
                const _q = dates.length > 1
                    ? `${confirmable.length} von ${dates.length} Einträgen ${confirmable.length === 1 ? "überschneidet" : "überschneiden"} sich mit bestehenden Terminen oder Blockern.\n\nTrotzdem alle eintragen? Die Einträge überschneiden sich dann bewusst.`
                    : (lastConflictMsg || "Der Mitarbeiter hat in diesem Zeitraum bereits einen Termin.") + "\n\nTrotzdem eintragen? Die Einträge überschneiden sich dann bewusst.";
                if (window.confirm(_q)) {
                    for (let i = 0; i < confirmable.length; i++) {
                        setProgress(`${i+1}/${confirmable.length}`);
                        const res2 = await api.createAppointment({ ...confirmable[i], allow_overlap: true });
                        if (res2.ok && res2.data) { addToState(res2.data); ok++; }
                        else if (res2.status === 409) { conflicts++; lastConflictMsg = (res2.data && res2.data.error) || lastConflictMsg; }
                        else { fails++; }
                    }
                } else {
                    conflicts += confirmable.length;
                }
            }
            setProgress(null);
            if (dates.length > 1) {
                const parts = [`${ok} Termine OK`];
                if (vacationSkipped) parts.push(`${vacationSkipped} in Urlaub übersprungen`);
                if (conflicts) parts.push(`${conflicts} mit Konflikt übersprungen`);
                if (fails) parts.push(`${fails} Fehler`);
                alert(`Serie eingetragen: ${parts.join(", ")}.`);
            } else if (vacationSkipped) {
                alert("Dieser Tag ist bereits als Urlaub eingetragen — keine zusätzliche Pause nötig.");
            } else if (conflicts && !ok) {
                alert((lastConflictMsg || "Zeitkonflikt") + " — nicht eingetragen.");
            } else if (fails) {
                alert("Termin konnte nicht eingetragen werden.");
            }
            setShowBlocker(false);
            setBForm(f => ({...f, employeeId: "", notes: "", fullDay: false, recurrence: "none"}));
        } finally {
            setBusy(false);
        }
    };
    const onClose = () => { if (!busy) setShowBlocker(false); };
    const empOpts = [{value:"",label:"-- Mitarbeiter --"}].concat((employees||[]).map(e => ({value:String(e.id),label:e.name})));
    const iS = {width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid #ddd",fontSize:14,boxSizing:"border-box"};
    const previewCount = bForm.recurrence === "none" ? 1 : computeOccurrences().length;
    return (<>
        {/* PAUSE_INTERN_SPLIT_v1 -- zwei Knoepfe, ein Fenster (vorher ein Knopf "Pause / Intern") */}
        <span style={{display:"inline-flex",gap:8,marginLeft:8,flexShrink:0}}>{/* als Gruppe: bleiben auch beim Umbruch (Handy) nebeneinander */}
            <Btn onClick={() => openMode("pause")} size={btnSize} variant="ghost" style={QUIET_BTN_STYLE} title="Pause eintragen">⏸ Pause</Btn>
            <Btn onClick={() => openMode("intern")} size={btnSize} variant="ghost" style={QUIET_BTN_STYLE} title="Reserviert, außer Haus, Meeting, Schulung, Krank …">Intern</Btn>
        </span>
        {showBlocker && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={onClose}>
            <div style={{background:"var(--card)",borderRadius:16,padding:24,width:Math.min(480,window.innerWidth-32),maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
                <h3 style={{margin:"0 0 16px",fontSize:18}}>{bMode === "pause" ? "⏸ Pause eintragen" : "Interner Termin eintragen"}</h3>
                <Select label="Mitarbeiter" options={empOpts} value={bForm.employeeId} onChange={e=>setBForm(f=>({...f,employeeId:e.target.value}))} />
                {bMode === "intern" && <div style={{marginBottom:14}}><label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:14}}>Art</label><select style={iS} value={bForm.bType} onChange={e=>setBForm(f=>({...f,bType:e.target.value}))}><option value="">– Art wählen –</option>{bTypes.map(t=><option key={t} value={t}>{(BLOCKER_KINDS.find(k => k.type === t) || {}).icon} {t}</option>)}</select></div>}
                {/* VACATION_ENTITLEMENT_v1 (Toby 22.09.: „wie empfohlen“) -- Urlaub NICHT mehr als Kalender-Blocker: der liefe am
                    Urlaubsanspruch vorbei. Hinweis + Sprung zum Schichtplan; vorhandene Blocker bleiben unberuehrt. */}
                {bForm.bType === "Urlaub" && (
                  <div data-feature="VACATION_ENTITLEMENT_v1" style={{marginBottom:14,padding:"10px 12px",borderRadius:10,background:`${COLORS.warning}14`,border:`1px solid ${COLORS.warning}55`,fontSize:13,lineHeight:1.55}}>
                    Urlaub bitte im <strong>Schichtplan</strong> eintragen – dort wird er vom Urlaubsanspruch abgezogen und blockt den Kalender automatisch.
                    {onGoToShifts && <div style={{marginTop:8}}><Btn size="sm" onClick={() => { onClose(); onGoToShifts(); }}>Zum Schichtplan</Btn></div>}
                  </div>
                )}
                <div style={{marginBottom:14}}><label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:14}}>Datum (Start)</label><input type="date" style={iS} value={bForm.date} onChange={e=>setBForm(f=>({...f,date:e.target.value}))} /></div>
                <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,cursor:"pointer"}}><input type="checkbox" checked={bForm.fullDay} onChange={e=>setBForm(f=>({...f,fullDay:e.target.checked}))} /> <span style={{fontSize:14}}>Ganzer Tag</span></label>
                {!bForm.fullDay && <><div style={{marginBottom:14}}><label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:14}}>Von</label><TimeCombo value={bForm.startTime} onChange={v=>setBForm(f=>({...f,startTime:v}))} style={iS} /></div><div style={{marginBottom:14}}><label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:14}}>Bis</label><TimeCombo value={bForm.endTime} onChange={v=>setBForm(f=>({...f,endTime:v}))} style={iS} /></div><div style={{fontSize:13,color:"#666",marginBottom:14}}>Dauer: {dur} Min.</div></>}
                {/* PAUSE_INTERN_SPLIT_v1 -- Pause: Schnellknoepfe (2/3 aller Pausen sind 15/30/45/60 Min), Ende rechnet sich selbst */}
                {bMode === "pause" && !bForm.fullDay && (
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:-6,marginBottom:14}}>
                        {[15, 30, 45, 60].map(n => {
                            const act = bForm.endTime === addMin(bForm.startTime, n);
                            return <button key={n} type="button" onClick={() => setBForm(f => ({...f, endTime: addMin(f.startTime, n)}))}
                                style={{padding:"6px 12px",borderRadius:16,fontSize:12.5,cursor:"pointer",background: act ? COLORS.primary : "var(--card)", color: act ? "white" : COLORS.text, border: `1px solid ${act ? COLORS.primary : COLORS.border}`}}>{n} Min</button>;
                        })}
                    </div>
                )}
                {/* PAUSE_RECURRING_v1 -- Recurrence-Pickr */}
                <div style={{marginBottom: 14, padding: 12, background: "var(--cardHover)", borderRadius: 10, border: `1px solid ${COLORS.border}`}}>
                    <label style={{display:"block",marginBottom:8,fontWeight:600,fontSize:13}}>🔁 Wiederholung</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom: bForm.recurrence === "none" ? 0 : 12}}>
                        {[
                            {k:"none",     l:"Einmalig"},
                            {k:"daily",    l:"Täglich"},
                            {k:"weekly",   l:"Wöchentlich"},
                            {k:"monthly",  l:"Monatlich"},
                        ].map(o => {
                            const act = bForm.recurrence === o.k;
                            return <button key={o.k} onClick={() => setBForm(f => ({...f, recurrence: o.k}))} style={{padding:"6px 12px",borderRadius:16,fontSize:12.5,cursor:"pointer",background: act ? COLORS.primary : "var(--card)", color: act ? "white" : COLORS.text, border: `1px solid ${act ? COLORS.primary : COLORS.border}`, fontWeight: act ? 700 : 500}}>{o.l}</button>;
                        })}
                    </div>
                    {bForm.recurrence !== "none" && <>
                        <div style={{marginBottom:10}}>
                            <label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:13}}>Bis Datum</label>
                            <input type="date" style={iS} value={bForm.untilDate} onChange={e=>setBForm(f=>({...f,untilDate:e.target.value}))} />
                        </div>
                        {bForm.recurrence === "daily" && (
                            <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,cursor:"pointer"}}>
                                <input type="checkbox" checked={bForm.skipWeekends} onChange={e=>setBForm(f=>({...f,skipWeekends:e.target.checked}))} />
                                Wochenenden überspringen (Sa/So)
                            </label>
                        )}
                        <div style={{fontSize:12,color:COLORS.textLight,marginTop:8}}>→ Erstellt {previewCount} Termin{previewCount === 1 ? "" : "e"}</div>
                    </>}
                </div>
                <div style={{marginBottom:14}}><label style={{display:"block",marginBottom:4,fontWeight:500,fontSize:14}}>{bForm.bType === "Reserviert für Kundin" ? "Name der Kundin" : "Notiz (optional)"}</label><input type="text" style={iS} value={bForm.notes} placeholder={bMode === "pause" ? "z.B. Mittagspause" : (kindHint || "optional")} onChange={e=>setBForm(f=>({...f,notes:e.target.value}))} /></div>
                {progress && <div style={{padding:"8px 12px",background:`${COLORS.primary}15`,borderRadius:8,fontSize:13,marginBottom:10,fontWeight:600,color:COLORS.primary}}>⏳ Eintragen läuft: {progress}</div>}
                <div style={{display:"flex",gap:8,marginTop:16}}><Btn style={{flex:1,background:"#9e9e9e"}} onClick={onClose} disabled={busy}>Abbrechen</Btn><Btn style={{flex:1}} onClick={handleBlocker} disabled={busy || bForm.bType === "Urlaub"}>{busy ? "Eintragen..." : `${previewCount > 1 ? previewCount + "× " : ""}Eintragen`}</Btn></div>
            </div>
        </div>
        )}
    </>);
};

// BOOKING_MODAL_v2 -- Kunden-Suche, Wiederbesuchs-Schnellwahl, Service-Kategorien, "letztes Mal"-Uebernahme
export const BookingModal = ({ open, onClose, employees, customers, setCustomers, services, appointments, setAppointments, defaultDate, preselectCustomerId, startInNewCustomerMode, preselectEmployeeId, preselectStartTime }) => {
  const [form, setForm] = useState({ customerId: preselectCustomerId || "", employeeId: preselectEmployeeId ? String(preselectEmployeeId) : "", serviceIds: [], date: defaultDate || formatDate(today), startTime: preselectStartTime || "09:00", hairLength: "", hairLong: false, hairThick: false });
  const [step, setStep] = useState(1);
  // BOOKING_MODAL_v2 -- Kunden-Suche
  const [custQuery, setCustQuery] = useState("");
  // CAL_NEW_CUST_QUICK_BOOK_v1 -- Inline-Neukunden-Anlage statt Picker
  const [newCustMode, setNewCustMode] = useState(false);
  const [newCustForm, setNewCustForm] = useState({ firstName: "", lastName: "", phone: "", email: "", notes: "" });
  const [newCustSaving, setNewCustSaving] = useState(false);
  const [newCustError, setNewCustError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        customerId: preselectCustomerId || "",
        employeeId: preselectEmployeeId ? String(preselectEmployeeId) : "",
        serviceIds: [],
        date: defaultDate || formatDate(today),
        startTime: preselectStartTime || "09:00",
        hairLength: "", hairLong: false, hairThick: false,
      });
      setStep(1);
      setCustQuery("");
      // CAL_NEW_CUST_QUICK_BOOK_v1 -- starte direkt im Neukunde-Modus wenn Toby/Julia "Neukunde + Termin" geklickt hat
      setNewCustMode(!!startInNewCustomerMode && !preselectCustomerId);
      setNewCustForm({ firstName: "", lastName: "", phone: "", email: "", notes: "" }); /* CUST_NOTE_NEWCUST_v1 -- notes mit zuruecksetzen, sonst wird das Feld beim 2. Oeffnen uncontrolled */
      setNewCustError("");
    }
  }, [open, defaultDate, preselectCustomerId, startInNewCustomerMode, preselectEmployeeId, preselectStartTime]);

  // CAL_NEW_CUST_QUICK_BOOK_v1 -- Neukunden anlegen + direkt als selected setzen
  const saveNewCustomer = async () => {
    const fn = (newCustForm.firstName || "").trim();
    const ln = (newCustForm.lastName || "").trim();
    const phone = (newCustForm.phone || "").trim();
    if (!fn || !ln) { setNewCustError("Vor- und Nachname sind Pflicht"); return; }
    if (!phone) { setNewCustError("Telefonnummer ist Pflicht"); return; }
    setNewCustError("");
    setNewCustSaving(true);
    try {
      const fullName = `${fn} ${ln}`;
      const res = await api.createCustomer({
        name: fullName, phone: phone, email: (newCustForm.email || "").trim(),
        customer_type: "laufkunde", sms_consent: 1, notes: (newCustForm.notes || "").trim(), /* CUST_NOTE_NEWCUST_v1 */
      });
      if (res && res.ok) {
        const data = res.data || res;
        const newId = data.id || Math.max(0, ...customers.map(c => c.id)) + 1;
        const newC = { id: newId, name: fullName, phone, email: (newCustForm.email||"").trim(),
                       customerType: "laufkunde", visits: 0, totalSpent: 0, noShows: 0, lastVisit: "", smsConsent: true,
                       notes: (newCustForm.notes || "").trim() /* CUST_NOTE_NEWCUST_v1 */ };
        if (setCustomers) setCustomers(prev => [...prev, newC]);
        setForm(f => ({ ...f, customerId: newId }));
        setNewCustMode(false);
      } else {
        setNewCustError("Speichern fehlgeschlagen: " + ((res && res.data && res.data.error) || "unbekannt"));
      }
    } catch (e) {
      setNewCustError("Netzwerk-Fehler: " + (e && e.message ? e.message : "unbekannt"));
    }
    setNewCustSaving(false);
  };

  // BOOKING_MODAL_v2 -- gefilterte Kunden (max 20 Treffer)
  // CUSTSEARCH_EMPTY_v1 -- bei leerer Suche KEINE Kundenliste mehr vorab zeigen (Toby: "nicht gleich
  // so, alle suchen ihre Kunden ueber die Suche"). Erst tippen -> dann Treffer.
  const filteredCustomers = React.useMemo(() => {
    // CUSTSEARCH_RANK_v1 (Toby 29.07.) -- Treffer nach Relevanz statt nur alphabetisch:
    // Namens-Anfang vor Wort-Anfang vor Teilstring (vorher stand bei Suche „gitt" z.B.
    // „Brigitte" vor „Gitta"). Plus Umlaut-Toleranz in beide Richtungen:
    // „Müller" wird mit „müller", „mueller" UND „muller" gefunden.
    const raw = (custQuery || "").trim().toLowerCase();
    if (!raw) return [];
    const fold = s => String(s || "").toLowerCase()
      .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");
    const deacc = s => String(s || "").toLowerCase().normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "").replace(/ß/g, "ss");
    const q1 = fold(raw), q2 = deacc(raw);
    const scored = [];
    customers.forEach(c => {
      const n1 = fold(c.name), n2 = deacc(c.name);
      const p = String(c.phone || "").toLowerCase();
      const e1 = fold(c.email), e2 = deacc(c.email);
      let s = -1;
      if (n1.startsWith(q1) || n2.startsWith(q2)) s = 0;
      else if (n1.split(/[\s\-.,]+/).some(w => w.startsWith(q1)) || n2.split(/[\s\-.,]+/).some(w => w.startsWith(q2))) s = 1;
      else if (n1.includes(q1) || n2.includes(q2)) s = 2;
      else if (p.includes(raw) || e1.includes(q1) || e2.includes(q2)) s = 3;
      if (s >= 0) scored.push([s, c]);
    });
    scored.sort((a, b) => a[0] - b[0] || String(a[1].name || "").localeCompare(String(b[1].name || ""), "de"));
    return scored.slice(0, 20).map(x => x[1]);
  }, [customers, custQuery]);
  const selectedCustomer = customers.find(c => c.id === Number(form.customerId));
  // CUST_NOTE_BOOKING_v1 -- dauerhafte Kunden-Notiz (z.B. "kommt immer 10 Min zu spaet") im Buchungs-
  // Dialog anzeigen + bearbeiten. Wird am KUNDEN gespeichert -> erscheint bei jedem Termin dieses Kunden.
  const [custNote, setCustNote] = useState("");
  useEffect(() => { setCustNote(selectedCustomer ? (selectedCustomer.notes || "") : ""); }, [form.customerId, selectedCustomer && selectedCustomer.notes]);
  // APT_NOTE_ONCE_v1 (Toby 07.10.2026: „go, nur dieser Termin als Standard“) -- Umschalter über dem Notizfeld:
  // „Nur dieser Termin“ (Standard) speichert am Termin, „Dauerhaft“ wie bisher am Kunden. Beide Texte bleiben beim Umschalten erhalten.
  const [noteMode, setNoteMode] = useState("once");
  const [onceNote, setOnceNote] = useState("");
  useEffect(() => { if (open) { setNoteMode("once"); setOnceNote(""); } }, [open]);

  // BOOKING_MODAL_v2 -- Services nach Kategorie gruppieren
  const servicesByCategory = React.useMemo(() => {
    const groups = {};
    (services || []).filter(s => s.is_active !== 0 && s.is_active !== false).forEach(s => {
      const cat = s.category || "Sonstiges";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    // Stabile Reihenfolge: Damen → Herren → Kinder → andere alphabetisch (BUSINESS_TYPE_v2: je Branche)
    const order = isKosmetikSalon()
      ? ["Gesicht", "Körper", "Hände & Nägel", "Füße", "Wimpern & Brauen", "Haarentfernung", "Sonstiges"]
      : ["Damen", "Farbe", "Schnitt", "Herren", "Kinder", "Pflege", "Styling", "Kosmetik"]; // SERVICE_AUDIENCE_COLORS_v1: Farbe zaehlt als Damen -> direkt dahinter
    const cats = Object.keys(groups).sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return { groups, cats };
  }, [services]);

  // BOOKING_MODAL_v2 -- letzten Termin des Kunden finden (fuer "wie letztes Mal")
  // BOOKING_LASTAPPT_FIX_v1 -- nur VERGANGENE Termine zaehlen (vorher zeigte ein zukuenftiger
  // Termin faelschlich als "letzter Termin"). Datum strikt vor heute.
  const lastAppointment = React.useMemo(() => {
    if (!form.customerId) return null;
    const cid = Number(form.customerId);
    const _todayStr = formatDate(today);
    const past = (appointments || [])
      .filter(a => Number(a.customerId || a.customer_id) === cid && a.status !== "cancelled" && String(a.date || "") < _todayStr)
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    return past[0] || null;
  }, [appointments, form.customerId]);

  const applyLastAppointment = () => {
    if (!lastAppointment) return;
    const ids = (lastAppointment.serviceIds || lastAppointment.service_ids || []).map(Number);
    const empId = lastAppointment.employeeId || lastAppointment.employee_id;
    setForm(f => ({
      ...f,
      serviceIds: ids,
      employeeId: empId ? String(empId) : f.employeeId,
    }));
  };

  // BOOKING_MODAL_v2 -- Wiederbesuchs-Schnellwahl
  const setFollowUpDate = (weeks) => {
    const d = new Date();
    d.setDate(d.getDate() + (weeks * 7));
    setForm(f => ({ ...f, date: formatDate(d) }));
  };

  // RECURRING_BOOKING_v1 (3.5) -- Serien-Termine
  const [recurring, setRecurring] = useState({ enabled: false, weeks: 4, count: 6 });
  // SERIES_INPUT_FIX_v1 (Tamara 21.09.2026: aus "1" + "3" wurde 13, die 1 liess sich nicht loeschen) --
  // Felder duerfen waehrend des Tippens leer sein; Grenzen (Wochen 1-26, Anzahl 2-26) erst beim Verlassen/Buchen.
  const normRecurring = (r) => ({ ...r,
    weeks: Math.max(1, Math.min(26, parseInt(r.weeks, 10) || 1)),
    count: Math.max(2, Math.min(26, parseInt(r.count, 10) || 2)) });
  // SERIES_PREVIEW_v1 -- Datumsliste der Serie ab dem gewaehlten Datum (gleiche Rechnung wie beim Buchen)
  const seriesDates = (startDs, r) => {
    if (!startDs) return [];
    const n = normRecurring(r); const base = new Date(startDs); const out = [];
    for (let i = 0; i < n.count; i++) { const d = new Date(base); d.setDate(d.getDate() + i * n.weeks * 7); out.push(d); }
    return out;
  };
  // RECUR_SERIES_v1 -- nach Serienbuchung: Übersicht (Drucken/Kopieren) zeigen, onClose erst danach
  const [seriesResult, setSeriesResult] = useState(null);

  // TIER_DURATION_ALLPATHS_v1 -- Stufen-Dauern fuer den Kalender.
  // Bisher rechnete der manuelle Termin IMMER mit der Dauer der Dienstleistung, waehrend
  // Online-Formular/Zoey/Voice-KI die Dauer der Stufe nehmen -- derselbe Schnitt bekam je
  // nach Buchungsweg eine andere Laenge. Quelle ist der bestehende Endpunkt /pricing/matrix
  // (Stufen + Mitarbeiter-Zuordnung + duration_min), kein neuer Backend-Weg.
  // Mitarbeiter-Einzelpreise (service_employee_prices) bleiben aussen vor: Tabelle ist leer,
  // die Oberflaeche dafuer gibt es nicht. Wird sie eingefuehrt, gehoert sie hier mit rein.
  const [tierDurations, setTierDurations] = useState(null); // { empTier: {empId: tierId}, dur: {"svcId:tierId": min} }
  useEffect(() => {
    let cancelled = false;
    api.request("/pricing/matrix")
      .then(r => {
        if (cancelled || !r || !r.ok || !r.data) return;
        const empTier = {};
        (r.data.employees || []).forEach(e => { if (e.pricing_tier_id) empTier[String(e.id)] = Number(e.pricing_tier_id); });
        const dur = {};
        const price = {};   // TIER_PRICE_MANUAL_v1 -- Stufenpreise je Leistung
        (r.data.prices || []).forEach(p => {
          if (p.duration_min != null) dur[String(p.service_id) + ":" + String(p.tier_id)] = Number(p.duration_min);
          if (p.price_eur != null) price[String(p.service_id) + ":" + String(p.tier_id)] = Number(p.price_eur);
        });
        setTierDurations({ empTier, dur, price });
      })
      .catch(() => { /* defensiv: ohne Matrix bleibt es bei der Dauer der Dienstleistung */ });
    return () => { cancelled = true; };
  }, []);

  // Dauer EINER Leistung fuer den gewaehlten Mitarbeiter: Stufe wenn hinterlegt, sonst Dienstleistung.
  const durationForService = (svc, empId) => {
    const base = calcDynamicDuration(svc, { longHair: form.hairLong, thickHair: form.hairThick });
    if (!svc || !empId || !tierDurations) return base;
    const tierId = tierDurations.empTier[String(empId)];
    if (!tierId) return base;
    const tierDur = tierDurations.dur[String(svc.id) + ":" + String(tierId)];
    return (tierDur > 0) ? tierDur : base;
  };

  const selectedServices = form.serviceIds.map(id => services.find(s => s.id === id)).filter(Boolean);
  const selectedEmp = employees.find(e => e.id === Number(form.employeeId));
  const isWeekend = (() => { const d = new Date(form.date); return d.getDay() === 0 || d.getDay() === 6; })();

  // TIER_PRICE_MANUAL_v1 (Toby 08.10.2026) -- Preis der Stufe des gewaehlten Mitarbeiters (Preis-Matrix), sonst wie bisher.
  // Der Server setzt beim Speichern denselben Preis (pricing.resolve_price); hier nur, damit er vorher schon stimmt.
  const priceForService = (svc, emp) => {
    const base = calcDynamicPrice(svc, emp, isWeekend);
    if (!svc || !emp || !tierDurations || !tierDurations.price) return base;
    const tierId = tierDurations.empTier[String(emp.id)];
    if (!tierId) return base;
    const tp = tierDurations.price[String(svc.id) + ":" + String(tierId)];
    return (tp != null && !isNaN(tp)) ? tp : base;
  };
  const totalPrice = selectedServices.reduce((s, svc) => s + priceForService(svc, selectedEmp), 0);
  // CUSTOMER_PREFERRED_DURATION_v1 -- aus Termin-Historie pro Kunde+Service die "erfahrene" Dauer holen
  const [preferredDurations, setPreferredDurations] = useState({}); // { [svcId]: {avg,median,last,suggest,count,history} }
  useEffect(() => {
    if (!form.customerId || !form.serviceIds.length) { setPreferredDurations({}); return; }
    let cancelled = false;
    const ids = form.serviceIds.map(Number).filter(Boolean).join(",");
    api.request(`/customers/${form.customerId}/preferred-durations?service_ids=${ids}`)
      .then(r => { if (!cancelled && r && r.ok && r.data) setPreferredDurations(r.data || {}); })
      .catch(() => { if (!cancelled) setPreferredDurations({}); });
    return () => { cancelled = true; };
  }, [form.customerId, form.serviceIds.join(",")]);
  const hasPreferred = Object.keys(preferredDurations).length > 0;
  // Vorschlag: Summe der Median-Dauern für die ausgewählten Services
  const preferredTotalDuration = form.serviceIds.reduce((s, sid) => {
    const pref = preferredDurations[String(sid)];
    if (pref && pref.suggest) return s + Number(pref.suggest);
    const svc = services.find(x => x.id === sid);
    // TIER_DURATION_ALLPATHS_v1 -- Fallback ohne Kundenhistorie: Dauer der Stufe beachten.
    // Die Historie des Kunden bleibt bewusst vorrangig (CUSTOMER_PREFERRED_DURATION_v1 ist
    // spezifischer: dieser Kunde bei dieser Leistung).
    return s + (svc ? durationForService(svc, form.employeeId) : 0);
  }, 0);
  const calcTotal = selectedServices.reduce((s, svc) => s + durationForService(svc, form.employeeId), 0);
  // Wenn historische Daten vorhanden + User hat noch nicht manuell geändert → nimm die historische Dauer
  const totalDuration = hasPreferred ? preferredTotalDuration : calcTotal;

        // SLOT_BUSY_API_v1 - phase-aware busy intervals from backend (Single Source of Truth)
        const [phaseAwareBusy, setPhaseAwareBusy] = useState(null);
        // SHIFT_BREAK_BOOK_v1 (Toby 29.09.2026, Meldung Hamza) -- Pause aus dem Schichtplan getrennt merken:
        // im Uhrzeit-Menue "(Pause)" statt "(belegt)" und waehlbar; beim Speichern fragt der Server nach.
        const [shiftBreaks, setShiftBreaks] = useState([]);
        useEffect(() => {
            if (!form.employeeId || !form.date) { setPhaseAwareBusy(null); setShiftBreaks([]); return; }
            let cancelled = false;
            fetch("/api/employees/" + form.employeeId + "/busy-intervals?date=" + encodeURIComponent(form.date), {headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") }})
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    if (cancelled) return;
                    if (d && Array.isArray(d.busy)) {
                        // Server mit SHIFT_BREAK_BOOK_v1: appt_busy = belegt OHNE Pause, breaks = Pause.
                        // Aelterer Server: nur busy (Pause steckt drin und bleibt "belegt").
                        const typed = Array.isArray(d.appt_busy) && Array.isArray(d.breaks);
                        setPhaseAwareBusy(typed ? d.appt_busy : d.busy);
                        setShiftBreaks(typed ? d.breaks : []);
                    } else { setPhaseAwareBusy(null); setShiftBreaks([]); }
                })
                .catch(() => { if (!cancelled) { setPhaseAwareBusy(null); setShiftBreaks([]); } });
            return () => { cancelled = true; };
        }, [form.employeeId, form.date]);

        const busySlots = (appointments || [])
            .filter(a => a && String(a.employeeId || a.employee_id) === String(form.employeeId) && a.date === form.date && a.status !== 'cancelled')
            .map(a => ({ start: String(a.startTime || a.start_time || '00:00').slice(0,5), dur: Number(a.totalDuration || a.total_duration) || 0 }));
        const slotToM = t => { const p = String(t).split(':'); return Number(p[0])*60+Number(p[1]||0); };
        const isSlotBusy = (time) => {
            if (!form.employeeId) return false;
            const dur = totalDuration || 15;
            const toM = slotToM;
            const ns = toM(time), ne = ns + dur;
            // SLOT_BUSY_API_v1: prefer phase-aware intervals from backend, defensiver Fallback auf alte Client-Logik
            if (Array.isArray(phaseAwareBusy)) {
                return phaseAwareBusy.some(b => { const bs = toM(b.start), be = toM(b.end); return ns < be && bs < ne; });
            }
            return busySlots.some(b => { const bs = toM(b.start), be = bs + b.dur; return ns < be && bs < ne; });
        };
        // SHIFT_BREAK_BOOK_v1 -- reicht der Termin in die Pause aus dem Schichtplan?
        const isSlotBreak = (time) => {
            if (!form.employeeId || !shiftBreaks.length) return false;
            const ns = slotToM(time), ne = ns + (totalDuration || 15);
            return shiftBreaks.some(b => { const bs = slotToM(b.start), be = slotToM(b.end); return ns < be && bs < ne; });
        };

  const handleBook = async () => {
      const recN = normRecurring(recurring); // SERIES_INPUT_FIX_v1
      try {
          // HAIR_LENGTH_PICKER_v1 -- Haarlaenge in Notes
          const _hairLabels = { kurz: "Kurz", mittel: "Mittel", lang: "Lang", sehr_lang: "Sehr lang" };
          const _hairNote = form.hairLength ? "Haarlänge: " + _hairLabels[form.hairLength] : "";
          const _thickNote = form.hairThick ? "dickes Haar" : "";
          const _seriesNote = recN.enabled ? `Serie: alle ${recN.weeks} Wochen, ${recN.count} Termine` : "";
          const _allNotes = [String(onceNote || "").trim(), _hairNote, _thickNote, _seriesNote].filter(Boolean).join(" | "); // APT_NOTE_ONCE_v1
          // RECURRING_BOOKING_v1 (3.5): bei Serien zuerst Master-Termin anlegen, dann N-1 Folgetermine
          const basePayload = {
              customer_id: Number(form.customerId),
              employee_id: Number(form.employeeId),
              service_ids: form.serviceIds.map(Number),
              date: form.date,
              start_time: form.startTime,
              total_duration: totalDuration,
              total_price: Math.round(totalPrice),
              status: "confirmed",
              notes: _allNotes,
              source: "manual",
          };
          if (recN.enabled) {
              basePayload.recurring_interval_weeks = Number(recN.weeks);
          }
          let res = await api.createAppointment(basePayload);
          // SHIFT_BREAK_BOOK_v1 (Toby 29.09.2026) -- Zeit liegt nur in der Pause aus dem Schichtplan:
          // eigene Rückfrage, dann gibt allow_break NUR die Pause frei (echte Termine bleiben geschützt).
          // Gilt nach dem Ja auch für die Folgetermine einer Serie (gleicher Wochentag, gleiche Pause).
          let breakOk = false;
          if (res && res.status === 409 && res.data && res.data.reason === "break") {
              if (!window.confirm((res.data.error || "Die Uhrzeit liegt in der Pause laut Schichtplan.") + "\n\nTrotzdem eintragen?")) return;
              breakOk = true;
              res = await api.createAppointment({ ...basePayload, allow_break: true });
          }
          // MANUAL_OVERLAP_OK_v1 (Toby 29.07.) -- Konflikt: Salon darf nach Bestätigung bewusst überlappen
          if (res && res.status === 409 && res.data && res.data.overlap_confirmable) {
              if (window.confirm((res.data.error || "Der Mitarbeiter hat in diesem Zeitraum bereits einen Termin.") + "\n\nTrotzdem eintragen? Die Termine überschneiden sich dann bewusst.")) {
                  res = await api.createAppointment({ ...basePayload, allow_overlap: true, ...(breakOk ? { allow_break: true } : {}) });
              }
          }
          if (res.status === 409) { const errData = res.data || {}; alert(errData.error || 'Zeitkonflikt: Mitarbeiter hat bereits einen Termin!'); return; }
                if (res.ok) {
              const data = res.data || res;
              // MANUAL_SHIFT_HINT_v1 -- Buchung außerhalb der Schicht geht durch, Server schickt nur noch einen Hinweis
              if (data.shift_hint) alert(data.shift_hint);
              const newApt = {
                  id: data.id || Math.max(0, ...appointments.map(a => a.id)) + 1,
                  customerId: Number(form.customerId),
                  employeeId: Number(form.employeeId),
                  serviceIds: form.serviceIds.map(Number),
                  date: form.date,
                  startTime: form.startTime,
                  totalDuration,
                  totalPrice: Math.round(totalPrice),
                  status: "confirmed",
                  notes: _allNotes,   // APT_NOTE_ONCE_v1 -- 💬 sofort an der Kachel, nicht erst nach dem Neuladen
                  source: "manual",
                  paymentStatus: "pending",
                  paymentMethod: null,
              };
              setAppointments(prev => [...prev, newApt]);

              // CUST_NOTE_BOOKING_v1 -- geaenderte Kunden-Notiz am KUNDEN speichern (persistent, alle Termine)
              if (selectedCustomer && custNote !== (selectedCustomer.notes || "")) {
                try {
                  await api.updateCustomer(selectedCustomer.id, { notes: custNote });
                  if (setCustomers) setCustomers(prev => prev.map(c => c.id === selectedCustomer.id ? { ...c, notes: custNote } : c));
                } catch (_) {}
              }

              // RECURRING_BOOKING_v1 (3.5): N-1 Folgetermine anlegen
              // RECUR_SERIES_v1 -- Ergebnisse je Termin sammeln fuer die Kunden-Übersicht
              const _seriesItems = [{ date: form.date, ok: true }];
              if (recN.enabled && Number(recN.count) > 1) {
                  const parentId = newApt.id;
                  const baseDate = new Date(form.date);
                  let okFollow = 0, failFollow = 0;
                  for (let i = 1; i < Number(recN.count); i++) {
                      const d = new Date(baseDate);
                      d.setDate(d.getDate() + i * Number(recN.weeks) * 7);
                      const followPayload = { ...basePayload,
                          ...(breakOk ? { allow_break: true } : {}),  // SHIFT_BREAK_BOOK_v1
                          date: formatDate(d),
                          recurring_parent_id: parentId,
                          notes: `Serie #${i+1}/${recN.count} (alle ${recN.weeks} Wochen)`,
                      };
                      try {
                          const fr = await api.createAppointment(followPayload);
                          if (fr && fr.ok) {
                              okFollow++;
                              _seriesItems.push({ date: followPayload.date, ok: true });
                              const fd = fr.data || {};
                              setAppointments(prev => [...prev, {
                                  id: fd.id || (Math.max(0, ...prev.map(a => a.id)) + 1),
                                  customerId: Number(form.customerId),
                                  employeeId: Number(form.employeeId),
                                  serviceIds: form.serviceIds.map(Number),
                                  date: followPayload.date,
                                  startTime: form.startTime,
                                  totalDuration,
                                  totalPrice: Math.round(totalPrice),
                                  status: "confirmed",
                                  notes: followPayload.notes,
                                  paymentStatus: "pending",
                                  paymentMethod: null,
                                  recurringParentId: parentId,
                              }]);
                          } else {
                              failFollow++;
                              _seriesItems.push({ date: followPayload.date, ok: false, error: (fr && fr.data && fr.data.error) || "" });
                          }
                      } catch { failFollow++; _seriesItems.push({ date: followPayload.date, ok: false, error: "Netzwerkfehler" }); }
                  }
              }
              // CAL_INSTANT_REFRESH_v1 -- Phasen-Children (Einwirkzeit) erzeugt der Server; sofort
              // neu laden, damit sie nicht erst beim 45s-Poll im Kalender erscheinen.
              try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
              // RECUR_SERIES_v1 -- bei Serien: Übersicht zum Drucken/Kopieren zeigen (ersetzt die
              // alte Fehl-Kurzmeldung); Modal schliesst erst nach der Übersicht. Sonst wie bisher.
              if (recN.enabled && _seriesItems.length > 1) {
                  setSeriesResult({
                      customerName: (selectedCustomer && selectedCustomer.name) || (customers.find(c => c.id === Number(form.customerId)) || {}).name || "",
                      employeeName: (selectedEmp && selectedEmp.name) || "",
                      serviceNames: selectedServices.map(s => s.name).join(", "),
                      startTime: String(form.startTime).slice(0, 5),
                      items: _seriesItems,
                  });
              } else {
                  onClose();
              }
          } else {
              // APPT_SAVE_ERRMSG_FIX_v1 -- Fehlertext liegt in res.data.error (api.request-Form);
              // res.error gab es nie -> zeigte immer nur "Unbekannter Fehler"
              alert("Fehler beim Speichern: " + ((res.data && res.data.error) || "Unbekannter Fehler"));
          }
      } catch (e) {
          console.error("Termin speichern fehlgeschlagen:", e);
          alert("Termin konnte nicht gespeichert werden.");
      }
  };

  return (
    <Modal open={open} onClose={onClose} title="Neuen Termin buchen" width={560}>
      {step === 1 && (
        <div>
          {/* BOOKING_MODAL_v2 -- Kunden-Suche statt Scroll-Dropdown */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Kunde *</label>
            {selectedCustomer ? (
              <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: `${COLORS.primary}10`, border: `1px solid ${COLORS.primary}40`, borderRadius: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{selectedCustomer.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
                    {selectedCustomer.phone}{selectedCustomer.email ? " · " + selectedCustomer.email : ""}
                  </div>
                </div>
                <button onClick={() => { setForm(f => ({ ...f, customerId: "" })); setCustQuery(""); }}
                  style={{ background: "none", border: "none", color: COLORS.textLight, cursor: "pointer", padding: 4 }} title="Anderen Kunden wählen">
                  <X size={16} />
                </button>
              </div>
              {/* CUST_NOTE_BOOKING_v1 -- dauerhafte Kunden-Notiz (erscheint bei jedem Termin dieses Kunden) */}
              {/* CUST_NOTE_NEWCUST_v1 -- gleiche gelbe Umrandung wie beim Neukunden, damit die Notiz auffällt */}
              {/* APT_NOTE_ONCE_v1 -- Umschalter „Nur dieser Termin | Dauerhaft“, Standard „Nur dieser Termin“ */}
              <div data-note-box="1" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8,
                    background: noteMode === "perm" ? `${COLORS.warning}14` : `${COLORS.primary}10`,
                    border: noteMode === "perm" ? `1.5px solid ${COLORS.warning}` : `1.5px solid ${COLORS.primary}66` }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>Notiz</span>
                  <div role="tablist" style={{ display: "inline-flex", background: "var(--cardHover)", borderRadius: 8, padding: 2, gap: 2 }}>
                    {[["once", "💬 Nur dieser Termin", onceNote], ["perm", "📌 Dauerhaft", custNote !== (selectedCustomer.notes || "") ? "x" : ""]].map(([k, lbl, dirty]) => (
                      <button key={k} type="button" role="tab" aria-selected={noteMode === k} onClick={() => setNoteMode(k)}
                        style={{ border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                 background: noteMode === k ? "var(--card)" : "transparent",
                                 color: noteMode === k ? (k === "perm" ? COLORS.text : COLORS.primary) : COLORS.textLight,
                                 boxShadow: noteMode === k ? "0 1px 3px rgba(0,0,0,0.12)" : "none" }}>
                        {lbl}{dirty && noteMode !== k ? " •" : ""}
                      </button>
                    ))}
                  </div>
                </div>
                {noteMode === "once" ? (
                  <>
                    {selectedCustomer.notes ? (
                      <div style={{ fontSize: 11.5, color: COLORS.textLight, marginBottom: 6, lineHeight: 1.35, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 54, overflow: "hidden" }}>
                        📌 Dauerhaft: {selectedCustomer.notes}
                      </div>
                    ) : null}
                    <textarea value={onceNote} onChange={e => setOnceNote(e.target.value)} rows={2} maxLength={1000}
                      placeholder="z.B. Sohn kommt mit und wird auch geschnitten (erscheint beim nächsten Termin nicht)"
                      style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.primary}55`, background: "var(--card)", color: COLORS.text, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                  </>
                ) : (
                <>
                <div style={{ fontSize: 11.5, color: COLORS.textLight, marginBottom: 4 }}>Erscheint bei jedem Termin, z.B. „kommt immer 10 Min zu spät"</div>
                <textarea value={custNote} onChange={e => setCustNote(e.target.value)} rows={2}
                  placeholder="Dauerhafte Notiz zu diesem Kunden…"
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.warning}66`, background: "var(--card)", color: COLORS.text, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                </>
                )}
                <div style={{ fontSize: 10.5, color: COLORS.textLight, marginTop: 4, lineHeight: 1.35 }}>
                  🔒 Gesundheitsangaben (z. B. Allergien) nur mit ausdrücklicher Einwilligung des Kunden eintragen. {/* AI_HEALTH_HINT_v1 / Compliance B10 */}
                </div>
              </div>
              </>
            ) : newCustMode ? (
              /* CAL_NEW_CUST_QUICK_BOOK_v1 -- Inline-Anlage neuer Kunde */
              <div style={{ padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <strong style={{ fontSize: 13 }}>🆕 Neuen Kunden anlegen</strong>
                  <button onClick={() => { setNewCustMode(false); setNewCustError(""); }} title="Stattdessen bestehenden Kunden wählen"
                    style={{ background: "none", border: "none", color: COLORS.textLight, cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
                    Bestehenden wählen
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)" /* NEWCUST_MOBILE_FIT_v1 -- Nachname ragte am Handy über den Rand */, gap: 8, marginBottom: 8 }}>
                  <input autoFocus placeholder="Vorname *" value={newCustForm.firstName}
                    onChange={e => setNewCustForm(f => ({ ...f, firstName: e.target.value }))}
                    style={{ width: "100%", minWidth: 0, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
                  <input placeholder="Nachname *" value={newCustForm.lastName}
                    onChange={e => setNewCustForm(f => ({ ...f, lastName: e.target.value }))}
                    style={{ width: "100%", minWidth: 0, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
                </div>
                <input type="tel" placeholder="Telefon *" value={newCustForm.phone}
                  onChange={e => setNewCustForm(f => ({ ...f, phone: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box", marginBottom: 8 }} />
                <input type="email" placeholder="E-Mail (optional)" value={newCustForm.email}
                  onChange={e => setNewCustForm(f => ({ ...f, email: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box", marginBottom: 8 }} />
                {/* CUST_NOTE_NEWCUST_v1 (Toby 19.09.2026: "das Feld dauerhafte Notiz zu diesem Kunden hinzufügen,
                    gelb umranden") -- beim Anlegen eines neuen Kunden fehlte die dauerhafte Notiz komplett; sie
                    musste hinterher im CRM nachgetragen werden. Gelbe Umrandung wie im Termin-Fenster. */}
                <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: `${COLORS.warning}14`, border: `1.5px solid ${COLORS.warning}` }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4, color: COLORS.text }}>
                    📌 Dauerhafte Notiz zu diesem Kunden <span style={{ fontWeight: 400, color: COLORS.textLight }}>(erscheint bei jedem Termin)</span>
                  </label>
                  <textarea value={newCustForm.notes} onChange={e => setNewCustForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                    placeholder="z.B. kommt immer 10 Min zu spät"
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.warning}66`, background: "var(--card)", color: COLORS.text, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                  <div style={{ fontSize: 10.5, color: COLORS.textLight, marginTop: 4, lineHeight: 1.35 }}>
                    🔒 Gesundheitsangaben (z. B. Allergien) nur mit ausdrücklicher Einwilligung des Kunden eintragen. {/* AI_HEALTH_HINT_v1 / Compliance B10 */}
                  </div>
                </div>
                {/* CUST_DUP_HINT_v1 -- Hinweis auf bestehende Kunden (keine Sperre); Klick übernimmt den Bestandskunden */}
                <DupCustomerHint
                  hits={findDuplicateCustomers(customers, { name: `${newCustForm.firstName || ""} ${newCustForm.lastName || ""}`, phone: newCustForm.phone })}
                  onPick={c => { setForm(f => ({ ...f, customerId: c.id })); setNewCustMode(false); setNewCustError(""); }} />
                {newCustError && <div style={{ fontSize: 11, color: COLORS.danger, marginBottom: 8 }}>{newCustError}</div>}
                <Btn size="sm" onClick={saveNewCustomer} disabled={newCustSaving}>
                  <Check size={12} /> {newCustSaving ? "Speichere..." : "Anlegen & weiter"}
                </Btn>
              </div>
            ) : (
              <>
                <input type="search" autoFocus placeholder="Name, Telefon oder E-Mail..."
                  value={custQuery} onChange={e => setCustQuery(e.target.value)}
                  /* CUSTSEARCH_NOAUTOFILL_v1 -- Browser-Autocomplete + Keychain-Vorschlaege deaktivieren */
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  name="shearly-customer-search"
                  data-1p-ignore data-lpignore="true" data-form-type="other"
                  style={{ width: "100%", padding: "10px 12px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text, boxSizing: "border-box" }} />
                {/* CAL_NEW_CUST_QUICK_BOOK_v1 -- Schnell-Anlage-Trigger über der Liste */}
                {setCustomers && (
                  <button onClick={() => { setNewCustMode(true); setNewCustError(""); }}
                    style={{ marginTop: 6, width: "100%", padding: "8px 12px", border: `1px dashed ${COLORS.border}`, borderRadius: 8, background: "transparent", color: COLORS.primary, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <UserPlus size={12} /> Neuen Kunden anlegen
                  </button>
                )}
                {/* CUSTSEARCH_EMPTY_v1 -- ohne Sucheingabe nur ein dezenter Hinweis statt der vollen Liste */}
                {!(custQuery || "").trim() ? (
                  <div style={{ marginTop: 6, padding: "10px 12px", fontSize: 12, color: COLORS.textLight, textAlign: "center" }}>
                    Tippe einen Namen, eine Telefonnummer oder E-Mail, um zu suchen.
                  </div>
                ) : (
                <div style={{ marginTop: 6, maxHeight: 200, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                  {filteredCustomers.length === 0 && (
                    <div style={{ padding: 12, fontSize: 12, color: COLORS.textLight, textAlign: "center" }}>
                      Keine Treffer für „{custQuery}".
                    </div>
                  )}
                  {filteredCustomers.map(c => (
                    <div key={c.id} onClick={() => { setForm(f => ({ ...f, customerId: c.id })); setCustQuery(""); }}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--cardHover)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: COLORS.textLight }}>{c.phone || "—"}{c.email ? " · " + c.email : ""}</div>
                    </div>
                  ))}
                </div>
                )}
              </>
            )}
          </div>

          {/* APT_BADGES_v1 -- Warnung bei Risiko-Kunde (No-Show ≥3) oder Neukunden-Hinweis */}
          {selectedCustomer && (selectedCustomer.noShows ?? selectedCustomer.no_shows ?? 0) >= 1 && ( /* NO_SHOW_MARK_v1 -- schon ab 1 */
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "rgba(220,38,38,0.08)", border: `1px solid ${COLORS.danger}40`, borderRadius: 8, fontSize: 12, color: COLORS.danger, display: "flex", alignItems: "center", gap: 6 }}>
              <span>🚫</span>
              <strong>{(selectedCustomer.noShows ?? selectedCustomer.no_shows)}× nicht erschienen</strong>
              <span style={{ color: COLORS.textLight }}>{selectedCustomer.lastNoShow ? "· zuletzt am " + String(selectedCustomer.lastNoShow).slice(0, 10).split("-").reverse().join(".") + " " : ""}· ohne abzusagen</span>
            </div>
          )}
          {/* BOOKING_LASTAPPT_FIX_v1 -- "Neukunde" nur zeigen wenn KEIN vergangener Termin existiert,
              sonst widersprach es der "Letzter Termin"-Box (visits-Zaehler vs. echte Termin-Historie). */}
          {selectedCustomer && (selectedCustomer.visits === 0 || selectedCustomer.visits === undefined) && !lastAppointment && (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.4)", borderRadius: 8, fontSize: 12, color: COLORS.success, display: "flex", alignItems: "center", gap: 6 }}>
              <span>🆕</span>
              <strong>Neukunde</strong>
              <span style={{ color: COLORS.textLight }}>· erste Buchung im Salon</span>
            </div>
          )}

          {/* BOOKING_MODAL_v2 -- "wie letztes Mal" Schnell-Uebernahme */}
          {selectedCustomer && lastAppointment && (
            <div style={{ marginBottom: 14, padding: "10px 12px", background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontSize: 12, color: COLORS.textLight }}>
                <strong style={{ color: COLORS.text }}>Letzter Termin ({lastAppointment.date}):</strong>{" "}
                {(lastAppointment.serviceIds || lastAppointment.service_ids || []).map(id => services.find(s => s.id === id)?.name).filter(Boolean).join(", ") || "—"}
              </div>
              <Btn size="sm" variant="secondary" onClick={applyLastAppointment}>Wie letztes Mal</Btn>
            </div>
          )}

          {/* CUSTOMER_PREFERRED_DURATION_v1 -- pro Kunde Erfahrungs-Vorschlag aus Historie */}
          {selectedCustomer && hasPreferred && form.serviceIds.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 12px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 8, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, color: COLORS.text, flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, color: COLORS.success, marginBottom: 4 }}>
                  ⚡ Erfahrungswert: {preferredTotalDuration} Min
                </div>
                <div style={{ fontSize: 11, color: COLORS.textLight, lineHeight: 1.45 }}>
                  {form.serviceIds.map(sid => {
                    const pref = preferredDurations[String(sid)];
                    if (!pref) return null;
                    const svc = services.find(x => x.id === sid);
                    const name = svc ? svc.name : `Service ${sid}`;
                    const variabel = pref.history.length > 1 && Math.max(...pref.history) - Math.min(...pref.history) > 15;
                    return (
                      <div key={sid}>
                        <strong>{name}:</strong> {pref.count}× gebucht
                        {" · "}letzte: {pref.history.slice(0,3).join("/")} Min
                        {variabel ? " (schwankt — Median verwendet)" : ""}
                      </div>
                    );
                  })}
                </div>
              </div>
              {preferredTotalDuration !== calcTotal && (
                <Btn size="sm" variant="secondary" onClick={() => setPreferredDurations({})} title="Stattdessen Service-Standarddauer nehmen">
                  Standard ({calcTotal} Min)
                </Btn>
              )}
            </div>
          )}

          <Select label="Mitarbeiter" value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
            options={[{ value: "", label: "Mitarbeiter wählen..." }, ...employees.map(e => ({ value: e.id, label: `${e.name} (${e.role || e.role_title || ""})` }))]} />

          {/* BOOKING_MODAL_v2 -- Services nach Kategorie gruppiert */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Dienstleistungen</label>
            {servicesByCategory.cats.map(cat => {
              // SERVICE_AUDIENCE_COLORS_v1 -- Damen/Farbe zart rot, Herren zart blau (gleiche Regel wie die Buchungsseite);
              // gewaehlt bleibt am Rahmen in der Salonfarbe erkennbar, der Farbton bleibt dabei stehen.
              const tint = svcTintOf(cat);
              return (
              <div key={cat} style={{ marginBottom: 10 }} data-feature={tint ? "SERVICE_AUDIENCE_COLORS_v1" : undefined}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  {tint && <span style={{ width: 8, height: 8, borderRadius: "50%", background: tint.dot, flexShrink: 0 }} />}
                  {cat}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {servicesByCategory.groups[cat].map(svc => {
                    const sel = form.serviceIds.includes(svc.id);
                    return (
                      <button key={svc.id} onClick={() => setForm(f => ({ ...f, serviceIds: sel ? f.serviceIds.filter(id => id !== svc.id) : [...f.serviceIds, svc.id] }))}
                        style={tint ? {
                          padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: sel ? 700 : 500, cursor: "pointer",
                          background: sel ? tint.bgStrong : tint.bg, color: sel ? COLORS.primary : COLORS.text,
                          border: sel ? `1.5px solid ${COLORS.primary}` : `1px solid ${tint.edge}`, transition: "all 0.15s"
                        } : {
                          padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer",
                          background: sel ? `${COLORS.primary}15` : "var(--cardHover)", color: sel ? COLORS.primary : COLORS.textLight,
                          border: `1px solid ${sel ? COLORS.primary : COLORS.border}`, transition: "all 0.15s"
                        }}>
                        {svc.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>

          {/* HAIR_LENGTH_PICKER_v1 -- 4-Stufen-Haarlänge statt nur Bool 'lang'.
              Steuert weiter calcDynamicDuration via hairLong-Mapping + landet in der Notiz.
              BUSINESS_TYPE_v1 -- bei Kosmetikstudios komplett ausgeblendet. */}
          {!isKosmetikSalon() && <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 6, display: "block" }}>Haarlänge</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { key: "kurz",      label: "Kurz" },
                { key: "mittel",    label: "Mittel" },
                { key: "lang",      label: "Lang" },
                { key: "sehr_lang", label: "Sehr lang" },
              ].map(opt => {
                const active = form.hairLength === opt.key;
                return (
                  <button key={opt.key}
                    onClick={() => setForm(f => ({
                      ...f,
                      hairLength: active ? "" : opt.key,
                      // hairLong mappen fuer calcDynamicDuration: lang/sehr_lang -> true
                      hairLong: (opt.key === "lang" || opt.key === "sehr_lang") && !active,
                    }))}
                    style={{
                      padding: "8px 14px", borderRadius: 18, fontSize: 12.5, cursor: "pointer",
                      background: active ? COLORS.primary : "var(--cardHover)",
                      color: active ? "white" : COLORS.text,
                      border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                      fontWeight: active ? 700 : 500,
                    }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>}
          {!isKosmetikSalon() && <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.hairThick} onChange={e => setForm(f => ({ ...f, hairThick: e.target.checked }))} /> Dickes Haar (Aufpreis-Dauer)
            </label>
          </div>}
          <Btn onClick={() => setStep(2)} disabled={!form.customerId || !form.employeeId || form.serviceIds.length === 0}>
            Weiter <ArrowRight size={14} />
          </Btn>
        </div>
      )}
      {step === 2 && (
        <div>
          {/* BOOKING_MODAL_v2 -- Wiederbesuchs-Schnellwahl */}
          <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", marginBottom: 6 }}>Wiederbesuch in</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[3, 4, 6, 8, 10].map(w => (
                <button key={w} onClick={() => setFollowUpDate(w)}
                  style={{ padding: "6px 12px", borderRadius: 18, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    background: "var(--card)", color: COLORS.primary, border: `1px solid ${COLORS.border}` }}>
                  {w} Wo
                </button>
              ))}
            </div>
          </div>

          {/* RECURRING_BOOKING_v1 (3.5) -- Serien-Termine fuer Stammkunden */}
          <div style={{ marginBottom: 12, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: recurring.enabled ? 8 : 0 }}>
              <input type="checkbox" checked={recurring.enabled} onChange={e => setRecurring(r => ({ ...r, enabled: e.target.checked }))} />
              Als Serie buchen
              <span style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 400 }}>(z.B. Stammkunde alle 4 Wochen)</span>
            </label>
            {recurring.enabled && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textLight, display: "block", marginBottom: 3 }}>Alle … Wochen</label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" /* SERIES_INPUT_FIX_v2: Textfeld, weil Safari select() bei type=number ignoriert */ value={recurring.weeks}
                    onFocus={e => { const t = e.target; setTimeout(() => { try { t.select(); } catch (_) {} }, 0); }} onMouseUp={e => e.preventDefault()}
                    onChange={e => setRecurring(r => ({ ...r, weeks: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))}
                    onBlur={() => setRecurring(r => ({ ...r, weeks: normRecurring(r).weeks }))}
                    style={{ width: "100%", padding: 6, borderRadius: 6, border: `1px solid ${COLORS.border}`, boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: COLORS.textLight, display: "block", marginBottom: 3 }}>Anzahl Termine</label>
                  <input type="text" inputMode="numeric" pattern="[0-9]*" /* SERIES_INPUT_FIX_v2: Textfeld, weil Safari select() bei type=number ignoriert */ value={recurring.count}
                    onFocus={e => { const t = e.target; setTimeout(() => { try { t.select(); } catch (_) {} }, 0); }} onMouseUp={e => e.preventDefault()}
                    onChange={e => setRecurring(r => ({ ...r, count: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))}
                    onBlur={() => setRecurring(r => ({ ...r, count: normRecurring(r).count }))}
                    style={{ width: "100%", padding: 6, borderRadius: 6, border: `1px solid ${COLORS.border}`, boxSizing: "border-box" }} />
                </div>
              </div>
            )}
            {/* SERIES_PREVIEW_v1 (Tamara 21.09.2026: "laeuft in die falsche Richtung?") -- zeigt vor dem Buchen,
                welche Termine entstehen: ab dem Datum unten, jeweils nach vorne. */}
            {recurring.enabled && (() => {
              const ds = seriesDates(form.date, recurring);
              if (!ds.length) return <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 8 }}>Datum unten wählen — die Serie startet an diesem Tag.</div>;
              const f = d => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
              const last = ds[ds.length - 1];
              const list = ds.length <= 4 ? ds.map(f).join(", ") : ds.slice(0, 3).map(f).join(", ") + " … " + f(last);
              return (
                <div style={{ fontSize: 12, color: COLORS.text, marginTop: 8, lineHeight: 1.45 }}>
                  📅 <strong>{ds.length} Termine</strong> ab dem Datum unten: {list}{last.getFullYear()}
                </div>
              );
            })()}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Input label="Datum" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            <Select label="Uhrzeit" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
              options={TIME_SLOTS.map(t => {
                // SHIFT_BREAK_BOOK_v1 -- echte Termine: "(belegt)", gesperrt; nur Pause: "(Pause)", waehlbar mit Rueckfrage
                const busy = isSlotBusy(t), pause = !busy && isSlotBreak(t);
                return { value: t, label: busy ? t + ' (belegt)' : pause ? t + ' (Pause)' : t, disabled: busy };
              })} />
          </div>

          {/* APPT_NEARBY_HINT_v1 (Toby 20.09.2026) -- derselbe Kunde hat am selben Tag oder ±7 Tage schon einen Termin.
              Nur Hinweis, der Buchen-Knopf bleibt unverändert. */}
          {(() => {
            const near = findNearbyAppointments(appointments, form.customerId, form.date);
            if (!near.length) return null;
            const cName = customers.find(c => c.id === Number(form.customerId))?.name || "Dieser Kunde";
            const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
            return (
              <div data-nearby-hint="1" style={{ margin: "4px 0 8px", padding: "8px 10px", borderRadius: 8, background: `${COLORS.warning}14`, border: `1.5px solid ${COLORS.warning}`, fontSize: 12.5, color: COLORS.text }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>📅 {cName} hat schon {near.length === 1 ? "einen Termin" : "Termine"} in der Nähe:</div>
                {near.map(a => {
                  const [y, m, d] = String(a.date).slice(0, 10).split("-").map(Number);
                  const wd = WD[new Date(y, m - 1, d).getDay()];
                  const emp = employees.find(e => String(e.id) === String(a.employeeId));
                  const svc = (a.serviceNames || []).join(", ");
                  return (
                    <div key={a.id} style={{ padding: "2px 0" }}>
                      {a._diff === 0 ? <strong>am selben Tag · </strong> : <span style={{ color: COLORS.textLight }}>{Math.abs(a._diff)} {Math.abs(a._diff) === 1 ? "Tag" : "Tage"} {a._diff < 0 ? "vorher" : "danach"} · </span>}
                      {wd} {formatDateShort(a.date)} · {a.startTime}{svc ? ` · ${svc}` : ""}{emp ? ` bei ${emp.name}` : ""}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <Card style={{ background: "var(--bg)", marginBottom: 16, marginTop: 8 }}>
            <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700 }}>Zusammenfassung</h4>
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <div><strong>Kunde:</strong> {customers.find(c => c.id === Number(form.customerId))?.name}</div>
              <div><strong>Mitarbeiter:</strong> {selectedEmp?.name}</div>
              <div><strong>Services:</strong> {selectedServices.map(s => s.name).join(", ")}</div>
              <div><strong>Geschätzte Dauer:</strong> {totalDuration} Min.{form.hairLong && " (+ Aufschlag lange Haare)"}{form.hairThick && " (+ Aufschlag dickes Haar)"}</div>
              <div><strong>Termin:</strong> {form.date} um {form.startTime} – {getEndTime(form.startTime, totalDuration)}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.primary, marginTop: 8 }}>
                Geschätzter Preis: {Math.round(totalPrice)} €
                {isWeekend && <span style={{ fontSize: 11, color: COLORS.warning, marginLeft: 8 }}>(inkl. Wochenend-Aufschlag)</span>}
              </div>
            </div>
          </Card>

          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={() => setStep(1)}><ChevronLeft size={14} /> Zurück</Btn>
            <Btn variant="success" onClick={handleBook}><Check size={14} /> Termin buchen</Btn>
          </div>
        </div>
      )}
      {/* RECUR_SERIES_v1 -- Termin-Übersicht nach Serienbuchung (liegt als fixed-Overlay über allem) */}
      <SeriesOverviewModal data={seriesResult} onClose={() => { setSeriesResult(null); onClose(); }} />
    </Modal>
  );
};


// ============================================================
// RECUR_SERIES_v1 (Toby 27.07.) -- Termin-Übersicht nach Serien-/Folgeterminbuchung:
// zeigt alle angelegten Termine der Serie, mit Drucken + Text-Kopieren für die Kundin.
// Eigenständige Top-Level-Komponente (Muster ShiftTemplateModal, KEINE Inline-Definition
// im Parent -- sonst Remount-Falle/Safari-Crash).
// ============================================================
export const SeriesOverviewModal = ({ data, onClose }) => {
  if (!data) return null;
  const fmtDate = ds => {
    try {
      return new Date(String(ds).slice(0, 10) + "T12:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
    } catch (_) { return String(ds); }
  };
  const okItems = data.items.filter(i => i.ok);
  const failItems = data.items.filter(i => !i.ok);
  const copyText = [
    `Terminübersicht${data.customerName ? " für " + data.customerName : ""}`,
    data.serviceNames ? `Leistung: ${data.serviceNames}` : null,
    data.employeeName ? `Bei: ${data.employeeName}` : null,
    "",
    ...okItems.map(i => `• ${fmtDate(i.date)} um ${data.startTime} Uhr`),
  ].filter(l => l !== null).join("\n");
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      alert("Terminliste kopiert — z.B. in WhatsApp oder SMS einfügen.");
    } catch (_) {
      window.prompt("Zum Kopieren markieren + Cmd/Strg-C:", copyText);
    }
  };
  const doPrint = () => {
    const w = window.open("", "_blank");
    if (!w) { alert("Popup-Blocker aktiv — bitte Popups für diese Seite erlauben."); return; }
    const esc = s => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    w.document.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>Terminübersicht</title>
<style>body{font-family:-apple-system,"Segoe UI",Arial,sans-serif;max-width:520px;margin:40px auto;color:#222}
h1{font-size:20px;margin:0 0 2px}.sub{color:#666;font-size:13px;margin-bottom:18px}
ul{list-style:none;padding:0;margin:0}li{padding:9px 0;border-bottom:1px solid #eee;font-size:15px}
.foot{margin-top:24px;font-size:12px;color:#888}</style></head><body>
<h1>Ihre Termine${data.customerName ? " – " + esc(data.customerName) : ""}</h1>
<div class="sub">${esc(data.serviceNames || "")}${data.employeeName ? " · bei " + esc(data.employeeName) : ""}</div>
<ul>${okItems.map(i => `<li>${esc(fmtDate(i.date))} um ${esc(data.startTime)} Uhr</li>`).join("")}</ul>
<div class="foot">Bitte geben Sie uns bei Verhinderung rechtzeitig Bescheid.</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (_) {} }, 250);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, width: 420, maxWidth: "92%", maxHeight: "84vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Termine eingetragen ✓</h3>
        <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" }}>
          {data.customerName}{data.serviceNames ? ` — ${data.serviceNames}` : ""}{data.employeeName ? ` bei ${data.employeeName}` : ""}
        </p>
        <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "4px 12px", marginBottom: 12 }}>
          {data.items.map((it, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: i < data.items.length - 1 ? `1px solid ${COLORS.border}` : "none", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>{fmtDate(it.date)}, {data.startTime} Uhr</span>
              {it.ok
                ? <span style={{ color: COLORS.success, fontWeight: 600 }}>✓</span>
                : <span style={{ color: COLORS.danger, fontWeight: 600 }} title={it.error || ""}>nicht gebucht</span>}
            </div>
          ))}
        </div>
        {failItems.length > 0 && (
          <div style={{ fontSize: 12, color: COLORS.danger, marginBottom: 12 }}>
            {failItems.length === 1 ? "1 Termin konnte" : `${failItems.length} Termine konnten`} nicht gebucht werden (Zeitkonflikt) — bitte im Kalender eine Alternative wählen.
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" size="sm" onClick={doPrint} style={{ flex: 1, justifyContent: "center" }}>🖨 Drucken</Btn>
          <Btn variant="secondary" size="sm" onClick={doCopy} style={{ flex: 1, justifyContent: "center" }}>📋 Kopieren</Btn>
          <Btn size="sm" onClick={onClose} style={{ flex: 1, justifyContent: "center" }}>Fertig</Btn>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PAGE: Bookings List
// ============================================================
export const BookingsPage = ({ appointments, setAppointments, customers, employees, services }) => {
  const [filter, setFilter] = useState("all");
  const [followUpModal, setFollowUpModal] = useState(null);
  // RECUR_SERIES_v1 -- Rhythmus (Wochen) + Anzahl fuer Folgetermine + Übersicht danach
  const [fuWeeks, setFuWeeks] = useState(4);
  const [fuCount, setFuCount] = useState(1);
  const [seriesOverview, setSeriesOverview] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingCustomerId, setBookingCustomerId] = useState(null);
  // CANCEL_REASON_v1 -- Storno-Dialog (Bookings-Page)
  const [cancelTarget, setCancelTarget] = useState(null);
  // BOOKINGS_PAGE_CLEANUP_v1 (Toby 03.07.) -- Seite zeigte rohe DB-Zeilen: Phasen-Teile
  // (Kind-Zeilen eines Einwirkzeit-Termins), Pausen und Assistent-Blocks erschienen als
  // eigene "Termine" (Wolf: 1340 Zeilen statt 817 echter Termine). Jetzt: genau EINE
  // Zeile pro echtem Termin (Master/Standalone), Zeitraum-Filter (Default: Kommende),
  // Kunden-/Leistungs-Suche, ehrliche Zaehlung. Reine Anzeige, keine Buchungslogik.
  const [range, setRange] = useState("upcoming"); // upcoming | today | past | all
  const [search, setSearch] = useState("");
  const [showMax, setShowMax] = useState(50);
  const todayStr = formatDate(new Date());
  const cleaned = appointments.filter(a => {
    if (a.phaseParentId || a.phase_parent_id) return false;              // Phasen-Teil: Master zaehlt
    if (a.source === "blocker" || a.source === "assistant") return false; // Pause / Assistent-Block
    return true;
  });
  const upcomingCount = cleaned.filter(a => String(a.date).slice(0, 10) >= todayStr && a.status !== "cancelled").length;
  const filtered = cleaned.filter(a => {
    const d = String(a.date).slice(0, 10);
    if (range === "upcoming" && d < todayStr) return false;
    if (range === "today" && d !== todayStr) return false;
    if (range === "past" && d >= todayStr) return false;
    if (filter !== "all" && a.status !== filter) return false;
    if (search.trim()) {
      const cust = customers.find(c => c.id === a.customerId);
      const hay = (((cust && cust.name) || "") + " " +
        (a.serviceIds || []).map(sid => (services.find(s => s.id === sid) || {}).name || "").join(" ")).toLowerCase();
      if (!hay.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });
  // Sortierung: Heute/Zukunft zuerst (naechster oben), Vergangenheit dahinter (juengste oben)
  const _sortKey = a => `${String(a.date).slice(0, 10)}${a.startTime || ""}`;
  const sorted = [...filtered].sort((a, b) => {
    const ka = _sortKey(a), kb = _sortKey(b);
    const fa = ka.slice(0, 10) >= todayStr, fb = kb.slice(0, 10) >= todayStr;
    if (fa !== fb) return fa ? -1 : 1;
    return fa ? ka.localeCompare(kb) : kb.localeCompare(ka);
  });
  const visible = sorted.slice(0, showMax);

  // CANCEL_REASON_v1 -- alter cancelAppt (lokal-only) durch dialog-flow ersetzt
  const cancelAppt = (apt) => setCancelTarget(apt);

  // RECUR_SERIES_v1 (Toby 27.07.) -- ersetzt createFollowUp. Der alte Knopf hatte 3 Fehler:
  // (1) schickte KEINE Dauer/Preis mit -> Server legte 0-Minuten-Termine an (unsichtbar im
  // Kalender = "funktioniert nicht"); (2) wartete die Antwort nicht ab -> Konflikte (409)
  // wurden verschluckt, Phantom-Termin verschwand beim Neuladen; (3) nur 1 Folgetermin
  // statt Rhythmus. Jetzt: voller Payload, await je Termin, N Termine im Wochen-Rhythmus,
  // danach Kunden-Übersicht (Drucken/Kopieren).
  const createFollowUps = async (apt, weeks, count) => {
    const results = [];
    const parentId = apt.recurringParentId || apt.id;
    for (let i = 1; i <= count; i++) {
      const d = new Date(String(apt.date).slice(0, 10) + "T12:00:00");
      d.setDate(d.getDate() + i * weeks * 7);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      try {
        const res = await api.request("/appointments", {
          method: "POST",
          body: JSON.stringify({
            customer_id: apt.customerId, employee_id: apt.employeeId,
            service_ids: apt.serviceIds, date: dateStr, start_time: apt.startTime,
            total_duration: apt.totalDuration || 0, total_price: apt.totalPrice || 0,
            status: "confirmed", source: "manual",
            recurring_parent_id: parentId, recurring_interval_weeks: weeks,
            notes: count > 1 ? `Folgetermin ${i}/${count} (alle ${weeks} Wochen)` : `Folgetermin (+${weeks} Wochen)`,
          }),
        });
        if (res && res.ok) {
          const rd = res.data || {};
          results.push({ date: dateStr, ok: true });
          setAppointments(prev => [...prev, {
            ...apt, id: rd.id || (Math.max(0, ...prev.map(a => a.id)) + 1), date: dateStr,
            status: "confirmed", paymentStatus: "pending", paymentMethod: null,
            notes: "", recurringParentId: parentId,
          }]);
        } else {
          results.push({ date: dateStr, ok: false, error: (res && res.data && res.data.error) || "" });
        }
      } catch (_) {
        results.push({ date: dateStr, ok: false, error: "Netzwerkfehler" });
      }
    }
    try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
    setFollowUpModal(null);
    const _cust = customers.find(c => c.id === apt.customerId);
    const _emp = employees.find(e => e.id === apt.employeeId);
    setSeriesOverview({
      customerName: (_cust && _cust.name) || "",
      employeeName: (_emp && _emp.name) || "",
      serviceNames: (apt.serviceIds || []).map(sid => (services.find(s => s.id === sid) || {}).name).filter(Boolean).join(", "),
      startTime: String(apt.startTime).slice(0, 5),
      items: results,
    });
  };

  return (
    <div>
      <Header title="Termine" subtitle={`${cleaned.length} Termine · ${upcomingCount} kommende`} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}><Btn onClick={() => setShowBooking(true)} size={window.innerWidth < 768 ? "sm" : undefined}><Plus size={16} /> Neuer Termin</Btn></div>
      {/* BOOKINGS_PAGE_CLEANUP_v1: Zeitraum + Status + Suche */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { val: "upcoming", label: "Kommende" },
          { val: "today", label: "Heute" },
          { val: "past", label: "Vergangene" },
          { val: "all", label: "Alle" },
        ].map(f => (
          <Btn key={f.val} variant={range === f.val ? "primary" : "secondary"} size="sm" onClick={() => { setRange(f.val); setShowMax(50); }}>
            {f.label}
          </Btn>
        ))}
        <input
          type="text" value={search} placeholder="Kunde oder Leistung suchen…"
          onChange={e => { setSearch(e.target.value); setShowMax(50); }}
          style={{ marginLeft: "auto", padding: "7px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                   background: "var(--card)", color: COLORS.text, fontSize: 13, minWidth: 200 }} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { val: "all", label: "Alle" },
          { val: "confirmed", label: "Bestätigt" },
          { val: "cancelled", label: "Abgesagt" },
        ].map(f => (
          <Btn key={f.val} variant={filter === f.val ? "primary" : "secondary"} size="sm" onClick={() => { setFilter(f.val); setShowMax(50); }}>
            {f.label}
          </Btn>
        ))}
      </div>
      {filtered.length === 0 ? (
        <Card>
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <CalendarDays size={48} color={COLORS.textLight} style={{ marginBottom: 16, opacity: 0.4 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>Keine Termine vorhanden</h3>
            <p style={{ color: COLORS.textLight, fontSize: 14, marginBottom: 20 }}>
              {filter !== "all" ? "Keine Termine mit diesem Filter gefunden. Versuche einen anderen Filter." : "Erstelle deinen ersten Termin über den Kalender oder die Online-Buchung."}
            </p>
          </div>
        </Card>
      ) : (
      <Card style={{ padding: 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
              {["Datum", "Zeit", "Kunde", "Mitarbeiter", "Services", "Dauer", "Preis", "Status", "Zahlung", ""].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* BOOKINGS_PAGE_CLEANUP_v1: bereinigt + sortiert (heute/kommend zuerst), max showMax Zeilen */}
            {visible.map(apt => {
              const cust = customers.find(c => c.id === apt.customerId);
              const emp = employees.find(e => e.id === apt.employeeId);
              const svcs = (apt.serviceIds||[]).map(sid => services.find(s => s.id === sid)?.name).join(", ");
              return (
                <tr key={apt.id} style={{ borderBottom: `1px solid ${COLORS.border}`, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--cardHover)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "10px 12px" }}>{formatDateShort(apt.date)}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{apt.startTime}</td>
                  <td style={{ padding: "10px 12px" }}>{cust?.name}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: emp?.color }} />
                      {emp?.name}
                    </span>
                  </td>
                  {/* SVC_COL_WIDE_v1 (Toby 21.07.) -- Services-Spalte größer: vorher 180px + „…"-Abschneidung,
                      jetzt bis 340px mit Umbruch, voller Text zusätzlich als Tooltip */}
                  <td title={svcs} style={{ padding: "10px 12px", minWidth: 200, maxWidth: 340, whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>{svcs}</td>
                  <td style={{ padding: "10px 12px" }}>{apt.totalDuration} Min.</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{apt.totalPrice} €</td>
                  <td style={{ padding: "10px 12px" }}>
                    <Badge text={apt.status === "confirmed" ? "Bestätigt" : apt.status === "pending" ? "Ausstehend" : apt.status === "no_show" ? "Nicht erschienen" : "Abgesagt"} color={apt.status === "confirmed" ? COLORS.success : apt.status === "pending" || apt.status === "no_show" ? COLORS.warning : COLORS.danger} />
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {/* BOOKING_PAY_ONLINE_v1 -- online bezahlt, aber noch kein Bon der Kasse: eigenes Etikett (Spec 3a) */}
                    <Badge text={apt.paymentStatus === "paid" && apt.paymentMethod === "online" && !apt.posReceiptNumber ? "💳 Online bezahlt" : apt.paymentStatus === "paid" ? "Bezahlt" : apt.prepayStatus === "pending" ? "⏳ Zahlung läuft" : "Offen"} color={apt.paymentStatus === "paid" ? COLORS.success : apt.prepayStatus === "pending" ? COLORS.warning : COLORS.textLight} />
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {apt.status !== "cancelled" && (
                        <Btn variant="secondary" size="sm" onClick={() => { setFuWeeks(4); setFuCount(1); setFollowUpModal(apt); }} title="Folgetermine im Rhythmus">
                          <RefreshCw size={12} />
                        </Btn>
                      )}
                      {apt.status !== "cancelled" && (
                        <Btn variant="danger" size="sm" onClick={() => cancelAppt(apt)} title="Stornieren"><X size={12} /></Btn>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* BOOKINGS_PAGE_CLEANUP_v1: grosse Listen haeppchenweise nachladen */}
        {sorted.length > showMax && (
          <div style={{ textAlign: "center", padding: 12, borderTop: `1px solid ${COLORS.border}` }}>
            <Btn variant="secondary" size="sm" onClick={() => setShowMax(m => m + 100)}>
              Weitere anzeigen ({sorted.length - showMax} übrig)
            </Btn>
          </div>
        )}
      </Card>
      )}

      {/* Follow-Up Modal — RECUR_SERIES_v1: Rhythmus (2/3/4/5/6/8 Wochen) + Anzahl statt einzelnem Folgetermin */}
      {followUpModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setFollowUpModal(null)}>
          <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, width: 380, maxWidth: "92%" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Folgetermine im Rhythmus</h3>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px" }}>
              {customers.find(c => c.id === followUpModal.customerId)?.name} — gleiche Leistung, gleicher Mitarbeiter, gleiche Uhrzeit
            </p>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Rhythmus</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
              {[2, 3, 4, 5, 6, 8].map(w => (
                <button key={w} onClick={() => setFuWeeks(w)} style={{
                  padding: "10px 6px", border: `1.5px solid ${fuWeeks === w ? COLORS.primary : COLORS.border}`, borderRadius: 8,
                  background: fuWeeks === w ? `${COLORS.primary}10` : "var(--card)", color: COLORS.text,
                  cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>
                  alle {w} Wo.
                </button>
              ))}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Wie viele Termine?</div>
            <input type="number" min="1" max="26" value={fuCount}
              onChange={e => setFuCount(Math.max(1, Math.min(26, Number(e.target.value) || 1)))}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "var(--card)", color: COLORS.text, fontSize: 14, marginBottom: 10 }} />
            <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 14 }}>
              {(() => {
                const out = [];
                for (let i = 1; i <= Math.min(fuCount, 4); i++) {
                  const d = new Date(String(followUpModal.date).slice(0, 10) + "T12:00:00");
                  d.setDate(d.getDate() + i * fuWeeks * 7);
                  out.push(d.toLocaleDateString("de-DE"));
                }
                return `Geplante Termine: ${out.join(", ")}${fuCount > 4 ? ", …" : ""}`;
              })()}
            </div>
            <Btn onClick={() => createFollowUps(followUpModal, fuWeeks, fuCount)} style={{ width: "100%", justifyContent: "center" }}>
              {fuCount > 1 ? `${fuCount} Termine eintragen` : "Folgetermin eintragen"}
            </Btn>
            <button onClick={() => setFollowUpModal(null)} style={{ width: "100%", marginTop: 10, padding: "10px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, cursor: "pointer", color: COLORS.textLight }}>Abbrechen</button>
          </div>
        </div>
      )}
    {/* RECUR_SERIES_v1 -- Übersicht nach Folgetermin-Rhythmus (Drucken/Kopieren) */}
    <SeriesOverviewModal data={seriesOverview} onClose={() => setSeriesOverview(null)} />
    {showBooking && <BookingModal open={showBooking} onClose={() => { setShowBooking(false); setBookingCustomerId(null); }} employees={employees} customers={customers} services={services} appointments={appointments} setAppointments={setAppointments} preselectCustomerId={bookingCustomerId} />}

    {/* CANCEL_REASON_v1 -- Storno-Dialog */}
    <CancelDialog
      open={cancelTarget !== null}
      onClose={() => setCancelTarget(null)}
      customerName={cancelTarget ? (customers.find(c => c.id === cancelTarget.customerId)?.name || "") : ""}
      seriesCount={seriesFutureSiblings(cancelTarget, appointments).length /* SERIES_CANCEL_v1 */}
      onConfirm={async (reason, note, cancelSeries) => {
        if (!cancelTarget) return;
        try {
          const res = await api.updateAppointment(cancelTarget.id, {
            status: "cancelled",
            cancellation_reason: reason,
            cancellation_note: note || null,
            cancel_series: !!cancelSeries, // SERIES_CANCEL_v1
          });
          if (res && res.ok) {
            // SERIES_CANCEL_v1 -- auch die vom Server mitstornierten Serien-Termine lokal nachziehen
            const _extraIds = (res.data && res.data.series_cancelled_ids) || [];
            setAppointments(prev => prev.map(a => (a.id === cancelTarget.id || _extraIds.includes(a.id))
              ? { ...a, status: "cancelled", cancellation_reason: reason, cancellation_note: note || null }
              : a));
            try { window.dispatchEvent(new CustomEvent("shearly-refresh-appts")); } catch (_) {}
            setCancelTarget(null);
          } else {
            alert((res && res.data && res.data.error) || "Stornieren fehlgeschlagen.");
          }
        } catch (e) {
          alert("Fehler: " + (e && e.message ? e.message : "unbekannt"));
        }
      }}
    />
    </div>
  );
};